import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download, SlidersHorizontal, RefreshCw, Trash2, GripVertical,
  FileSpreadsheet, Play, FileText, FileDown, Ban, FilterX,
  ChevronDown, ChevronRight, ChevronLeft, ChevronUp,
  Plus, Pencil, Save, Baby, X, Search, Check, Link2,
  GripHorizontal, List, LayoutGrid, Database, User,
  ArrowDownUp, Clock, Bug, CheckCircle, Loader,
} from 'lucide-react';
import '../../STUI.css';
import { API_BASE_URL, databasesApi } from '../../services/api';
import wmLogo from '../../assets/logos/WM-Logo-CB-White-Header.png';

/* ─────────────────────────────────────────────────────────────
   STUI Style Guide — Standard Template User Interface
   Demo elements use stui-* classes from STUI.css (imported above).
   App live classes (module-container, btn, etc.)
   live in App.css. STUI.css is isolated to this page only.
─────────────────────────────────────────────────────────────── */

/* ── CSS location registry ─────────────────────────────────── */
const CSS_LOCATIONS = {
  buttons:             { file: 'STUI.css', line: 674  },
  actionMenu:          { file: 'STUI.css', line: 211  },
  select:              { file: 'STUI.css', line: 925  },
  sectionPanels:       { file: 'App.css',  line: 6508 },
  dataGrid:            { file: 'STUI.css', line: 371  },
  sectionLayout:       { file: 'STUI.css', line: 724  },
  detailSectionHeader: { file: 'STUI.css', line: 857  },
  pageHeader:          { file: 'STUI.css', line: 880  },
  baseReset:           { file: 'STUI.css', line: 896  },
  baseBody:            { file: 'STUI.css', line: 901  },
  baseFormControls:    { file: 'STUI.css', line: 909  },
  moduleHeader:        { file: 'STUI.css', line: 305  },
  filterSort:          { file: 'STUI.css', line: 475  },
  stepFamily:          { file: 'STUI.css', line: 952  },
  badge:               { file: 'STUI.css', line: 1023 },
  tile:                { file: 'STUI.css', line: 1094 },
  statRow:             { file: 'STUI.css', line: 1198 },
  label:               { file: 'STUI.css', line: 1240 },
  checkbox:            { file: 'STUI.css', line: 1252 },
  subLabel:            { file: 'STUI.css', line: 1267 },
  pill:                { file: 'STUI.css', line: 1279 },
  toggle:              { file: 'STUI.css', line: 1318 },
  settingRow:          { file: 'STUI.css', line: 1402 },
  listLayout:          { file: 'STUI.css', line: 1402 },
  formFields:          { file: 'STUI.css', line: 1554 },
  searchSelect:        { file: 'STUI.css', line: 1655 },
  relatedTables:       { file: 'STUI.css', line: 1842 },
  datasheetExtras:     { file: 'STUI.css', line: 1988 },
  pagination:          { file: 'STUI.css', line: 2057 },
  fkSelect:            { file: 'STUI.css', line: 2119 },
  composerSelect:      { file: 'STUI.css', line: 2177 },
  moduleHeaderCrud:    { file: 'STUI.css', line: 2326 },
  booleanDisplay:      { file: 'STUI.css', line: 2667 },
  breadcrumb:          { file: 'STUI.css', line: 2644 },
  sortControls:        { file: 'STUI.css', line: 606  },
  tabBtnIcon:          { file: 'STUI.css', line: 334  },
  columnWidths:        { file: 'STUI.css', line: 2570 },
  deleteCol:           { file: 'STUI.css', line: 2682 },
  spinning:            { file: 'STUI.css', line: 5107 },
  orgImages:           { file: 'STUI.css', line: 5111 },
  lightbox:            { file: 'STUI.css', line: 5148 },
  iconBrowser:         { file: 'STUI.css', line: 5172 },
  checkboxInput:       { file: 'STUI.css', line: 5289 },
  reportViewer:        { file: 'STUI.css', line: 5326 },
  reportPreview:       { file: 'STUI.css', line: 5401 },
};

/* ── Icon map for button configurator ─────────────────────── */
const BUTTON_ICONS = { Download, Play, RefreshCw, Plus, Trash2 };

/* ── Shared sub-components ─────────────────────────────────── */

const CssChip = ({ cssFile, cssLine, onClick }) => {
  if (!cssFile || !cssLine) return null;
  return (
    <button className="stui-css-chip" onClick={onClick}>
      {cssFile} : L{cssLine} ↗
    </button>
  );
};

const Section = ({ title, description, cssFile, cssLine, onChipClick, noBodyScroll, children }) => (
  <div className="stui-ssg-section">
    <div className="stui-ssg-section-header">
      <div className="stui-detail-section-header">{title}</div>
      <CssChip cssFile={cssFile} cssLine={cssLine} onClick={() => onChipClick?.(cssFile, cssLine)} />
    </div>
    {description && <p className="stui-ssg-section-desc">{description}</p>}
    <div className="stui-ssg-section-body">
      {children}
    </div>
  </div>
);

const Subsection = ({ label }) => (
  <div className="stui-detail-section-header stui-ssg-subsection">{label}</div>
);

const Example = ({ label, children }) => (
  <div className="stui-example-wrapper">
    <div className="stui-example-cell">{children}</div>
    {label && <span className="stui-example-label">{label}</span>}
  </div>
);

const CSSRef = ({ file, selector }) => (
  <code className="stui-ssg-cssref">
    {file} → {selector}
  </code>
);

const RefTable = ({ headers, rows }) => (
  <table className="stui-ssg-reftable">
    <thead>
      <tr>
        {headers.map(h => (
          <th key={h}>{h}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((row, i) => (
        <tr key={i}>
          {row.map((cell, j) => (
            <td key={j}>{cell}</td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

const PlaceholderTab = ({ label }) => (
  <div className="stui-ssg-placeholder">
    <div className="stui-ssg-placeholder-icon">🚧</div>
    <div className="stui-ssg-placeholder-title">{label}</div>
    <div className="stui-ssg-placeholder-sub">This template is being documented.</div>
  </div>
);

const UsedBy = ({ cssClass, usageMap, pageSlugMap, navigate }) => {
  const pages = usageMap ? (usageMap[cssClass] || []) : null;
  if (pages === null) return <span className="stui-text-italic">Loading…</span>;
  if (pages.length === 0) return <span className="stui-text-italic">No pages reference .{cssClass}</span>;
  return (
    <div className="stui-ssg-usedby">
      {pages.map(page => {
        const info = pageSlugMap?.[page];
        const slug = info?.slug;
        const fullPath = slug
          ? '/' + (info.dbScoped ? (localStorage.getItem('pmd-selected-db-slug') || 'gwa') + '/' : '') + slug
          : null;
        return (
          <button key={page} onClick={() => fullPath && navigate(fullPath)} className={`stui-ssg-usedby-btn ${fullPath ? 'linked' : 'unlinked'}`}>
            {page}
          </button>
        );
      })}
    </div>
  );
};

const TextInventory = ({ items, emptyMsg }) => {
  if (!items) return <span className="stui-text-italic">Loading…</span>;
  const unique = [...new Set(items)].filter(t => t && t.trim().length > 0);
  if (!unique.length) return <span className="stui-text-italic">{emptyMsg || 'No text found'}</span>;
  return (
    <div className="stui-ssg-textinv">
      {unique.map((text, i) => (
        <span key={i} className="stui-ssg-textinv-item">
          "{text}"
        </span>
      ))}
    </div>
  );
};

const ConfigOutput = ({ text }) => (
  <div className="stui-config-output">{text}</div>
);

/* ── Configurators ─────────────────────────────────────────── */

const ButtonConfigurator = () => {
  const [variant, setVariant] = useState('primary');
  const [icon, setIcon] = useState('none');
  const [label, setLabel] = useState('Button');
  const [disabled, setDisabled] = useState(false);
  const IconComp = BUTTON_ICONS[icon] || null;
  const output = [`stui-btn-${variant}`, icon !== 'none' ? `${icon} icon` : 'no icon', `label "${label || 'Button'}"`, disabled ? 'disabled' : 'enabled'].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {[
          { key: 'Variant', items: ['primary', 'secondary'], val: variant, set: setVariant, name: 'btn-v' },
          { key: 'Icon',    items: ['none', 'Download', 'Play', 'RefreshCw', 'Plus', 'Trash2'], val: icon, set: setIcon, name: 'btn-i' },
        ].map(({ key, items, val, set, name }) => (
          <div key={key} className="stui-row-10-wrap">
            <span className="stui-cfg-key">{key}</span>
            {items.map(v => (
              <label key={v} className="stui-cfg-label">
                <input type="radio" name={name} checked={val === v} onChange={() => set(v)} /> {v}
              </label>
            ))}
          </div>
        ))}
        <div className="stui-row-10">
          <span className="stui-cfg-key">Label</span>
          <input value={label} onChange={e => setLabel(e.target.value)} className="stui-cfg-input" style={{ width: 160 }} />
        </div>
        <div className="stui-row-10">
          <span className="stui-cfg-key">State</span>
          <label className="stui-cfg-label">
            <input type="checkbox" checked={disabled} onChange={e => setDisabled(e.target.checked)} /> Disabled
          </label>
        </div>
      </div>
      <div className="stui-example-cell" style={{ justifyContent: 'center' }}>
        <button className={`stui-btn stui-btn-${variant}`} disabled={disabled}>
          {IconComp && <IconComp size={14} />}{label || 'Button'}
        </button>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const ActionMenuConfigurator = () => {
  const [variant, setVariant] = useState('generic');
  const [open, setOpen] = useState(false);
  const [generic, setGeneric] = useState({ refresh: true, download: true, modifyView: true, delete: false });
  const [report, setReport] = useState({ pdf: true, excel: true, csv: true, separateFiles: false, cancelExport: false, clearFilters: false });
  const toggle = (setter, key) => setter(prev => ({ ...prev, [key]: !prev[key] }));
  const genericItems = ['Refresh', 'Download', 'Modify View'].filter((_, i) => [generic.refresh, generic.download, generic.modifyView][i]);
  const reportItems = [report.pdf && 'Download PDF', report.excel && 'Download Excel', report.csv && 'Download CSV', report.separateFiles && 'Separate Files toggle', report.cancelExport && 'Cancel Export (danger)', report.clearFilters && 'Clear Filters & Sort'].filter(Boolean);
  const output = variant === 'generic'
    ? ['stui-action-menu', 'generic', genericItems.length ? 'items: ' + genericItems.join(', ') : 'no items', generic.delete ? 'danger: Delete' : 'no danger item'].join(' · ')
    : ['stui-action-menu', 'report', reportItems.length ? 'items: ' + reportItems.join(', ') : 'no items'].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-row-10">
        <span className="stui-cfg-key">Variant</span>
        {['generic', 'report'].map(v => (
          <label key={v} className="stui-cfg-label">
            <input type="radio" name="am-v" checked={variant === v} onChange={() => setVariant(v)} /> {v}
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {variant === 'generic'
          ? [['refresh','Refresh'],['download','Download'],['modifyView','Modify View'],['delete','Delete (danger)']].map(([k,l]) => (
              <label key={k} className="stui-cfg-label">
                <input type="checkbox" checked={generic[k]} onChange={() => toggle(setGeneric, k)} /> {l}
              </label>
            ))
          : [['pdf','PDF'],['excel','Excel'],['csv','CSV'],['separateFiles','Separate Files'],['cancelExport','Cancel Export'],['clearFilters','Clear Filters & Sort']].map(([k,l]) => (
              <label key={k} className="stui-cfg-label">
                <input type="checkbox" checked={report[k]} onChange={() => toggle(setReport, k)} /> {l}
              </label>
            ))
        }
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }} className="stui-example-cell">
        <div className="stui-action-menu">
          <button className="stui-action-menu-btn" onClick={() => setOpen(o => !o)} title="Actions"><GripVertical size={16} /></button>
          {open && (
            <div className="stui-action-menu-dropdown" style={{ minWidth: 180 }}>
              {variant === 'generic' ? (
                <>
                  {generic.refresh    && <button className="stui-action-menu-item" onClick={() => setOpen(false)}><RefreshCw size={16} /> Refresh</button>}
                  {generic.download   && <button className="stui-action-menu-item" onClick={() => setOpen(false)}><Download size={16} /> Download</button>}
                  {generic.modifyView && <button className="stui-action-menu-item" onClick={() => setOpen(false)}><SlidersHorizontal size={16} /> Modify View</button>}
                  {generic.delete && <><div className="stui-action-menu-divider" /><button className="stui-action-menu-item stui-action-menu-item-danger" onClick={() => setOpen(false)}><Trash2 size={16} /> Delete</button></>}
                </>
              ) : (
                <>
                  {report.separateFiles && <label className="stui-action-menu-item" style={{ cursor:'pointer' }}><input type="checkbox" style={{ marginRight:4 }} /><span>Separate Files</span></label>}
                  {(report.pdf || report.excel || report.csv) && <div className="stui-action-menu-divider" />}
                  {report.pdf   && <button className="stui-action-menu-item" onClick={() => setOpen(false)}><FileText size={16} /> Download PDF</button>}
                  {report.excel && <button className="stui-action-menu-item" onClick={() => setOpen(false)}><FileSpreadsheet size={16} /> Download Excel</button>}
                  {report.csv   && <button className="stui-action-menu-item" onClick={() => setOpen(false)}><FileDown size={16} /> Download CSV</button>}
                  {report.cancelExport && <><div className="stui-action-menu-divider" /><button className="stui-action-menu-item stui-action-menu-item-danger" onClick={() => setOpen(false)}><Ban size={16} /> Cancel Export</button></>}
                  {report.clearFilters && <><div className="stui-action-menu-divider" /><button className="stui-action-menu-item" onClick={() => setOpen(false)}><FilterX size={16} /> Clear Filters &amp; Sort</button></>}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const SelectConfigurator = () => {
  const [disabled, setDisabled] = useState(false);
  const [placeholder, setPlaceholder] = useState('-- None --');
  const [optionList, setOptionList] = useState('Panel, I/O Type, Drawing');
  const options = optionList.split(',').map(o => o.trim()).filter(Boolean);
  const output = ['stui-select', `placeholder "${placeholder}"`, `${options.length} option${options.length !== 1 ? 's' : ''}`, disabled ? 'disabled' : 'enabled'].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        <div className="stui-row-10">
          <span className="stui-cfg-key-lg">Placeholder</span>
          <input value={placeholder} onChange={e => setPlaceholder(e.target.value)} className="stui-cfg-input" style={{ width: 160 }} />
        </div>
        <div className="stui-row-10">
          <span className="stui-cfg-key-lg">Options</span>
          <input value={optionList} onChange={e => setOptionList(e.target.value)} className="stui-cfg-input" style={{ width: 220 }} placeholder="comma-separated" />
        </div>
        <div className="stui-row-10">
          <span className="stui-cfg-key-lg">State</span>
          <label className="stui-cfg-label">
            <input type="checkbox" checked={disabled} onChange={e => setDisabled(e.target.checked)} /> Disabled
          </label>
        </div>
      </div>
      <div className="stui-example-cell">
        <select className="stui-select" disabled={disabled} defaultValue="">
          <option value="">{placeholder}</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const SectionPanelConfigurator = () => {
  const [number, setNumber] = useState(1);
  const [title, setTitle] = useState('Report Configuration');
  const [hasDesc, setHasDesc] = useState(true);
  const [desc, setDesc] = useState('Select filter values to narrow report results.');
  const [collapsed, setCollapsed] = useState(false);
  const [hasBtn, setHasBtn] = useState(false);
  const output = ['stui-step-panel', `number ${number}`, `title "${title}"`, hasDesc ? `desc "${desc.slice(0,30)}…"` : 'no description', collapsed ? 'collapsed' : 'expanded', hasBtn ? 'Run Report button' : 'no action button'].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {[
          ['Number', <input type="number" min="1" max="9" value={number} onChange={e => setNumber(+e.target.value)} className="stui-cfg-input-sm" />],
          ['Title',  <input value={title} onChange={e => setTitle(e.target.value)} className="stui-cfg-input" style={{ width: 220 }} />],
        ].map(([k, ctrl]) => (
          <div key={k} className="stui-row-10">
            <span className="stui-cfg-key-md">{k}</span>
            {ctrl}
          </div>
        ))}
        <div className="stui-row-10">
          <span className="stui-cfg-key-md">Desc</span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer', color: 'var(--color-gray-600)', marginRight: 8 }}>
            <input type="checkbox" checked={hasDesc} onChange={e => setHasDesc(e.target.checked)} /> show
          </label>
          {hasDesc && <input value={desc} onChange={e => setDesc(e.target.value)} className="stui-cfg-input" style={{ width: 200 }} />}
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <span className="stui-cfg-key-md">Options</span>
          {[['collapsed','Collapsed',collapsed,setCollapsed],['hasBtn','Action Button',hasBtn,setHasBtn]].map(([k,l,v,s]) => (
            <label key={k} className="stui-cfg-label">
              <input type="checkbox" checked={v} onChange={e => s(e.target.checked)} /> {l}
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="stui-step-panel">
          <div className="stui-step-header">
            <span className="stui-step-number">{number}</span>
            <h3>{title || 'Panel Title'}</h3>
            {hasBtn && <button className="stui-btn stui-btn-primary stui-ml-auto-mr"><Play size={14} /> Run Report</button>}
            <span style={{ marginLeft: hasBtn ? 0 : 'auto', color: 'var(--color-gray-400)' }}>
              {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
            </span>
          </div>
          {!collapsed && hasDesc && <p className="stui-step-desc">{desc}</p>}
          {!collapsed && <div style={{ padding: '8px 0', color: 'var(--color-gray-300)', fontSize: 11, fontStyle: 'italic' }}>…body content…</div>}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const DataGridConfigurator = () => {
  const [clickable, setClickable]     = useState(true);
  const [noResize, setNoResize]       = useState(true);
  const [colCount, setColCount]       = useState(3);
  const [rowCount, setRowCount]       = useState(4);
  const [withFilter, setWithFilter]   = useState(false);

  // Real filter state — mirrors FilterableDataGrid exactly
  const [openFilter, setOpenFilter]           = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [pendingSelections, setPendingSelections] = useState({});
  const [filterSearch, setFilterSearch]       = useState({});
  const [sortConfig, setSortConfig]           = useState({ key: null, direction: null });
  const [activeFilters, setActiveFilters]     = useState({});
  const filterRef  = useRef(null);
  const headerRefs = useRef({});

  const colKeys = Array.from({ length: colCount }, (_, i) => `col${i}`);
  const cols    = Array.from({ length: colCount }, (_, i) => ({ key: `col${i}`, label: `Col ${i + 1}` }));
  const rows    = Array.from({ length: rowCount }, (_, i) =>
    Object.fromEntries(cols.map((c, j) => [c.key, `R${i+1}C${j+1}`]))
  );

  // Click-outside closes dropdown — same as FilterableDataGrid
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setOpenFilter(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset filter state when withFilter is toggled off
  useEffect(() => {
    if (!withFilter) { setOpenFilter(null); setActiveFilters({}); }
  }, [withFilter]);

  const getColumnOptions = (colKey) => [...new Set(rows.map(r => r[colKey]))].sort();

  const getFilteredOptions = (colKey) => {
    const opts = getColumnOptions(colKey);
    const s = (filterSearch[colKey] || '').toLowerCase();
    return s ? opts.filter(o => o.toLowerCase().includes(s)) : opts;
  };

  const openFilterDropdown = (colKey) => {
    if (!withFilter) return;
    const header = headerRefs.current[colKey];
    if (header) {
      const rect = header.getBoundingClientRect();
      const dropdownHeight = 320, dropdownWidth = 240;
      let top  = rect.bottom + 4;
      let left = rect.left;
      if (top  + dropdownHeight > window.innerHeight) top  = rect.top - dropdownHeight - 4;
      if (left + dropdownWidth  > window.innerWidth)  left = window.innerWidth - dropdownWidth - 8;
      setDropdownPosition({ top, left });
    }
    setPendingSelections(prev => ({ ...prev, [colKey]: activeFilters[colKey] || [] }));
    setOpenFilter(colKey);
  };

  const handleCheckboxChange = (colKey, value) => {
    setPendingSelections(prev => {
      const cur = prev[colKey] || [];
      return { ...prev, [colKey]: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value] };
    });
  };

  const isAllSelected = (colKey) => {
    const opts = getFilteredOptions(colKey);
    const sel  = pendingSelections[colKey] || [];
    return opts.length > 0 && opts.every(o => sel.includes(o));
  };

  const handleSelectAll = (colKey) => {
    const opts = getFilteredOptions(colKey);
    const sel  = pendingSelections[colKey] || [];
    const all  = opts.every(o => sel.includes(o));
    setPendingSelections(prev => ({
      ...prev,
      [colKey]: all ? sel.filter(s => !opts.includes(s)) : [...new Set([...sel, ...opts])]
    }));
  };

  const applyFilter = (colKey) => {
    const sel  = pendingSelections[colKey] || [];
    const all  = getColumnOptions(colKey);
    const isAll = all.length > 0 && all.every(o => sel.includes(o));
    setActiveFilters(prev => ({ ...prev, [colKey]: isAll ? [] : sel }));
    setOpenFilter(null);
  };

  const handleSort = (colKey, dir) => { setSortConfig({ key: colKey, direction: dir }); setOpenFilter(null); };

  const filteredRows = rows.filter(row =>
    Object.entries(activeFilters).every(([k, vals]) => !vals?.length || vals.includes(row[k]))
  );
  const sortedRows = sortConfig.key
    ? [...filteredRows].sort((a, b) => {
        const av = a[sortConfig.key], bv = b[sortConfig.key];
        return sortConfig.direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      })
    : filteredRows;

  const hasActiveFilter = (colKey) => activeFilters[colKey]?.length > 0;
  const isSorted        = (colKey) => sortConfig.key === colKey;

  const output = [`stui-data-grid`, `${colCount} col${colCount !== 1 ? 's' : ''}`, `${rowCount} row${rowCount !== 1 ? 's' : ''}`, clickable ? 'clickable' : 'not clickable', noResize ? 'no-resize' : 'resizable', withFilter ? 'filter' : 'no filter'].join(' · ');

  return (
    <div className="stui-col-12">
      <div className="stui-row-12-wrap">
        {[['Columns', colCount, setColCount, 1, 6], ['Rows', rowCount, setRowCount, 2, 8]].map(([lbl, val, set, mn, mx]) => (
          <div key={lbl} className="stui-row-6">
            <span className="stui-label-xs">{lbl}</span>
            <input type="number" min={mn} max={mx} value={val} onChange={e => set(Math.min(mx, Math.max(mn, +e.target.value)))} className="stui-cfg-input-sm" />
          </div>
        ))}
        {[['clickable','Clickable rows',clickable,setClickable],['noResize','No resize',noResize,setNoResize],['withFilter','With filter',withFilter,setWithFilter]].map(([k,l,v,s]) => (
          <label key={k} className="stui-cfg-label">
            <input type="checkbox" checked={v} onChange={e => s(e.target.checked)} /> {l}
          </label>
        ))}
        {withFilter && <span style={{ fontSize: 11, color: 'var(--color-gray-400)', fontStyle: 'italic' }}>Click a column header to open filter</span>}
      </div>

      <div className="stui-data-grid-container" style={{ maxHeight: 180 }}>
        <div className="stui-data-grid-scroll-area">
          <table className={`stui-data-grid${noResize ? ' stui-no-column-resize' : ''} stui-w-full`}>
            <thead>
              <tr>
                {cols.map(c => {
                  const active = hasActiveFilter(c.key) || isSorted(c.key);
                  return (
                    <th
                      key={c.key}
                      ref={el => { headerRefs.current[c.key] = el; }}
                      onClick={() => openFilterDropdown(c.key)}
                      style={withFilter ? { cursor: 'pointer' } : {}}
                    >
                      <span>{c.label}</span>
                      {withFilter && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                          style={{ marginLeft: 4, opacity: active ? 1 : 0.3, color: active ? 'var(--color-teal-600)' : 'inherit' }}>
                          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                        </svg>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, i) => (
                <tr key={i} className={clickable ? 'stui-clickable' : ''}>
                  {colKeys.map(k => <td key={k}>{row[k]}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {openFilter && (
        <div
          ref={filterRef}
          className="stui-filter-dropdown"
          style={{ position: 'fixed', top: dropdownPosition.top, left: dropdownPosition.left, zIndex: 9999 }}
          onClick={e => e.stopPropagation()}
        >
          <div className="stui-filter-sort-section">
            <button className={`stui-filter-sort-btn${sortConfig.key === openFilter && sortConfig.direction === 'asc' ? ' active' : ''}`} onClick={() => handleSort(openFilter, 'asc')}>
              <span>↑ Sort A to Z</span>
            </button>
            <button className={`stui-filter-sort-btn${sortConfig.key === openFilter && sortConfig.direction === 'desc' ? ' active' : ''}`} onClick={() => handleSort(openFilter, 'desc')}>
              <span>↓ Sort Z to A</span>
            </button>
          </div>
          <div className="stui-filter-divider" />
          <div className="stui-filter-search">
            <input
              type="text"
              placeholder="Search"
              value={filterSearch[openFilter] || ''}
              onChange={e => setFilterSearch(prev => ({ ...prev, [openFilter]: e.target.value }))}
            />
          </div>
          <div className="stui-filter-options">
            <label className="stui-filter-option stui-filter-option-select-all">
              <input type="checkbox" checked={isAllSelected(openFilter)} onChange={() => handleSelectAll(openFilter)} />
              <span>(Select All)</span>
            </label>
            {getFilteredOptions(openFilter).map(opt => (
              <label key={opt} className="stui-filter-option">
                <input type="checkbox" checked={(pendingSelections[openFilter] || []).includes(opt)} onChange={() => handleCheckboxChange(openFilter, opt)} />
                <span>{opt}</span>
              </label>
            ))}
          </div>
          <div className="stui-filter-footer">
            <button onClick={() => applyFilter(openFilter)} className="stui-filter-ok-btn">OK</button>
            <button onClick={() => setOpenFilter(null)} className="stui-filter-cancel-btn">Cancel</button>
          </div>
        </div>
      )}

      <ConfigOutput text={output} />
    </div>
  );
};

const FilterConfigurator = () => {
  const [showSort, setShowSort]         = useState(true);
  const [sortDir, setSortDir]           = useState('asc');
  const [showSearch, setShowSearch]     = useState(true);
  const [optionCount, setOptionCount]   = useState(4);
  const [showSelectAll, setShowSelectAll] = useState(true);
  const DEMO_OPTIONS = ['Alpha', 'Bravo', 'Charlie', 'Delta', 'Echo', 'Foxtrot'];
  const options = DEMO_OPTIONS.slice(0, Math.min(optionCount, DEMO_OPTIONS.length));
  const output = [
    'stui-filter-dropdown',
    showSort ? `sort:${sortDir !== 'none' ? sortDir : 'none'}` : 'no sort',
    showSearch ? 'search' : 'no search',
    `${optionCount} option${optionCount !== 1 ? 's' : ''}`,
    showSelectAll ? 'select-all' : 'no select-all',
  ].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-row-12-wrap">
        {[['showSort','Sort buttons',showSort,setShowSort],['showSearch','Search input',showSearch,setShowSearch],['showSelectAll','Select All row',showSelectAll,setShowSelectAll]].map(([k,l,v,s]) => (
          <label key={k} className="stui-cfg-label">
            <input type="checkbox" checked={v} onChange={e => s(e.target.checked)} /> {l}
          </label>
        ))}
        {showSort && (
          <div className="stui-row-6">
            <span className="stui-label-xs">Active sort</span>
            <select className="stui-select" value={sortDir} onChange={e => setSortDir(e.target.value)}>
              <option value="none">None</option>
              <option value="asc">Asc</option>
              <option value="desc">Desc</option>
            </select>
          </div>
        )}
        <div className="stui-row-6">
          <span className="stui-label-xs">Options</span>
          <input type="number" min={1} max={6} value={optionCount} onChange={e => setOptionCount(Math.min(6, Math.max(1, +e.target.value)))} className="stui-cfg-input-sm" />
        </div>
      </div>
      <div className="stui-filter-dropdown" style={{ position: 'relative', width: 240 }}>
        {showSort && (
          <div className="stui-filter-sort-section">
            <button className={`stui-filter-sort-btn${sortDir === 'asc' ? ' active' : ''}`}><span>↑ Sort A to Z</span></button>
            <button className={`stui-filter-sort-btn${sortDir === 'desc' ? ' active' : ''}`}><span>↓ Sort Z to A</span></button>
          </div>
        )}
        {showSort && <div className="stui-filter-divider" />}
        {showSearch && <div className="stui-filter-search"><input type="text" placeholder="Search" readOnly /></div>}
        <div className="stui-filter-options">
          {showSelectAll && (
            <label className="stui-filter-option stui-filter-option-select-all">
              <input type="checkbox" defaultChecked readOnly /> <span>(Select All)</span>
            </label>
          )}
          {options.map(v => (
            <label key={v} className="stui-filter-option">
              <input type="checkbox" defaultChecked readOnly /> <span>{v}</span>
            </label>
          ))}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const SectionLayoutConfigurator = () => {
  const [hasChip, setHasChip] = useState(true);
  const [hasDesc, setHasDesc] = useState(true);
  const [subsections, setSubsections] = useState(2);
  const [chipFile, setChipFile] = useState('STUI.css');
  const [chipLine, setChipLine] = useState(724);
  const output = ['stui-section-card', hasDesc ? 'with description' : 'no description', hasChip ? `${chipFile} : L${chipLine} chip` : 'no chip', `${subsections} subsection${subsections !== 1 ? 's' : ''}`].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-row-12-wrap">
        <label className="stui-cfg-label">
          <input type="checkbox" checked={hasChip} onChange={e => setHasChip(e.target.checked)} /> CSS chip
        </label>
        {hasChip && (
          <>
            <select className="stui-select" value={chipFile} onChange={e => setChipFile(e.target.value)}>
              <option value="STUI.css">STUI.css</option>
              <option value="App.css">App.css</option>
            </select>
            <input type="number" value={chipLine} onChange={e => setChipLine(+e.target.value)} className="stui-cfg-input-sm" style={{ width: 64 }} placeholder="line" />
          </>
        )}
        <label className="stui-cfg-label">
          <input type="checkbox" checked={hasDesc} onChange={e => setHasDesc(e.target.checked)} /> description
        </label>
        <div className="stui-row-6">
          <span className="stui-label-xs">Subsections</span>
          <input type="number" min="0" max="6" value={subsections} onChange={e => setSubsections(+e.target.value)} className="stui-cfg-input-sm" />
        </div>
      </div>
      <div className="stui-section-card" style={{ maxHeight: 200, overflow: 'hidden' }}>
        <div className="stui-section-header">
          <div className="stui-section-header-left">
            <h2>Section Title</h2>
            {hasDesc && <p>Optional description of what this section documents.</p>}
          </div>
          {hasChip && <span className="stui-css-chip" style={{ pointerEvents: 'none' }}>{chipFile} : L{chipLine} ↗</span>}
        </div>
        <div style={{ padding: '12px 16px', overflowY: 'auto' }}>
          {Array.from({ length: subsections }, (_, i) => (
            <div key={i} className="stui-subsection-divider">Subsection {i + 1}</div>
          ))}
          {subsections === 0 && <span className="stui-text-italic">No subsections</span>}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const ReportHeaderConfigurator = () => {
  const [icon, setIcon] = useState('FileSpreadsheet');
  const [title, setTitle] = useState('I/O Assignments');
  const [desc, setDesc] = useState('Tag IO assignment details by panel, drop, slot, channel');
  const [hasExport, setHasExport] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const IconMap = { FileSpreadsheet, FileText, FileDown };
  const IconComp = IconMap[icon] || FileSpreadsheet;
  const output = [`stui-page-header`, `${icon} icon`, `title "${title}"`, desc ? `desc "${desc.slice(0, 28)}…"` : 'no description', hasExport ? 'with export action menu' : 'no export menu'].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        <div className="stui-row-10">
          <span className="stui-cfg-key-md">Icon</span>
          {['FileSpreadsheet','FileText','FileDown'].map(v => (
            <label key={v} className="stui-cfg-label">
              <input type="radio" name="rh-icon" checked={icon === v} onChange={() => setIcon(v)} /> {v}
            </label>
          ))}
        </div>
        <div className="stui-row-10">
          <span className="stui-cfg-key-md">Title</span>
          <input value={title} onChange={e => setTitle(e.target.value)} className="stui-cfg-input" style={{ width: 200 }} />
        </div>
        <div className="stui-row-10">
          <span className="stui-cfg-key-md">Desc</span>
          <input value={desc} onChange={e => setDesc(e.target.value)} className="stui-cfg-input" style={{ width: 260 }} />
        </div>
        <div className="stui-row-10">
          <span className="stui-cfg-key-md">Options</span>
          <label className="stui-cfg-label">
            <input type="checkbox" checked={hasExport} onChange={e => setHasExport(e.target.checked)} /> Export action menu
          </label>
        </div>
      </div>
      <div style={{ border: '1px solid var(--color-gray-200)', borderRadius: 6, overflow: 'hidden' }}>
        <div className="stui-page-header">
          <div className="stui-row-md">
            <h1 className="stui-page-title">
              <IconComp size={20} />{title || 'Report Title'}
            </h1>
            {desc && <span className="stui-text-sm-muted">{desc}</span>}
          </div>
          {hasExport && (
            <div className="stui-action-menu">
              <button className="stui-action-menu-btn" onClick={() => setExportOpen(o => !o)}><GripVertical size={16} /></button>
              {exportOpen && (
                <div className="stui-action-menu-dropdown" style={{ minWidth: 160 }}>
                  <button className="stui-action-menu-item" onClick={() => setExportOpen(false)}><FileText size={16} /> Download PDF</button>
                  <button className="stui-action-menu-item" onClick={() => setExportOpen(false)}><FileSpreadsheet size={16} /> Download Excel</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Helpers shared across new configurators ───────────────── */
const cfgRow = (label, control, width = 80) => (
  <div className="stui-row-10">
    <span style={{ fontSize: 11, color: 'var(--color-gray-500)', width, flexShrink: 0 }}>{label}</span>
    {control}
  </div>
);
const cfgRadios = (name, items, val, set) => items.map(v => (
  <label key={v} className="stui-cfg-label">
    <input type="radio" name={name} checked={val === v} onChange={() => set(v)} /> {v}
  </label>
));
const cfgTextInput = (val, set, width = 180) => (
  <input value={val} onChange={e => set(e.target.value)} style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--color-gray-200)', borderRadius: 4, width, color: 'var(--color-gray-700)' }} />
);
const cfgCheck = (val, set, label) => (
  <label className="stui-cfg-label">
    <input type="checkbox" checked={val} onChange={e => set(e.target.checked)} /> {label}
  </label>
);

/* ── Sort Controls Configurator ─────────────────────────────── */
const SortControlsConfigurator = () => {
  const [col, setCol]   = useState('Tag Name');
  const [dir, setDir]   = useState('asc');
  const output = `stui-btn-sort-state · col "${col}" · dir ${dir}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Column', cfgTextInput(col, setCol, 160))}
        {cfgRow('Direction', <div className="stui-flex-10">{cfgRadios('sc-dir', ['asc','desc'], dir, setDir)}</div>)}
      </div>
      <div className="stui-example-cell" style={{ gap: 10 }}>
        <span className="stui-btn-sort-state">{dir === 'asc' ? '↑' : '↓'} {col || 'Column'}</span>
        <button className="stui-btn-sort-reset">✕ Clear Sort</button>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Column Widths Configurator ─────────────────────────────── */
const ColumnWidthsConfigurator = () => {
  const [span, setSpan] = useState(12);
  const output = `stui-col-${span} · span ${span}`;
  return (
    <div className="stui-col-12">
      {cfgRow('Span (1–24)', <input type="number" min={1} max={24} value={span} onChange={e => setSpan(Math.min(24, Math.max(1, +e.target.value)))} className="stui-cfg-input-sm" style={{ width: 64 }} />)}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)', gap: 2, width: '100%' }}>
        <div style={{ gridColumn: `span ${span}`, background: 'var(--color-teal-600)', borderRadius: 3, padding: '6px 0', textAlign: 'center', color: 'white', fontSize: 11, fontWeight: 600 }}>{span}</div>
        {span < 24 && <div style={{ gridColumn: `span ${24 - span}`, background: 'var(--color-gray-100)', borderRadius: 3, padding: '6px 0', textAlign: 'center', fontSize: 11, color: 'var(--color-gray-400)' }}>{24 - span}</div>}
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Module Header Configurator ─────────────────────────────── */
const ModuleHeaderConfigurator = () => {
  const [icon, setIcon]   = useState('Baby');
  const [title, setTitle] = useState('Instruments');
  const [count, setCount] = useState(42);
  const IconMap = { Baby, FileSpreadsheet, FileText, Database };
  const IconComp = IconMap[icon] || Baby;
  const output = `stui-module-header · ${icon} icon · title "${title}" · count ${count}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Icon', <div className="stui-flex-10">{cfgRadios('mh-icon', ['Baby','FileSpreadsheet','FileText','Database'], icon, setIcon)}</div>)}
        {cfgRow('Title', cfgTextInput(title, setTitle))}
        {cfgRow('Count', <input type="number" min={0} value={count} onChange={e => setCount(+e.target.value)} className="stui-cfg-input-sm" style={{ width: 72 }} />)}
      </div>
      <div style={{ border: '1px solid var(--color-gray-200)', borderRadius: 4, overflow: 'hidden' }}>
        <div className="stui-module-header-styleguideonly">
          <div className="stui-module-header-left">
            <IconComp size={20} className="stui-page-icon" />
            <h1>{title || 'Module'}</h1>
            <span className="stui-module-header-meta">{count} records</span>
          </div>
          <div className="stui-module-header-actions">
            <button className="stui-crud-btn" disabled title="Add"><Plus size={16} /></button>
            <button className="stui-crud-btn" disabled title="Edit"><Pencil size={16} /></button>
            <button className="stui-action-menu-btn disabled" disabled title="Actions"><GripVertical size={16} /></button>
          </div>
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Boolean Display Configurator ───────────────────────────── */
const BooleanDisplayConfigurator = () => {
  const [val, setVal] = useState('true');
  const output = `stui-boolean-display · value ${val}`;
  return (
    <div className="stui-col-12">
      {cfgRow('Value', <div className="stui-flex-10">{cfgRadios('bd-val', ['true','false'], val, setVal)}</div>)}
      <div className="stui-example-cell">
        <div className="stui-boolean-display">
          {val === 'true' && <span className="stui-boolean-check">✓</span>}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Breadcrumb Configurator ────────────────────────────────── */
const BreadcrumbConfigurator = () => {
  const [p1, setP1] = useState('Projects');
  const [p2, setP2] = useState('BU28');
  const [cur, setCur] = useState('Instrumentation');
  const output = `stui-breadcrumb · "${p1}" > "${p2}" > "${cur}"`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Parent 1', cfgTextInput(p1, setP1, 140))}
        {cfgRow('Parent 2', cfgTextInput(p2, setP2, 140))}
        {cfgRow('Current', cfgTextInput(cur, setCur, 140))}
      </div>
      <div className="stui-example-cell">
        <div className="stui-breadcrumb" style={{ display: 'flex', alignItems: 'center' }}>
          <span className="stui-breadcrumb-parent">{p1 || 'Parent 1'}</span>
          <span className="stui-breadcrumb-arrow"> › </span>
          <span className="stui-breadcrumb-parent">{p2 || 'Parent 2'}</span>
          <span className="stui-breadcrumb-arrow"> › </span>
          <span className="stui-breadcrumb-current">{cur || 'Current'}</span>
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Badge Configurator ─────────────────────────────────────── */
const BadgeConfigurator = () => {
  const VARIANTS = ['accepted','expired','none','pending','construction'];
  const [variant, setVariant] = useState('accepted');
  const [label, setLabel]     = useState('');
  const defaultLabels = { accepted: 'Accepted', expired: 'Expired', none: 'Not Invited', pending: 'Invite Sent', construction: 'Construction' };
  const display = label || defaultLabels[variant];
  const output = `stui-badge-${variant} · label "${display}"`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Variant', <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{cfgRadios('badge-v', VARIANTS, variant, setVariant)}</div>)}
        {cfgRow('Label', cfgTextInput(label, setLabel, 160))}
      </div>
      <div className="stui-example-cell">
        <span className={`stui-badge-${variant}`}>{display}</span>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Tile Configurator ──────────────────────────────────────── */
const TileConfigurator = () => {
  const [title, setTitle]       = useState('Drawing Log');
  const [icon, setIcon]         = useState('FileText');
  const [clickable, setClickable] = useState(true);
  const [legend, setLegend]     = useState(true);
  const IconMap = { FileText, FileSpreadsheet, Database };
  const IconComp = IconMap[icon] || FileText;
  const output = `stui-tile · title "${title}" · ${icon} icon${clickable ? ' · clickable' : ''}${legend ? ' · with legend' : ''}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Icon', <div className="stui-flex-10">{cfgRadios('tile-icon', ['FileText','FileSpreadsheet','Database'], icon, setIcon)}</div>)}
        {cfgRow('Title', cfgTextInput(title, setTitle))}
        {cfgRow('Options', <div style={{ display: 'flex', gap: 12 }}>{cfgCheck(clickable, setClickable, 'Clickable')}{cfgCheck(legend, setLegend, 'Legend')}</div>)}
      </div>
      <div className="stui-example-cell">
        <div className={`stui-tile${clickable ? ' stui-clickable' : ''}`} style={{ width: 200 }}>
          <div className="stui-tile-header"><IconComp size={16} className="stui-tile-icon" /><span className="stui-tile-title">{title || 'Tile Title'}</span></div>
          <div className="stui-tile-content stui-text-placeholder">…chart…</div>
          {legend && <div className="stui-tile-legend"><span className="stui-legend-item"><span className="stui-legend-dot" style={{ background: 'var(--color-primary)' }} />Series A</span></div>}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Stat Row Configurator ──────────────────────────────────── */
const StatRowConfigurator = () => {
  const [stats, setStats] = useState('Rows:1,284 | Pages:43 | Layout:Tabloid | Time:312ms');
  const parsed = stats.split('|').map(s => s.trim()).filter(Boolean).map(s => { const [l, v] = s.split(':'); return { label: l?.trim() || '', value: v?.trim() || '' }; });
  const output = `stui-stat-row · ${parsed.map(s => `"${s.label}:${s.value}"`).join(' ')}`;
  return (
    <div className="stui-col-12">
      {cfgRow('Stats', <input value={stats} onChange={e => setStats(e.target.value)} placeholder="Label:Value | Label:Value" className="stui-cfg-input" style={{ width: 300 }} />, 48)}
      <div style={{ fontSize: 10, color: 'var(--color-gray-400)', marginTop: -4 }}>Separate stats with |, label:value pairs with :</div>
      <div className="stui-stat-row">
        <div className="stui-stat-group">
          {parsed.map((s, i) => (
            <div key={i} className="stui-stat-item">
              <span className="stui-stat-label">{s.label}</span>
              <span className="stui-stat-value">{s.value}</span>
            </div>
          ))}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Label Configurator ─────────────────────────────────────── */
const LabelConfigurator = () => {
  const [text, setText] = useState('Sort By:');
  const output = `stui-label · text "${text}"`;
  return (
    <div className="stui-col-12">
      {cfgRow('Text', cfgTextInput(text, setText))}
      <div className="stui-example-cell" style={{ gap: 8 }}>
        <span className="stui-label">{text || 'Label'}</span>
        <select className="stui-select"><option>Option A</option></select>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Checkbox Configurator ──────────────────────────────────── */
const CheckboxConfigurator = () => {
  const [label, setLabel]   = useState('Show QR Code in report header');
  const [checked, setChecked] = useState(true);
  const output = `stui-checkbox · label "${label}"${checked ? ' · checked' : ''}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Label', cfgTextInput(label, setLabel, 220))}
        {cfgRow('State', cfgCheck(checked, setChecked, 'Checked'))}
      </div>
      <div className="stui-example-cell">
        <label className="stui-checkbox"><input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} /> {label || 'Checkbox label'}</label>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Sub-Label Configurator ─────────────────────────────────── */
const SubLabelConfigurator = () => {
  const [label, setLabel]    = useState('Theme');
  const [sublabel, setSublabel] = useState('Controls the color palette across all pages');
  const output = `stui-sub-label · label "${label}" · sublabel "${sublabel}"`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Label', cfgTextInput(label, setLabel, 120))}
        {cfgRow('Sub-Label', cfgTextInput(sublabel, setSublabel, 260))}
      </div>
      <div className="stui-example-cell" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
        <span className="stui-label">{label || 'Label'}</span>
        <div className="stui-sub-label">{sublabel || 'Helper text'}</div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Pill Configurator ──────────────────────────────────────── */
const PillConfigurator = () => {
  const [options, setOptions] = useState('Light,Dark,Dim,Sepia,Hi-Con');
  const [selected, setSelected] = useState(0);
  const opts = options.split(',').map(o => o.trim()).filter(Boolean);
  const output = `stui-pill · options "${options}" · selected ${selected}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Options', <input value={options} onChange={e => setOptions(e.target.value)} placeholder="comma-separated" className="stui-cfg-input" style={{ width: 220 }} />, 64)}
        {cfgRow('Selected', <input type="number" min={0} max={opts.length - 1} value={selected} onChange={e => setSelected(+e.target.value)} className="stui-cfg-input-sm" style={{ width: 56 }} />, 64)}
      </div>
      <div className="stui-example-cell">
        <div className="stui-pill-group">
          {opts.map((o, i) => <button key={i} className={`stui-pill${i === selected ? ' selected' : ''}`} onClick={() => setSelected(i)}>{o}</button>)}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Toggle Configurator ────────────────────────────────────── */
const ToggleConfigurator = () => {
  const [label, setLabel] = useState('Enable notifications');
  const [on, setOn]       = useState(true);
  const output = `stui-toggle · label "${label}" · ${on ? 'on' : 'off'}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Label', cfgTextInput(label, setLabel, 200))}
        {cfgRow('State', cfgCheck(on, setOn, 'On'))}
      </div>
      <div className="stui-example-cell" style={{ gap: 10 }}>
        <label className="stui-toggle">
          <input type="checkbox" checked={on} onChange={e => setOn(e.target.checked)} />
          <span className="stui-toggle-slider" />
        </label>
        <span className="stui-label">{label || 'Toggle label'}</span>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Setting Row Configurator ───────────────────────────────── */
const SettingRowConfigurator = () => {
  const [type, setType]       = useState('toggle');
  const [label, setLabel]     = useState('Enable Feature');
  const [sublabel, setSublabel] = useState('Turns this option on for all users');
  const [on, setOn]           = useState(true);
  const output = `stui-row-setting · type ${type} · label "${label}" · sublabel "${sublabel}"`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Type', <div className="stui-flex-10">{cfgRadios('sr-type', ['toggle','select','buttons'], type, setType)}</div>)}
        {cfgRow('Label', cfgTextInput(label, setLabel))}
        {cfgRow('Sub-label', cfgTextInput(sublabel, setSublabel, 240))}
      </div>
      <div className="stui-example-cell" style={{ padding: 0 }}>
        <div className="stui-row-setting" style={{ width: '100%', padding: '12px 16px' }}>
          <div className="stui-row-setting-label">
            <div className="stui-label">{label || 'Setting'}</div>
            <div className="stui-sub-label">{sublabel}</div>
          </div>
          {type === 'toggle' && <label className="stui-toggle"><input type="checkbox" checked={on} onChange={e => setOn(e.target.checked)} /><span className="stui-toggle-slider" /></label>}
          {type === 'select' && <select className="stui-select"><option>Option A</option><option>Option B</option></select>}
          {type === 'buttons' && <div className="stui-row-setting-controls"><button className="stui-btn stui-btn-secondary">Browse</button><button className="stui-btn stui-btn-primary">Apply</button></div>}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── List Layout Configurator (static) ──────────────────────── */
const ListLayoutConfigurator = () => {
  const output = 'stui-list-view · list-panel left · detail-panel flex-1 · 24-col grid';
  return (
    <div className="stui-col-12">
      <div className="stui-text-sm-muted">Structural layout — no interactive options. Wrap <code>stui-list-panel</code> + <code>stui-detail-panel</code> inside <code>stui-list-view</code>.</div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Form Fields Configurator ───────────────────────────────── */
const FormFieldsConfigurator = () => {
  const [label, setLabel]     = useState('Tag Name');
  const [state, setState]     = useState('normal');
  const [position, setPosition] = useState('top');
  const stateClass = state === 'normal' ? '' : ` ${state}`;
  const posClass   = position === 'top' ? '' : ` label-${position}`;
  const output = `stui-field-group · label "${label}" · state ${state} · position ${position}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Label', cfgTextInput(label, setLabel, 140))}
        {cfgRow('State', <div className="stui-flex-10">{cfgRadios('ff-state', ['normal','dirty','fk','derived','readonly'], state, setState)}</div>)}
        {cfgRow('Position', <div className="stui-flex-10">{cfgRadios('ff-pos', ['top','left','right','bottom'], position, setPosition)}</div>)}
      </div>
      <div className="stui-example-cell">
        <div className={`stui-field-group${posClass}`} style={{ width: 240 }}>
          <label>{label || 'Field'}</label>
          {state === 'readonly' && <div className="stui-field-readonly">System managed</div>}
          {state === 'derived'  && <input className="stui-field-input derived" value="AUTO-2024" readOnly />}
          {state === 'fk'       && <div className="stui-field-input fk" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}><span>Panel A</span><Link2 size={12} className="stui-c-muted" /></div>}
          {(state === 'normal' || state === 'dirty') && <input className={`stui-field-input${stateClass}`} defaultValue="Sample value" />}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── SearchSelect Configurator ──────────────────────────────── */
const SearchSelectConfigurator = () => {
  const [value, setValue] = useState('Panel A');
  const [state, setState] = useState('normal');
  const output = `stui-searchselect-trigger · value "${value}" · state ${state}`;
  const cls = `stui-searchselect-trigger${state !== 'normal' ? ` ${state}` : ''}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Value', cfgTextInput(value, setValue, 140))}
        {cfgRow('State', <div className="stui-flex-10">{cfgRadios('ss-state', ['normal','open','disabled','dirty'], state, setState)}</div>)}
      </div>
      <div className="stui-example-cell">
        <div className={cls} style={{ width: 200 }}>
          <span className={`stui-searchselect-value${!value ? ' placeholder' : ''}`}>{value || '-- Select --'}</span>
          <ChevronDown size={14} className="stui-searchselect-chevron" />
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Related Tables Configurator ────────────────────────────── */
const RelatedTablesConfigurator = () => {
  const [tabs, setTabs]           = useState('Drawings,Tags,Equipment');
  const [collapsed, setCollapsed] = useState(false);
  const [active, setActive]       = useState(0);
  const tabArr = tabs.split(',').map(t => t.trim()).filter(Boolean);
  const output = `stui-related-section · tabs "${tabs}"${collapsed ? ' · collapsed' : ' · expanded'}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Tabs', <input value={tabs} onChange={e => setTabs(e.target.value)} placeholder="comma-separated" className="stui-cfg-input" style={{ width: 200 }} />, 56)}
        {cfgRow('State', cfgCheck(collapsed, setCollapsed, 'Collapsed'), 56)}
      </div>
      <div style={{ border: '1px solid var(--color-border)', borderRadius: 4, overflow: 'hidden', maxWidth: 480 }}>
        <div className={`stui-related-section${collapsed ? ' collapsed' : ''}`}>
          <div className="stui-related-header">
            <button className="stui-related-collapse-btn" onClick={() => setCollapsed(c => !c)}>{collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
            <div className="stui-related-tabs">
              {tabArr.map((t, i) => <button key={i} className={`stui-related-tab${active === i ? ' active' : ''}`} onClick={() => setActive(i)}>{t}<span className="stui-related-tab-count">{(i + 1) * 4}</span></button>)}
            </div>
          </div>
          {!collapsed && <div className="stui-related-content stui-related-body">Showing {tabArr[active] || 'records'} for this record…</div>}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Datasheet Extras Configurator ──────────────────────────── */
const DatasheetExtrasConfigurator = () => {
  const [mode, setMode] = useState('delete');
  const output = `stui-data-grid · mode ${mode}`;
  return (
    <div className="stui-col-12">
      {cfgRow('Mode', <div className="stui-flex-10">{cfgRadios('de-mode', ['delete','edit'], mode, setMode)}</div>)}
      <div style={{ overflowX: 'auto' }}>
        {mode === 'delete' ? (
          <table className="stui-data-grid delete-mode stui-w-full">
            <thead><tr><th style={{ width: 40 }}></th><th>Tag</th><th>Panel</th></tr></thead>
            <tbody>
              <tr><td className="delete-checkbox-col"><input type="checkbox" /></td><td>AI-100</td><td>Panel A</td></tr>
              <tr className="selected-for-delete"><td className="delete-checkbox-col"><input type="checkbox" checked readOnly /></td><td>DI-201</td><td>Panel B</td></tr>
            </tbody>
          </table>
        ) : (
          <table className="stui-data-grid edit-mode stui-w-full">
            <thead><tr><th>Tag</th><th>Description</th><th>Readonly</th></tr></thead>
            <tbody>
              <tr>
                <td className="editable-cell"><input defaultValue="AI-100" className="stui-editable-input" /></td>
                <td className="editable-cell modified"><input defaultValue="Modified desc" className="stui-editable-input" /></td>
                <td className="readonly-cell">Panel A</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Pagination Configurator ────────────────────────────────── */
const PaginationConfigurator = () => {
  const [page, setPage]         = useState(2);
  const [total, setTotal]       = useState(143);
  const [pageSize, setPageSize] = useState(50);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize + 1;
  const end   = Math.min(page * pageSize, total);
  const output = `stui-pagination · page ${page} · total ${total} · pageSize ${pageSize}`;
  const pages  = Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1);
  return (
    <div className="stui-col-12">
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {[['Page', page, setPage, 1, totalPages], ['Total', total, setTotal, 1, 9999], ['Page Size', pageSize, setPageSize, 10, 200]].map(([l, v, s, mn, mx]) => (
          <div key={l} className="stui-row-6">
            <span className="stui-label-xs">{l}</span>
            <input type="number" min={mn} max={mx} value={v} onChange={e => s(Math.min(mx, Math.max(mn, +e.target.value)))} className="stui-cfg-input-sm" style={{ width: 60 }} />
          </div>
        ))}
      </div>
      <div style={{ border: '1px solid var(--color-border)', borderRadius: 4, overflow: 'hidden', maxWidth: 420 }}>
        <div className="stui-pagination">
          <div className="stui-pagination-controls">
            <button className="stui-pagination-btn" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}><ChevronLeft size={14} /></button>
            {pages.map(p => <button key={p} className={`stui-pagination-btn${p === page ? ' active' : ''}`} onClick={() => setPage(p)}>{p}</button>)}
            <button className="stui-pagination-btn" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}><ChevronRight size={14} /></button>
          </div>
          <span className="stui-pagination-info">{start}–{end} of {total}</span>
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── FK Select Configurator ─────────────────────────────────── */
const FkSelectConfigurator = () => {
  const [path, setPath]   = useState('Building → Drawing');
  const [title, setTitle] = useState('Drawing');
  const output = `stui-fk-select · path "${path}" · title "${title}"`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Path', cfgTextInput(path, setPath, 200))}
        {cfgRow('Title', cfgTextInput(title, setTitle, 140))}
      </div>
      <div style={{ border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden', maxWidth: 460, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
        <div className="stui-fk-select-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            {path.split('→').map((seg, i, arr) => (
              <span key={i} style={{ color: i === arr.length - 1 ? 'var(--color-text)' : 'var(--color-gray-400)', fontWeight: i === arr.length - 1 ? 600 : 400 }}>
                {seg.trim()}{i < arr.length - 1 && <span style={{ margin: '0 4px' }}>→</span>}
              </span>
            ))}
          </div>
          <div className="stui-flex-4">
            <button className="stui-crud-btn" title="Close"><X size={16} /></button>
          </div>
        </div>
        <div className="stui-fk-select-body" style={{ padding: '12px 16px', minHeight: 60, fontSize: 12, color: 'var(--color-gray-400)', fontStyle: 'italic' }}>
          …{title} records…
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Composer Select Configurator ───────────────────────────── */
const ComposerSelectConfigurator = () => {
  const [module, setModule] = useState('Building');
  const [open, setOpen]     = useState(false);
  const modules = ['Building', 'Instrument', 'Panel', 'Drawing', 'Equipment'];
  const output = `stui-composer-select · module "${module}" · ${open ? 'open' : 'closed'}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Module', cfgTextInput(module, setModule, 140))}
        {cfgRow('State', cfgCheck(open, setOpen, 'Open'))}
      </div>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <div className="stui-composer-select">
          <label className="stui-composer-select-label">Module</label>
          <div className={`stui-composer-select-trigger${open ? ' open' : ''}`} onClick={() => setOpen(o => !o)}>
            <span>{module || 'Select…'}</span>
            <ChevronDown size={14} />
          </div>
          {open && (
            <div className="stui-composer-select-panel" style={{ position: 'relative', marginTop: 4, top: 'auto', left: 'auto' }}>
              <input className="stui-composer-select-search" placeholder="Search modules…" />
              <div className="stui-composer-select-options">
                {modules.map((m, i) => (
                  <div key={m} className={`stui-composer-select-option${m === module ? ' selected' : ''}`} onClick={() => { setModule(m); setOpen(false); }}>
                    <span className="stui-composer-select-option-name">{m}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Module Header CRUD Configurator ────────────────────────── */
const ModuleHeaderCrudConfigurator = () => {
  const [mode, setMode]   = useState('list');
  const [state, setState] = useState('view');
  const output = `stui-module-header-crud · mode ${mode} · state ${state}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Mode', <div className="stui-flex-10">{cfgRadios('mhc-mode', ['list','datasheet'], mode, setMode)}</div>)}
        {cfgRow('State', <div className="stui-flex-10">{cfgRadios('mhc-state', ['view','editing'], state, setState)}</div>)}
      </div>
      <div style={{ padding: '12px 16px', background: 'var(--color-surface-alt)', border: '1px solid var(--color-border)', borderRadius: 4, display: 'inline-flex' }}>
        {mode === 'list' ? (
          <div className="stui-module-header-crud">
            {state === 'view' ? <>
              <button className="stui-crud-btn" title="Add"><Plus size={18} /></button>
              <button className="stui-crud-btn" title="Edit"><Pencil size={18} /></button>
              <button className="stui-crud-btn disabled" disabled title="Save"><Save size={18} /></button>
            </> : <>
              <button className="stui-crud-btn stui-delete-btn" title="Delete"><Trash2 size={18} /></button>
              <button className="stui-crud-btn" title="Cancel"><X size={18} /></button>
              <button className="stui-crud-btn" title="Save"><Save size={18} /></button>
            </>}
          </div>
        ) : (
          <div className="stui-module-header-datasheet-controls">
            <div className="stui-module-header-crud">
              <button className="stui-crud-btn" title="Edit"><Pencil size={18} /></button>
              <button className={`stui-crud-btn${state === 'view' ? ' disabled' : ''}`} disabled={state === 'view'} title="Save"><Save size={18} /></button>
            </div>
          </div>
        )}
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Base Reset Static Configurators ────────────────────────── */
const BoxSizingConfigurator = () => (
  <div className="stui-col-8">
    <div className="stui-text-sm-muted">Universal rule — no options. Applied globally to every element and pseudo-element.</div>
    <ConfigOutput text="* { box-sizing: border-box } · *::before { box-sizing: border-box } · *::after { box-sizing: border-box }" />
  </div>
);

const BodyConfigurator = () => (
  <div className="stui-col-8">
    <div className="stui-text-sm-muted">Applied once to the <code>&lt;body&gt;</code> element — sets base font, weight, color, and anti-aliasing for the entire app.</div>
    <ConfigOutput text="body · font-family var(--font-secondary) · font-weight 500 · color var(--color-gray-800) · -webkit-font-smoothing antialiased" />
  </div>
);

const FormControlsConfigurator = () => (
  <div className="stui-col-8">
    <div className="stui-text-sm-muted">Applied to all native form elements — forces them to inherit the body font instead of the browser's system font.</div>
    <ConfigOutput text="input,select,textarea,button · font-family inherit · font-weight inherit" />
  </div>
);

/* ── Demo components ───────────────────────────────────────── */

const MiniGrid = () => (
  <div className="stui-data-grid-container" style={{ maxWidth: 420, maxHeight: 180 }}>
    <div className="stui-data-grid-scroll-area">
      <table className="stui-data-grid stui-no-column-resize stui-w-full">
        <thead><tr><th>Tag</th><th>Panel</th><th>I/O Type</th></tr></thead>
        <tbody>
          {[['AI-1001','MCP-101','Analog Input'],['DI-2201','MCP-101','Digital Input'],['AO-3301','MCP-102','Analog Output'],['DO-4401','MCP-102','Digital Output']].map(([t,p,io]) => (
            <tr key={t} className="stui-clickable"><td>{t}</td><td>{p}</td><td>{io}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const DemoActionMenu = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="stui-action-menu">
      <button className="stui-action-menu-btn" onClick={() => setOpen(o => !o)} title="Actions"><GripVertical size={16} /></button>
      {open && (
        <div className="stui-action-menu-dropdown" style={{ minWidth: 160 }}>
          <button className="stui-action-menu-item" onClick={() => setOpen(false)}><RefreshCw size={16} /> Refresh</button>
          <button className="stui-action-menu-item" onClick={() => setOpen(false)}><Download size={16} /> Download</button>
          <button className="stui-action-menu-item" onClick={() => setOpen(false)}><SlidersHorizontal size={16} /> Modify View</button>
          <div className="stui-action-menu-divider" />
          <button className="stui-action-menu-item stui-action-menu-item-danger" onClick={() => setOpen(false)}><Trash2 size={16} /> Delete</button>
        </div>
      )}
    </div>
  );
};

const DemoExportMenu = () => {
  const [open, setOpen] = useState(false);
  const [separate, setSeparate] = useState(false);
  return (
    <div className="stui-action-menu">
      <button className="stui-action-menu-btn" onClick={() => setOpen(o => !o)} title="Export"><GripVertical size={16} /></button>
      {open && (
        <div className="stui-action-menu-dropdown" style={{ minWidth: 180 }}>
          <label className="stui-action-menu-item" style={{ cursor:'pointer' }}><input type="checkbox" checked={separate} onChange={e => setSeparate(e.target.checked)} style={{ marginRight:4 }} /><span>Separate Files</span></label>
          <div className="stui-action-menu-divider" />
          <button className="stui-action-menu-item" onClick={() => setOpen(false)}><FileText size={16} /><span>Download PDF</span></button>
          <button className="stui-action-menu-item" onClick={() => setOpen(false)}><FileSpreadsheet size={16} /><span>Download Excel</span></button>
          <button className="stui-action-menu-item" onClick={() => setOpen(false)}><FileDown size={16} /><span>Download CSV</span></button>
          <div className="stui-action-menu-divider" />
          <button className="stui-action-menu-item stui-action-menu-item-danger" onClick={() => setOpen(false)}><Ban size={16} /><span>Cancel Export</span></button>
          <div className="stui-action-menu-divider" />
          <button className="stui-action-menu-item" onClick={() => setOpen(false)}><FilterX size={16} /><span>Clear Filters &amp; Sort</span></button>
        </div>
      )}
    </div>
  );
};

/* ── Tab: Elements ─────────────────────────────────────────── */

const ELEMENT_SECTIONS = [
  { id: 'action-menu',    label: 'Action Menu' },
  { id: 'boolean',        label: 'Boolean Display' },
  { id: 'breadcrumb',     label: 'Breadcrumb' },
  { id: 'badge',          label: 'Badge' },
  { id: 'buttons',        label: 'Buttons' },
  { id: 'sort-controls',  label: 'Sort Controls' },
  { id: 'checkbox',       label: 'Checkbox' },
  { id: 'column-widths',  label: 'Column Widths' },
  { id: 'data-grid',      label: 'Data Grid' },
  { id: 'filter-sort',    label: 'Filter / Sort' },
  { id: 'label',          label: 'Label' },
  { id: 'module-header',  label: 'Module Header' },
  { id: 'pill',           label: 'Pill' },
  { id: 'section-layout', label: 'Section Layout' },
  { id: 'section-panels', label: 'Section Panels' },
  { id: 'select',         label: 'Select / Combobox' },
  { id: 'setting-row',    label: 'Setting Row' },
  { id: 'stat-row',       label: 'Stat Row' },
  { id: 'sub-label',      label: 'Sub-Label' },
  { id: 'tile',           label: 'Tile' },
  { id: 'toggle',         label: 'Toggle' },
  { id: 'checkbox-input', label: 'Checkbox Input' },
  { id: 'spinning',       label: 'Spinning' },
  { id: 'lightbox',       label: 'Lightbox' },
  { id: 'org-images',     label: 'Org Images' },
  { id: 'icon-browser',   label: 'Icon Browser' },
];

const ElementsTab = ({ usageMap, pageSlugMap, elementTexts, navigate, onChipClick }) => {
  const [activeSection, setActiveSection] = useState('action-menu');
  return (
    <div className="stui-preview-zone stui-tab-body">

      <div className="stui-mb-20">
        <select className="stui-select" value={activeSection} onChange={e => setActiveSection(e.target.value)} style={{ minWidth: 220 }}>
          {ELEMENT_SECTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {/* Buttons */}
      {activeSection === 'buttons' && (
        <Section title="Buttons" description="Base .stui-btn with .stui-btn-primary (filled teal) and .stui-btn-secondary (ghost gray). App.css live: .btn / .btn-primary / .btn-secondary" cssFile={CSS_LOCATIONS.buttons.file} cssLine={CSS_LOCATIONS.buttons.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 4 }}>
            <Example label=".stui-btn.stui-btn-primary"><button className="stui-btn stui-btn-primary"><Download size={14} /> Download</button></Example>
            <Example label=".stui-btn.stui-btn-primary :disabled"><button className="stui-btn stui-btn-primary" disabled><Download size={14} /> Download</button></Example>
            <Example label=".stui-btn.stui-btn-secondary"><button className="stui-btn stui-btn-secondary">Cancel</button></Example>
            <Example label=".stui-btn.stui-btn-secondary :disabled"><button className="stui-btn stui-btn-secondary" disabled>Cancel</button></Example>
            <CSSRef file="STUI.css" selector=".stui-btn / .stui-btn-primary / .stui-btn-secondary" />
          </div>
          <Subsection label="Reference" />
          <RefTable headers={['Label','Variant','Context']} rows={[['Run Report','btn-primary','Report Preview — starts run'],['Running…','btn-primary','Report Preview — disabled while running'],['Download','btn-primary','File / export trigger'],['Apply','btn-primary','Filter and config panels'],['Save','btn-primary','Settings and forms'],['Add','btn-primary','Create / insert record'],['Cancel','btn-secondary','Dismiss dialog'],['Close','btn-secondary','Modal dismiss']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-btn" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Text Inventory" />
          <TextInventory items={elementTexts?.buttons} emptyMsg="No button labels found" />
          <Subsection label="Configurator" />
          <ButtonConfigurator />
        </Section>
      )}

      {/* Sort Controls */}
      {activeSection === 'sort-controls' && (
        <Section title="Sort Controls" description="Toolbar chips and icon buttons used in grid headers and toolbars to show and reset active sort state." cssFile={CSS_LOCATIONS.sortControls.file} cssLine={CSS_LOCATIONS.sortControls.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 8 }}>
            <div>
              <div className="stui-hint">Sort state chip</div>
              <span className="stui-btn-sort-state">↑ Tag Name</span>
            </div>
            <div>
              <div className="stui-hint">Reset button</div>
              <button className="stui-btn-sort-reset">✕ Clear Sort</button>
            </div>
            <div>
              <div className="stui-hint">Sort chip</div>
              <span className="stui-sort-chip">↑ Name <span className="stui-sort-chip-index">1</span></span>
            </div>
            <div>
              <div className="stui-hint">Icon tab btn</div>
              <div className="stui-flex-4">
                <button className="stui-tab-btn-icon"><List size={14} /></button>
                <button className="stui-tab-btn-icon active"><LayoutGrid size={14} /></button>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-btn-sort-state / .stui-btn-sort-reset / .stui-sort-chip / .stui-tab-btn-icon" />
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-btn-sort-state</td><td>Read-only chip showing active sort column — teal tinted</td></tr>
              <tr><td className="stui-mono">.stui-btn-sort-reset</td><td>Clickable button to clear sort — amber tinted</td></tr>
              <tr><td className="stui-mono">.stui-sort-chip</td><td>Compact sort indicator pill — teal bg, primary text</td></tr>
              <tr><td className="stui-mono">.stui-sort-chip-index</td><td>Multi-sort priority badge — small teal circle inside chip</td></tr>
              <tr><td className="stui-mono">.stui-tab-btn-icon</td><td>Icon-only toggle button — bordered, .active fills teal</td></tr>
            </tbody>
          </table>
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-btn-sort-state" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-tab-btn-icon" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <SortControlsConfigurator />
        </Section>
      )}

      {/* Column Widths */}
      {activeSection === 'column-widths' && (
        <Section title="Column Widths" description="Utility span classes for the 24-column grid layout used in detail forms. Apply stui-col-N to any grid child element to span N of 24 columns. stui-delete-col is a fixed-width checkbox column for row-delete mode." cssFile={CSS_LOCATIONS.columnWidths.file} cssLine={CSS_LOCATIONS.columnWidths.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Column Span Scale" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)', gap: 2, marginBottom: 16, width: '100%' }}>
            {[1,2,3,4,6,8,12,16,18,20,24].map(n => (
              <div key={n} className={`stui-col-${n}`} style={{ background: 'var(--color-teal-600)', borderRadius: 3, padding: '6px 4px', textAlign: 'center', color: 'white', fontSize: 10, fontWeight: 600, gridColumn: `span ${n}` }}>
                {n}
              </div>
            ))}
          </div>
          <Subsection label="Delete Column" />
          <div style={{ overflowX: 'auto', marginBottom: 16 }}>
            <table className="stui-data-grid stui-w-full">
              <thead>
                <tr>
                  <th className="stui-delete-col"><input type="checkbox" readOnly /></th>
                  <th>Tag Name</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="stui-delete-col"><input type="checkbox" readOnly /></td>
                  <td>AI-100</td><td>Analog Input</td>
                </tr>
                <tr>
                  <td className="stui-delete-col"><input type="checkbox" readOnly /></td>
                  <td>DI-201</td><td>Digital Input</td>
                </tr>
              </tbody>
            </table>
          </div>
          <CSSRef file="STUI.css" selector=".stui-col-1 … .stui-col-24 / .stui-delete-col" />
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>CSS</th><th>Notes</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-col-N</td><td className="stui-mono">grid-column: span N</td><td>N = 1–24, applied to grid children inside 24-col form grid</td></tr>
              <tr><td className="stui-mono">.stui-delete-col</td><td className="stui-mono">width: 32px; text-align: center</td><td>Fixed checkbox column shown in delete mode on datasheet grids</td></tr>
            </tbody>
          </table>
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-col-1" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-delete-col" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ColumnWidthsConfigurator />
        </Section>
      )}

      {/* Action Menu */}
      {activeSection === 'action-menu' && (
        <Section title="Action Menu" description="Six-dot GripVertical trigger + dropdown. Generic: Refresh, Download, Modify View, danger Delete. Report variant adds PDF/Excel/CSV, Cancel Export, Clear Filters & Sort. App.css live: .action-menu" cssFile={CSS_LOCATIONS.actionMenu.file} cssLine={CSS_LOCATIONS.actionMenu.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Generic Variant" />
          <div className="stui-example-cell stui-row-end-mb"><DemoActionMenu /></div>
          <Subsection label="Report / Export Variant" />
          <div className="stui-example-cell stui-row-end-mb"><DemoExportMenu /></div>
          <Subsection label="Disabled State" />
          <div className="stui-example-cell stui-row-end-mb">
            <button className="stui-action-menu-btn disabled" disabled title="Disabled"><GripVertical size={16} /></button>
          </div>
          <Subsection label="Reference" />
          <RefTable headers={['Item','Class','Variant','Notes']} rows={[['Refresh','stui-action-menu-item','Generic',''],['Download','stui-action-menu-item','Generic',''],['Modify View','stui-action-menu-item','Generic',''],['Delete','stui-action-menu-item-danger','Generic','Red — destructive'],['Separate Files','stui-action-menu-item','Report','Checkbox inside row'],['Download PDF','stui-action-menu-item','Report','FileText icon'],['Download Excel','stui-action-menu-item','Report','FileSpreadsheet icon'],['Download CSV','stui-action-menu-item','Report','FileDown icon'],['Cancel Export','stui-action-menu-item-danger','Report','Red — Ban icon'],['Clear Filters & Sort','stui-action-menu-item','Report','FilterX icon']]} />
          <CSSRef file="STUI.css" selector=".stui-action-menu-btn / .stui-action-menu-dropdown / .stui-action-menu-item / .stui-action-menu-item-danger" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-action-menu" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Text Inventory" />
          <TextInventory items={elementTexts?.menuItems} emptyMsg="No menu items found" />
          <Subsection label="Configurator" />
          <ActionMenuConfigurator />
        </Section>
      )}

      {/* Select / Combobox */}
      {activeSection === 'select' && (
        <Section title="Select / Combobox" description="Compact select used in toolbars and option rows. Reference: Group By select in Report Viewer." cssFile={CSS_LOCATIONS.select.file} cssLine={CSS_LOCATIONS.select.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 4 }}>
            <Example label=".stui-select">
              <select className="stui-select" defaultValue=""><option value="">-- None --</option><option value="panel">Panel</option><option value="io_type">I/O Type</option><option value="drawing">Drawing</option></select>
            </Example>
            <Example label=".stui-select :disabled">
              <select className="stui-select" disabled defaultValue=""><option value="">-- None --</option></select>
            </Example>
            <CSSRef file="STUI.css" selector=".stui-select" />
          </div>
          <Subsection label="Used By" />
          <Subsection label="Text Inventory" />
          <TextInventory items={elementTexts?.selectOptions?.filter(o => o && !o.match(/^[-—\s]*(none|select|all)[-—\s]*$/i))} emptyMsg="No select options found" />
          <Subsection label="Configurator" />
          <SelectConfigurator />
        </Section>
      )}

      {/* Section Panels */}
      {activeSection === 'section-panels' && (
        <Section title="Section Panels" description="Collapsible card panels with numbered badge, h3 title, optional description, and body. All classes in STUI.css Section 14." cssFile={CSS_LOCATIONS.stepFamily.file} cssLine={CSS_LOCATIONS.stepFamily.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 480 }}>
            <div className="stui-step-panel">
              <div className="stui-step-header">
                <span className="stui-step-number">1</span>
                <h3>Report Configuration</h3>
                <span style={{ marginLeft: 'auto', color: 'var(--color-gray-400)' }}><ChevronDown size={16} /></span>
              </div>
              <p className="stui-step-desc">Select filter values to narrow report results.</p>
              <div style={{ padding: '8px 0', color: 'var(--color-gray-300)', fontSize: 11, fontStyle: 'italic' }}>…body content…</div>
            </div>
            <div className="stui-step-panel">
              <div className="stui-step-header">
                <span className="stui-step-number">2</span>
                <h3>Report Preview</h3>
                <button className="stui-btn stui-btn-primary stui-ml-auto-mr"><Play size={14} /> Run Report</button>
                <span className="stui-c-muted"><ChevronRight size={16} /></span>
              </div>
            </div>
            <CSSRef file="STUI.css" selector=".stui-step-panel / .stui-step-header / .stui-step-number / .stui-step-desc" />
          </div>
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>File</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-step-panel</td><td>STUI.css</td><td>Collapsible card shell — white bg, gray border, rounded, padding lg</td></tr>
              <tr><td className="stui-mono">.stui-step-header</td><td>STUI.css</td><td>Flex row: badge + h3 + optional right controls</td></tr>
              <tr><td className="stui-mono">.stui-step-number</td><td>STUI.css</td><td>Teal circle badge with step number</td></tr>
              <tr><td className="stui-mono">.stui-step-desc</td><td>STUI.css</td><td>Subtitle paragraph — text-sm, gray-500</td></tr>
            </tbody>
          </table>
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-step-header" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Text Inventory" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div><div className="stui-hint">Panel titles (h3)</div><TextInventory items={elementTexts?.panelTitles} emptyMsg="No panel titles found" /></div>
            <div><div className="stui-hint">Descriptions</div><TextInventory items={elementTexts?.panelDescs} emptyMsg="No panel descriptions found" /></div>
          </div>
          <Subsection label="Configurator" />
          <SectionPanelConfigurator />
        </Section>
      )}

      {/* Data Grid */}
      {activeSection === 'data-grid' && (
        <Section title="Data Grid" description="Scrollable table with sticky headers, alternating rows, teal row hover. Clickable rows use .clickable. App.css live: .data-grid-container / .data-grid" cssFile={CSS_LOCATIONS.dataGrid.file} cssLine={CSS_LOCATIONS.dataGrid.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ marginBottom: 4 }}>
            <MiniGrid />
            <CSSRef file="STUI.css" selector=".stui-data-grid-container / .stui-data-grid / .stui-data-grid th / .stui-data-grid td" />
          </div>
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-data-grid-container</td><td>Outer wrapper — overflow hidden, border, rounded, shadow</td></tr>
              <tr><td className="stui-mono">.stui-data-grid-scroll-area</td><td>Horizontal scroll wrapper inside the container</td></tr>
              <tr><td className="stui-mono">.stui-data-grid</td><td>Table element — full width, collapse borders, alternating rows, sticky thead</td></tr>
              <tr><td className="stui-mono">.stui-data-grid th</td><td>Header cell — primary bg, white text, resizable, position relative</td></tr>
              <tr><td className="stui-mono">.stui-data-grid td</td><td>Data cell — text-sm, gray-700, left-aligned</td></tr>
              <tr><td className="stui-mono">.stui-no-column-resize</td><td>Modifier — disables the column resize handle on th</td></tr>
              <tr><td colSpan={2} style={{ background: 'var(--color-gray-50)', color: 'var(--color-gray-400)', fontSize: 11, fontStyle: 'italic' }}>Filter / Sort — triggered from column headers (see Filter / Sort section)</td></tr>
              <tr><td className="stui-mono">.stui-filter-dropdown</td><td>Fixed popup container — white, shadowed, flex column</td></tr>
              <tr><td className="stui-mono">.stui-filter-sort-btn</td><td>Full-width sort button — hover + .active state</td></tr>
              <tr><td className="stui-mono">.stui-filter-search</td><td>Search input wrapper</td></tr>
              <tr><td className="stui-mono">.stui-filter-options</td><td>Scrollable checkbox list (max 200px)</td></tr>
              <tr><td className="stui-mono">.stui-filter-option</td><td>Checkbox row — hover highlight</td></tr>
            </tbody>
          </table>
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-data-grid-container" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-filter-dropdown" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <DataGridConfigurator />
        </Section>
      )}

      {/* Filter / Sort */}
      {activeSection === 'filter-sort' && (
        <Section title="Filter / Sort Dropdown" description="Column header popup: sort buttons at top, search input, scrollable checkbox list. Triggered from stui-data-grid column headers." cssFile={CSS_LOCATIONS.filterSort.file} cssLine={CSS_LOCATIONS.filterSort.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ position: 'relative', height: 280, marginBottom: 16 }}>
            <div className="stui-filter-dropdown" style={{ position: 'relative', width: 240 }}>
              <div className="stui-filter-sort-section">
                <button className="stui-filter-sort-btn active"><span>↑ Sort A to Z</span></button>
                <button className="stui-filter-sort-btn"><span>↓ Sort Z to A</span></button>
              </div>
              <div className="stui-filter-divider" />
              <div className="stui-filter-search">
                <input type="text" placeholder="Search" readOnly />
              </div>
              <div className="stui-filter-options">
                <label className="stui-filter-option stui-filter-option-select-all">
                  <input type="checkbox" defaultChecked readOnly /> <span>(Select All)</span>
                </label>
                {['Alpha', 'Bravo', 'Charlie', 'Delta'].map(v => (
                  <label key={v} className="stui-filter-option">
                    <input type="checkbox" defaultChecked readOnly /> <span>{v}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-filter-dropdown</td><td>Fixed popup container — white, shadowed, flex column</td></tr>
              <tr><td className="stui-mono">.stui-filter-sort-section</td><td>Top section wrapping sort buttons</td></tr>
              <tr><td className="stui-mono">.stui-filter-sort-btn</td><td>Full-width sort button — hover + .active state</td></tr>
              <tr><td className="stui-mono">.stui-filter-divider</td><td>1px horizontal separator</td></tr>
              <tr><td className="stui-mono">.stui-filter-search</td><td>Search input wrapper</td></tr>
              <tr><td className="stui-mono">.stui-filter-options</td><td>Scrollable checkbox list (max 200px)</td></tr>
              <tr><td className="stui-mono">.stui-filter-option</td><td>Checkbox row — hover highlight</td></tr>
              <tr><td className="stui-mono">.stui-filter-option-select-all</td><td>Select All row — bottom border separator</td></tr>
            </tbody>
          </table>
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-filter-dropdown" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <FilterConfigurator />
        </Section>
      )}

      {/* Section Layout */}
      {activeSection === 'section-layout' && (
        <Section title="Section Layout" description="The style guide's own scaffolding: section cards, header bar, CSS chip, subsection dividers, example cells, and config output blocks." cssFile={CSS_LOCATIONS.sectionLayout.file} cssLine={CSS_LOCATIONS.sectionLayout.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Section Card" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <Example label=".stui-section-card"><div className="stui-section-card" style={{ width: 140, maxHeight: 60, overflow: 'hidden' }}><div className="stui-section-header"><div className="stui-section-header-left"><h2 style={{ fontSize: 12 }}>Title</h2></div></div></div></Example>
            <Example label=".stui-css-chip"><span className="stui-css-chip" style={{ pointerEvents: 'none' }}>STUI.css : L724 ↗</span></Example>
          </div>
          <Subsection label="Subsection Divider" />
          <div className="stui-subsection-divider">Preview</div>
          <div className="stui-subsection-divider">Reference</div>
          <Subsection label="Example Cell" />
          <div className="stui-example-cell" style={{ width: 180 }}>Demo content here</div>
          <Subsection label="Config Output" />
          <div className="stui-config-output">stui-btn-primary · Download icon · label "Download" · enabled</div>
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-section-card" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <SectionLayoutConfigurator />
        </Section>
      )}

      {/* Module Header */}
      {activeSection === 'module-header' && (
        <Section title="Module Header" description="Full-width header bar with icon, title, and meta count on the left; plus, pencil/save, and action menu on the right." cssFile={CSS_LOCATIONS.moduleHeader.file} cssLine={CSS_LOCATIONS.moduleHeader.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          {[
            { label: 'Default', themeClass: '' },
            { label: 'Dark',    themeClass: 'stui-theme-dark' },
            { label: 'Dim',     themeClass: 'stui-theme-dim' },
            { label: 'Sepia',   themeClass: 'stui-theme-sepia' },
          ].map(({ label, themeClass }) => (
            <div key={label} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, color: 'var(--color-gray-400)', marginBottom: 2 }}>{label}</div>
              <div className={`stui-preview-zone ${themeClass}`} style={{ border: '1px solid var(--color-gray-200)', borderRadius: 4 }}>
                <div className="stui-module-header-styleguideonly">
                  <div className="stui-module-header-left">
                    <Baby size={20} className="stui-page-icon" />
                    <h1>Module Title</h1>
                    <span className="stui-module-header-meta">42 records</span>
                  </div>
                  <div className="stui-module-header-actions">
                    <button className="stui-crud-btn" disabled title="Add"><Plus size={16} /></button>
                    <button className="stui-crud-btn" disabled title="Edit"><Pencil size={16} /></button>
                    <button className="stui-crud-btn" disabled title="Save"><Save size={16} /></button>
                    <button className="stui-action-menu-btn disabled" disabled title="Actions"><GripVertical size={16} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-module-header</td><td>Outer bar — flex, space-between, bottom border</td></tr>
              <tr><td className="stui-mono">.stui-module-header-left</td><td>Icon + title + meta group (flex row, gap-lg)</td></tr>
              <tr><td className="stui-mono">.stui-module-header-left h1</td><td>Title — text-lg, primary color, no margin</td></tr>
              <tr><td className="stui-mono">.stui-module-header-meta</td><td>Record count / subtitle — text-sm, gray-500</td></tr>
              <tr><td className="stui-mono">.stui-module-header-actions</td><td>Right side button group (flex row, gap-sm)</td></tr>
              <tr><td className="stui-mono">.stui-crud-btn</td><td>32×32 circular icon button — primary color</td></tr>
              <tr><td className="stui-mono">.stui-crud-btn:disabled</td><td>Gray-out — 50% opacity, not-allowed cursor</td></tr>
            </tbody>
          </table>
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-module-header" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ModuleHeaderConfigurator />
        </Section>
      )}

      {activeSection === 'boolean' && (
        <Section title="Boolean Display" description="Read-only boolean cell used inside datasheet grids. A centered container with an optional success-colored check icon." cssFile={CSS_LOCATIONS.booleanDisplay.file} cssLine={CSS_LOCATIONS.booleanDisplay.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginBottom: 8 }}>
            <div>
              <div className="stui-hint">True</div>
              <div className="stui-boolean-display">
                <span className="stui-boolean-check">✓</span>
              </div>
            </div>
            <div>
              <div className="stui-hint">False</div>
              <div className="stui-boolean-display" />
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-boolean-display / .stui-boolean-check" />
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-boolean-display</td><td>Flex container — centered, min-height 20px</td></tr>
              <tr><td className="stui-mono">.stui-boolean-check</td><td>Check mark — success color</td></tr>
            </tbody>
          </table>
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-boolean-display" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <BooleanDisplayConfigurator />
        </Section>
      )}

      {activeSection === 'breadcrumb' && (
        <Section title="Breadcrumb" description="Inline navigation trail showing parent context and current location. Use inside a flex row." cssFile={CSS_LOCATIONS.breadcrumb.file} cssLine={CSS_LOCATIONS.breadcrumb.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-breadcrumb" style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span className="stui-breadcrumb-parent">Projects</span>
            <span className="stui-breadcrumb-arrow"> › </span>
            <span className="stui-breadcrumb-parent">BU28</span>
            <span className="stui-breadcrumb-arrow"> › </span>
            <span className="stui-breadcrumb-current">Instrumentation</span>
          </div>
          <CSSRef file="STUI.css" selector=".stui-breadcrumb / .stui-breadcrumb-parent / .stui-breadcrumb-arrow / .stui-breadcrumb-current" />
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-breadcrumb</td><td>Container — font-primary, text-sm, weight 500</td></tr>
              <tr><td className="stui-mono">.stui-breadcrumb-parent</td><td>Ancestor segment — opacity 0.7</td></tr>
              <tr><td className="stui-mono">.stui-breadcrumb-arrow</td><td>Separator character — opacity 0.6, margin 0 2px</td></tr>
              <tr><td className="stui-mono">.stui-breadcrumb-current</td><td>Active segment — font-weight 600</td></tr>
            </tbody>
          </table>
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-breadcrumb" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <BreadcrumbConfigurator />
        </Section>
      )}

      {activeSection === 'badge' && (
        <Section title="Badge" description="Inline status pill — rounded, colored bg/text. Used in tables and admin pages to show record or invitation status." cssFile={CSS_LOCATIONS.badge.file} cssLine={CSS_LOCATIONS.badge.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="stui-badge-accepted">Accepted</span>
            <span className="stui-badge-expired">Expired</span>
            <span className="stui-badge-none">Not Invited</span>
            <span className="stui-badge-pending">Invite Sent</span>
            <span className="stui-badge-construction">Construction</span>
          </div>
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>File</th><th>Appearance</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-badge</td><td>STUI.css</td><td>Base shell — rounded pill, 11px bold. No color.</td></tr>
              <tr><td className="stui-mono">.stui-badge-accepted</td><td>STUI.css</td><td>Green — #dcfce7 bg / #166534 text</td></tr>
              <tr><td className="stui-mono">.stui-badge-expired</td><td>STUI.css</td><td>Gray — #f3f4f6 bg / #6b7280 text</td></tr>
              <tr><td className="stui-mono">.stui-badge-none</td><td>STUI.css</td><td>Red — #fee2e2 bg / #b91c1c text</td></tr>
              <tr><td className="stui-mono">.stui-badge-pending</td><td>STUI.css</td><td>Yellow — #fef9c3 bg / #92400e text</td></tr>
              <tr><td className="stui-mono">.stui-badge-construction</td><td>STUI.css</td><td>Orange — #fed7aa bg / #9a3412 text</td></tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-badge-accepted" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-badge-accepted" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <BadgeConfigurator />
        </Section>
      )}

      {activeSection === 'tile' && (
        <Section title="Tile" description="Dashboard tile card — white bg, border, rounded corners, hover lift with teal border. Sub-elements: header (icon + title), content area, animated legend." cssFile={CSS_LOCATIONS.tile.file} cssLine={CSS_LOCATIONS.tile.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div className="stui-tile stui-clickable" style={{ width: 220 }}>
              <div className="stui-tile-header">
                <FileText size={16} className="stui-tile-icon" />
                <span className="stui-tile-title">Drawing Log</span>
              </div>
              <div className="stui-tile-content stui-text-placeholder">
                …chart content…
              </div>
              <div className="stui-tile-legend">
                <span className="stui-legend-item"><span className="stui-legend-dot" style={{ background: 'var(--color-primary)' }} />Series A</span>
                <span className="stui-legend-item"><span className="stui-legend-dot" style={{ background: '#6b7280' }} />Series B</span>
              </div>
            </div>
            <div className="stui-tile" style={{ width: 220 }}>
              <div className="stui-tile-header">
                <FileText size={16} className="stui-tile-icon" />
                <span className="stui-tile-title">Not Clickable</span>
              </div>
              <div className="stui-tile-content stui-text-placeholder">
                …chart content…
              </div>
            </div>
          </div>
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-tile</td><td>Outer card — white bg, border, rounded, hover lift</td></tr>
              <tr><td className="stui-mono">.stui-tile.clickable</td><td>Pointer cursor (explicit opt-in)</td></tr>
              <tr><td className="stui-mono">.stui-tile:not(.clickable)</td><td>Default cursor, no hover lift</td></tr>
              <tr><td className="stui-mono">.stui-tile-header</td><td>Flex row: icon + title, bottom border</td></tr>
              <tr><td className="stui-mono">.stui-tile-icon</td><td>Primary color icon</td></tr>
              <tr><td className="stui-mono">.stui-tile-title</td><td>text-sm, bold, gray-700</td></tr>
              <tr><td className="stui-mono">.stui-tile-content</td><td>Centered flex area — chart goes here</td></tr>
              <tr><td className="stui-mono">.stui-tile-legend</td><td>Animated legend — hidden, fades in on hover after 0.6s</td></tr>
              <tr><td className="stui-mono">.stui-legend-item</td><td>Dot + label row</td></tr>
              <tr><td className="stui-mono">.stui-legend-dot</td><td>8px circle — color set inline per series</td></tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-tile" />
          <Subsection label="Configurator" />
          <TileConfigurator />
        </Section>
      )}

      {activeSection === 'stat-row' && (
        <Section title="Stat Row" description="Horizontal strip of KPI stats separated by a top border. Each stat is a label (9px uppercase) + value (13px bold) pair. Used in report toolbars." cssFile={CSS_LOCATIONS.statRow.file} cssLine={CSS_LOCATIONS.statRow.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-stat-row">
            <div className="stui-stat-group">
              {[
                { label: 'Rows',   value: '1,284' },
                { label: 'Pages',  value: '43' },
                { label: 'Layout', value: 'tabloid' },
                { label: 'Time',   value: '312ms' },
              ].map(({ label, value }) => (
                <div key={label} className="stui-stat-item">
                  <span className="stui-stat-label">{label}</span>
                  <span className="stui-stat-value">{value}</span>
                </div>
              ))}
            </div>
          </div>
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-stat-row</td><td>Outer strip — top border, flex wrap, gap 16px</td></tr>
              <tr><td className="stui-mono">.stui-stat-group</td><td>Inner flex group — gap 24px between stat items</td></tr>
              <tr><td className="stui-mono">.stui-stat-item</td><td>Label + value pair — baseline aligned, gap 5px</td></tr>
              <tr><td className="stui-mono">.stui-stat-label</td><td>9px bold uppercase gray-400 — the KPI name</td></tr>
              <tr><td className="stui-mono">.stui-stat-value</td><td>13px semibold gray-700 — the KPI value</td></tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-stat-row" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-stat-row" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <StatRowConfigurator />
        </Section>
      )}

      {activeSection === 'label' && (
        <Section title="Label" description="Inline field label — 11px semibold, gray-600, no-wrap. Use alongside selects and inputs in toolbar option rows." cssFile={CSS_LOCATIONS.label.file} cssLine={CSS_LOCATIONS.label.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="stui-label">Sort By:</span>
            <select className="stui-select"><option>Column A</option></select>
            <span className="stui-label">Group By:</span>
            <select className="stui-select"><option>Column B</option></select>
          </div>
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-label</td><td>11px semibold gray-600, no-wrap — toolbar field label</td></tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-label" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-label" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <LabelConfigurator />
        </Section>
      )}

      {activeSection === 'checkbox' && (
        <Section title="Checkbox" description="Inline checkbox + label row. Apply to a <label> element wrapping an <input type=checkbox>. Flex, gap-6, text-xs, gray-600, pointer cursor." cssFile={CSS_LOCATIONS.checkbox.file} cssLine={CSS_LOCATIONS.checkbox.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-8">
            <label className="stui-checkbox"><input type="checkbox" defaultChecked /> Show QR Code in report header</label>
            <label className="stui-checkbox"><input type="checkbox" /> Include cover page</label>
            <label className="stui-checkbox"><input type="checkbox" defaultChecked /> Separate sheets per group</label>
          </div>
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-checkbox</td><td>On &lt;label&gt; — flex row, gap-6, text-xs, gray-600, pointer, no select</td></tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-checkbox" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-checkbox" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <CheckboxConfigurator />
        </Section>
      )}

      {activeSection === 'checkbox-input' && (
        <Section title="Checkbox Input" description="Fully custom-styled native checkbox replacing App.css .themed-checkbox. Apply stui-checkbox-input directly to <input type=checkbox>. Use inside a .stui-checkbox label wrapper." cssFile={CSS_LOCATIONS.checkboxInput.file} cssLine={CSS_LOCATIONS.checkboxInput.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-8">
            <label className="stui-checkbox"><input type="checkbox" className="stui-checkbox-input" defaultChecked /> Show QR Code in report header</label>
            <label className="stui-checkbox"><input type="checkbox" className="stui-checkbox-input" /> Include cover page</label>
            <label className="stui-checkbox"><input type="checkbox" className="stui-checkbox-input" disabled defaultChecked /> Disabled checked</label>
          </div>
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class / State</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-checkbox-input</td><td>On &lt;input type=checkbox&gt; — 1rem × 1rem, gray border, white bg, rounded-3</td></tr>
              <tr><td className="stui-mono">.stui-checkbox-input:checked</td><td>Teal fill + border-color — matches --color-primary</td></tr>
              <tr><td className="stui-mono">.stui-checkbox-input:checked::after</td><td>White checkmark drawn via border transform trick</td></tr>
              <tr><td className="stui-mono">.stui-checkbox-input:focus-visible</td><td>2px teal outline for keyboard nav accessibility</td></tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-checkbox-input" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-checkbox-input" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'spinning' && (
        <Section title="Spinning" description="Continuous rotation animation. Apply stui-spinning to any element to spin it indefinitely at 1s per revolution. Used on loading/refresh icons." cssFile={CSS_LOCATIONS.spinning.file} cssLine={CSS_LOCATIONS.spinning.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-8" style={{ gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <svg className="stui-spinning" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
              <span className="stui-label">Loading spinner (RefreshCw icon)</span>
            </div>
          </div>
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class / Rule</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">@keyframes stui-spin</td><td>0→360° rotation keyframe definition</td></tr>
              <tr><td className="stui-mono">.stui-spinning</td><td>Applies stui-spin animation — 1s linear infinite</td></tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector="@keyframes stui-spin / .stui-spinning" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-spinning" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'lightbox' && (
        <Section title="Lightbox" description="Full-screen image preview overlay. Triggered when clicking an image in view mode. Click overlay to dismiss." cssFile={CSS_LOCATIONS.lightbox.file} cssLine={CSS_LOCATIONS.lightbox.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-lightbox-overlay</td><td>Fixed full-screen overlay — rgba(0,0,0,0.8) bg, z-index 9999, flex center</td></tr>
              <tr><td className="stui-mono">.stui-lightbox-content</td><td>Inner content box — white border, checkerboard bg (shows transparency), relative position</td></tr>
              <tr><td className="stui-mono">.stui-lightbox-img</td><td>Image element — max 90vw × 80vh, object-fit contain</td></tr>
              <tr><td className="stui-mono">.stui-lightbox-label</td><td>Caption below image — 11px gray-500, text-center, padding-top 8px</td></tr>
              <tr><td className="stui-mono">.stui-lightbox-close</td><td>X button — absolute top-right, icon button style</td></tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-lightbox-overlay" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-lightbox-overlay" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'org-images' && (
        <Section title="Org Images" description="Organization logo and icon management table — upload, preview, hover-to-delete. Used in Organizations admin page." cssFile={CSS_LOCATIONS.orgImages.file} cssLine={CSS_LOCATIONS.orgImages.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-org-img-table</td><td>Full-width collapsing table for logo/icon rows</td></tr>
              <tr><td className="stui-mono">.stui-org-img-label</td><td>Left td — semibold label cell, 80px wide, middle-aligned</td></tr>
              <tr><td className="stui-mono">.stui-org-img-cell</td><td>Center td — 50px wide, center-aligned (reserved)</td></tr>
              <tr><td className="stui-mono">.stui-org-img-actions</td><td>Right td — right-aligned, holds upload btn or image wrap</td></tr>
              <tr><td className="stui-mono">.stui-org-upload-btn</td><td>Upload trigger — icon+arrow button, shows on hover (pure CSS, no JS)</td></tr>
              <tr><td className="stui-mono">.stui-org-img-wrap</td><td>Wrapper for image + delete overlay — relative, inline-block</td></tr>
              <tr><td className="stui-mono">.stui-org-icon-img</td><td>Icon image — 32×32px display</td></tr>
              <tr><td className="stui-mono">.stui-org-logo-img</td><td>Logo image — max 120px wide, max 40px tall</td></tr>
              <tr><td className="stui-mono">.stui-org-icon-img.dimmed, .stui-org-logo-img.dimmed</td><td>Opacity 0.3 — applied on hover to reveal delete button</td></tr>
              <tr><td className="stui-mono">.stui-org-img-delete</td><td>Delete overlay button — absolute centered Trash2 icon, shows on JS hover</td></tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-org-img-table" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-org-img-table" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'icon-browser' && (
        <Section title="Icon Browser" description="CSS grid of icon cards used in the Icon Browser development page. Each card shows the icon, its name, and a copy indicator." cssFile={CSS_LOCATIONS.iconBrowser.file} cssLine={CSS_LOCATIONS.iconBrowser.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-icon-grid</td><td>6-column CSS grid, 80px min column, gap 8px, padding 16px</td></tr>
              <tr><td className="stui-mono">.stui-icon-card</td><td>Button card — flex column center, white bg, border, rounded-md, hover lift</td></tr>
              <tr><td className="stui-mono">.stui-icon-card:hover</td><td>Teal-50 bg, primary border, slight translateY(-1px)</td></tr>
              <tr><td className="stui-mono">.stui-icon-card.copied</td><td>Teal border + teal-50 bg — flash state after copy</td></tr>
              <tr><td className="stui-mono">.stui-icon-preview</td><td>48×48px icon container — flex center</td></tr>
              <tr><td className="stui-mono">.stui-icon-name</td><td>11px monospace label below icon</td></tr>
              <tr><td className="stui-mono">.stui-icon-copy-indicator</td><td>Absolute bottom-right copy icon — opacity 0 until hover or copied</td></tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-icon-grid" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-icon-grid" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'sub-label' && (
        <Section title="Sub-Label" description="Secondary helper text beneath a field label — 11px, gray-500. Based on App.css .pref-sublabel." cssFile={CSS_LOCATIONS.subLabel.file} cssLine={CSS_LOCATIONS.subLabel.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-12">
            <div>
              <span className="stui-label">Theme</span>
              <div className="stui-sub-label">Controls the color palette across all pages</div>
            </div>
            <div>
              <span className="stui-label">Layout</span>
              <div className="stui-sub-label">Affects report page size and column widths</div>
            </div>
          </div>
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-sub-label</td><td>11px gray-500, margin-top 1px — secondary helper text</td></tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-sub-label" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-sub-label" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <SubLabelConfigurator />
        </Section>
      )}

      {activeSection === 'pill' && (
        <Section title="Pill" description="Selectable pill button — rounded, teal fill when selected, gray border when not. Use .stui-pill-group as the flex container." cssFile={CSS_LOCATIONS.pill.file} cssLine={CSS_LOCATIONS.pill.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-pill-group">
            <button className="stui-pill selected">Light</button>
            <button className="stui-pill">Dark</button>
            <button className="stui-pill">Dim</button>
            <button className="stui-pill">Sepia</button>
            <button className="stui-pill">High Contrast</button>
          </div>
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-pill-group</td><td>Flex container — gap 6px, wrap</td></tr>
              <tr><td className="stui-mono">.stui-pill</td><td>12px bold, rounded-12, gray border, pointer</td></tr>
              <tr><td className="stui-mono">.stui-pill.selected</td><td>Teal fill — primary bg + border, white text</td></tr>
              <tr><td className="stui-mono">.stui-pill:hover:not(.selected)</td><td>Gray-100 bg on hover</td></tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-pill" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-pill" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <PillConfigurator />
        </Section>
      )}

      {activeSection === 'toggle' && (
        <Section title="Toggle" description="iOS-style toggle switch. Use .stui-toggle-group to wrap multiple toggles. Apply .stui-toggle to a <label> containing an <input type=checkbox> followed by a <span class=stui-toggle-slider>." cssFile={CSS_LOCATIONS.toggle.file} cssLine={CSS_LOCATIONS.toggle.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-toggle-group" style={{ flexDirection: 'column', gap: 12 }}>
            {[['Enable notifications', true], ['Dark mode override', false], ['Show QR code', true]].map(([lbl, on]) => (
              <div key={lbl} className="stui-row-10">
                <label className="stui-toggle">
                  <input type="checkbox" defaultChecked={on} readOnly />
                  <span className="stui-toggle-slider" />
                </label>
                <span className="stui-label">{lbl}</span>
              </div>
            ))}
          </div>
          <Subsection label="Reference" />
          <table className="stui-data-grid stui-full-mb">
            <thead><tr><th>Class</th><th>Role</th></tr></thead>
            <tbody>
              <tr><td className="stui-mono">.stui-toggle-group</td><td>Flex container for toggle rows — gap 6px, wraps</td></tr>
              <tr><td className="stui-mono">.stui-toggle</td><td>On &lt;label&gt; — 40×22px container, position relative</td></tr>
              <tr><td className="stui-mono">.stui-toggle input</td><td>Hidden checkbox — opacity 0, no dimensions</td></tr>
              <tr><td className="stui-mono">.stui-toggle-slider</td><td>On &lt;span&gt; — gray pill, transitions to teal on checked</td></tr>
              <tr><td className="stui-mono">.stui-toggle-slider::before</td><td>White circle knob — slides right 18px on checked</td></tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-toggle-group / .stui-toggle" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-toggle-group" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-toggle" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ToggleConfigurator />
        </Section>
      )}

      {activeSection === 'setting-row' && (
        <Section title="Setting Row" description="Label-left / control-right flex row for settings pages. Use stui-row-setting as the outer row, stui-row-setting-label for the label stack, and stui-row-setting-controls for a group of buttons or inputs on the right." cssFile={CSS_LOCATIONS.settingRow.file} cssLine={CSS_LOCATIONS.settingRow.line} onChipClick={onChipClick} noBodyScroll>
          <Example label="Toggle row">
            <div className="stui-row-setting">
              <div className="stui-row-setting-label">
                <div className="stui-label">Enable Feature</div>
                <div className="stui-sublabel">Turns this option on for all users on this machine</div>
              </div>
              <label className="stui-toggle">
                <input type="checkbox" defaultChecked />
                <span className="stui-toggle-slider" />
              </label>
            </div>
          </Example>
          <Example label="Select row">
            <div className="stui-row-setting">
              <div className="stui-row-setting-label">
                <div className="stui-label">Default Database</div>
                <div className="stui-sublabel">Database to open on login</div>
              </div>
              <select className="stui-select"><option>Production</option><option>Staging</option></select>
            </div>
          </Example>
          <Example label="Controls row (path + buttons)">
            <div className="stui-row-setting">
              <div className="stui-row-setting-label">
                <div className="stui-label">Download Location</div>
                <div className="stui-sublabel">Where exported files are saved</div>
              </div>
              <div className="stui-row-setting-controls">
                <span className="stui-text-sm-muted">~/Downloads/DAWN</span>
                <button className="stui-btn stui-btn-primary">Change</button>
              </div>
            </div>
          </Example>
          <Subsection label="Class Reference" />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <tbody>
              <tr><td className="stui-mono">.stui-row-setting</td><td>Outer row — flex, space-between, gap 12px, padding 8px 0</td></tr>
              <tr><td className="stui-mono">.stui-row-setting-label</td><td>Left column — flex column for label + sublabel stack</td></tr>
              <tr><td className="stui-mono">.stui-row-setting-controls</td><td>Right column — flex row, gap 8px, no-shrink, for buttons/inputs</td></tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-row-setting" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-row-setting" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <SettingRowConfigurator />
        </Section>
      )}

    </div>
  );
};

/* ── Tab: Reports ──────────────────────────────────────────── */

const REPORTS_SECTIONS = [
  { id: 'page-header',    label: 'Page Header'    },
  { id: 'section-panels', label: 'Section Panels' },
  { id: 'report-viewer',  label: 'Report Viewer'  },
  { id: 'report-preview', label: 'Report Preview' },
];

const ReportsTab = ({ usageMap, pageSlugMap, elementTexts, navigate, onChipClick }) => {
  const [activeSection, setActiveSection] = useState('page-header');
  return (
    <div className="stui-preview-zone stui-tab-body">

      <div className="stui-mb-20">
        <select className="stui-select" value={activeSection} onChange={e => setActiveSection(e.target.value)} style={{ minWidth: 220 }}>
          {REPORTS_SECTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {activeSection === 'page-header' && (
        <Section title="Page Header" description="Flex row at the top of every module page. White background with gray bottom border, title left, action menu right." cssFile={CSS_LOCATIONS.pageHeader.file} cssLine={CSS_LOCATIONS.pageHeader.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ border: '1px solid var(--color-gray-200)', borderRadius: 6, overflow: 'hidden', marginBottom: 4, maxWidth: 700 }}>
            <div className="stui-page-header">
              <div className="stui-row-md">
                <h1 className="stui-page-title">
                  <FileSpreadsheet size={20} />I/O Assignments
                </h1>
                <span className="stui-text-sm-muted">Tag IO assignment details by panel, drop, slot, channel</span>
              </div>
              <DemoExportMenu />
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-page-header" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Property', 'Value', 'Notes']}
            rows={[
              ['display',          'flex',                              ''],
              ['align-items',      'center',                            ''],
              ['justify-content',  'space-between',                     'Title left, action menu right'],
              ['background-color', 'var(--color-bg-white)',             'White'],
              ['border-bottom',    '1px solid var(--color-gray-200)',   'Subtle bottom separator'],
              ['color',            'var(--color-gray-800)',             ''],
              ['font-family',      'var(--font-secondary)',             'Rajdhani / Roboto Condensed'],
              ['font-weight',      '500',                               ''],
              ['padding',          'var(--spacing-sm) 0.75rem',        '8px 12px'],
              ['min-height',       '49px',                              ''],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-page-header" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Text Inventory" />
          <div className="stui-text-italic">
            Page titles vary per module — not included in element-texts scan.
          </div>
          <Subsection label="Configurator" />
          <ReportHeaderConfigurator />
        </Section>
      )}

      {activeSection === 'section-panels' && (
        <Section title="Section Panels" description="Collapsible card sections separating configuration from preview. All classes in STUI.css Section 14." cssFile={CSS_LOCATIONS.stepFamily.file} cssLine={CSS_LOCATIONS.stepFamily.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 500 }}>
            <div className="stui-step-panel">
              <div className="stui-step-header">
                <span className="stui-step-number">1</span>
                <h3>Report Configuration</h3>
                <span style={{ marginLeft: 'auto', color: 'var(--color-gray-400)' }}><ChevronDown size={16} /></span>
              </div>
              <p className="stui-step-desc">Select filter values to narrow report results.</p>
              <div style={{ padding: '8px 0', color: 'var(--color-gray-400)', fontSize: 12, fontStyle: 'italic' }}>…parameter controls…</div>
            </div>
            <div className="stui-step-panel">
              <div className="stui-step-header">
                <span className="stui-step-number">2</span>
                <h3>Report Preview</h3>
                <button className="stui-btn stui-btn-primary stui-ml-auto-mr"><Play size={14} /> Run Report</button>
                <span className="stui-c-muted"><ChevronRight size={16} /></span>
              </div>
            </div>
            <CSSRef file="STUI.css" selector=".stui-step-panel / .stui-step-header / .stui-step-number / .stui-step-desc" />
          </div>
          <Subsection label="Text Inventory" />
          <div>
            <div className="stui-hint">Panel titles</div>
            <TextInventory items={elementTexts?.panelTitles} emptyMsg="No panel titles found" />
          </div>
          <Subsection label="Configurator" />
          <SectionPanelConfigurator />
        </Section>
      )}

      {activeSection === 'report-viewer' && (
        <Section title="Report Viewer" description="Modifier classes applied inside .stui-report-viewer. Extend step-panel behavior for collapsible sections, toolbar rows, and pre-run placeholders." cssFile={CSS_LOCATIONS.reportViewer.file} cssLine={CSS_LOCATIONS.reportViewer.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 560 }}>
            {/* Clickable step header with action + chevron */}
            <div className="stui-step-panel stui-step-panel-no-overflow">
              <div className="stui-step-header clickable">
                <span className="stui-step-number">2</span>
                <h3>Report Preview</h3>
                <button className="stui-btn stui-btn-primary stui-step-action"><Play size={14} /> Run Report</button>
                <span className="stui-step-chevron"><ChevronDown size={16} /></span>
              </div>
            </div>
            {/* Options row */}
            <div className="stui-step-panel">
              <div className="stui-step-header clickable">
                <span className="stui-step-number">1</span>
                <h3>Report Configuration</h3>
                <span className="stui-step-chevron"><ChevronDown size={16} /></span>
              </div>
              <div className="stui-options-row">
                <div className="stui-sort-slot">
                  <span style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>Sort by</span>
                  <select className="stui-select" style={{ fontSize: 12 }}><option>Panel</option><option>Tag</option></select>
                </div>
                <div className="stui-sort-slot">
                  <span style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>Then by</span>
                  <select className="stui-select" style={{ fontSize: 12 }}><option>None</option><option>Drop</option></select>
                </div>
              </div>
            </div>
            {/* Pre-run placeholder */}
            <div className="stui-step-panel">
              <div className="stui-report-placeholder">
                <p>Configure options above, then click Run Report to generate the preview.</p>
              </div>
            </div>
          </div>
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Description', 'Key Properties']}
            rows={[
              ['.stui-step-panel-no-overflow',  'Hides horizontal overflow on preview panel',       'overflow-x: hidden'],
              ['.stui-step-header.clickable',    'Marks step header as collapsible toggle',          'cursor: pointer; user-select: none'],
              ['.stui-step-chevron',             'Chevron pushed to right edge of step header',      'margin-left: auto; display: flex; align-items: center'],
              ['.stui-step-action',              'Primary action button pushed left of chevron',     'margin-left: auto; margin-right: 12px'],
              ['.stui-options-row',              'Toolbar row — sort selects, checkboxes, group-by', 'display: flex; gap: 16px; border-top; flex-wrap: wrap'],
              ['.stui-sort-slot',                'Single label + select(s) unit in options row',     'display: flex; align-items: center; gap: 4px'],
              ['.stui-report-placeholder',       'Empty-state shown before first run',               'padding: 60px 20px; text-align: center; color: #999'],
              ['.stui-report-placeholder p',     'Placeholder message text',                         'font-size: 16px; margin: 0'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-report-viewer" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'report-preview' && (
        <Section title="Report Preview" description="stui-rpt-preview-* — scaled HTML preview that mirrors PDF export. All pixel values match ReportPdfStyles.js and must not be converted to vars. Dynamic values (scale, column widths, brand color, zebra) remain inline." cssFile={CSS_LOCATIONS.reportPreview.file} cssLine={CSS_LOCATIONS.reportPreview.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Description', 'Key Properties']}
            rows={[
              ['.stui-rpt-preview-container',    'Root wrapper — pins all CSS vars to light-mode defaults; overflow: hidden',        'CSS var overrides (theme immune); overflow: hidden'],
              ['.stui-rpt-preview-scale',         'Scale transform wrapper — transform-origin fixed; transform/width/marginBottom inline', 'transform-origin: top left'],
              ['.stui-rpt-preview-header',        'Page header — WM logo left, title centered (absolute), params right',              'display: flex; padding: 10px 32px; border-bottom: 2px solid #1a5568'],
              ['.stui-rpt-preview-logo-block',    'Left block containing logo + optional QR code',                                    'flex: 0 0 auto; display: flex; gap: 10px'],
              ['.stui-rpt-preview-qr-block',      'QR code column — stacked code + label',                                           'display: flex; flex-direction: column; align-items: center'],
              ['.stui-rpt-preview-qr-label',      'Label text below QR code',                                                        'font-size: 6px; white-space: nowrap'],
              ['.stui-rpt-preview-spacer',        'Flex spacer pushing params block to the right',                                   'flex: 1 1 auto'],
              ['.stui-rpt-preview-title-block',   'Absolutely centered title overlay (pointer-events: none)',                        'position: absolute; left/right/top/bottom: 0; flex-direction: column; align/justify: center'],
              ['.stui-rpt-preview-title',         'Report title — brand color via inline style',                                     'font-size: 16px; font-weight: 700; Rajdhani; uppercase; letter-spacing: 1px'],
              ['.stui-rpt-preview-subtitle',      'Group subtitle below title — brand color via inline style',                       'font-size: 13px; font-weight: 700; Rajdhani; uppercase'],
              ['.stui-rpt-preview-record-count',  'Row count below title',                                                           'font-size: 8px; margin-top: 2px'],
              ['.stui-rpt-preview-params',        'Right-side parameter grid — 3 col × 2 row container',                            'flex: 0 0 auto; display: flex; flex-direction: column; gap: 2px'],
              ['.stui-rpt-preview-params-row',    'Single row of param slots',                                                       'display: flex; gap: 6px'],
              ['.stui-rpt-preview-param-slot',    'Individual param cell — width set inline (dynamic)',                              'height: 34px; overflow: hidden; padding-left: 6px'],
              ['.stui-rpt-preview-param-label',   'Param field label (5px — PDF mirror constant)',                                   'font-size: 5px; font-weight: 700; border-bottom: 0.5px'],
              ['.stui-rpt-preview-param-value',   'Param field value (4px — PDF mirror constant)',                                   'font-size: 4px; white-space: nowrap; overflow: hidden'],
              ['.stui-rpt-preview-param-none',    '"(none)" italic placeholder when param is empty',                                 'font-size: 6px; font-style: italic'],
              ['.stui-rpt-preview-param-overflow','Overflow indicator when values exceed slot height',                               'font-size: 4px; font-style: italic'],
              ['.stui-rpt-preview-table-wrap',    'Data table scroll/overflow wrapper',                                              'flex: none; overflow-x: visible'],
              ['.stui-rpt-preview-table',         'PDF-mirror data table (7px header, 8px body)',                                    'border-collapse: collapse; table-layout: fixed; width: 100%'],
              ['.stui-rpt-preview-thead',         'Sticky thead wrapper',                                                            'position: sticky; top: 0; z-index: 10'],
              ['.stui-rpt-preview-th',            'Header cell — width + textAlign set inline (dynamic)',                           'padding: 4px 3px; font-size: 7px; background: var(--color-gray-200); uppercase'],
              ['.stui-rpt-preview-th-line',       'Label line block inside th with reserved min-height',                            'display: block; min-height: 1em'],
              ['.stui-rpt-preview-th-sort',       'Sort-field indicator below column label',                                        'display: block; font-size: 6px; font-weight: 400; color: gray-400'],
              ['.stui-rpt-preview-td',            'Data cell — textAlign set inline (dynamic)',                                     'padding: 3px 4px; font-size: 8px; Helvetica; white-space: nowrap'],
              ['.stui-rpt-preview-td.wrap',       'Wrappable cell variant (Note / Comment columns)',                                'vertical-align: top; white-space: normal'],
              ['.stui-rpt-preview-empty-row',     'Empty state row when report has no results',                                     'padding: var(--spacing-xl); text-align: center; color: gray-400'],
              ['.stui-rpt-preview-footer',        'Page footer — org left, WM logo right',                                         'display: flex; justify-content: space-between; border-top: 2px solid #1a5568; font-size: 9px'],
              ['.stui-rpt-preview-footer-org',    'Left footer block — org name + optional icon',                                   'display: flex; align-items: center; gap: 4px'],
              ['.stui-rpt-preview-footer-wm',     'Right footer block — WM logo + text',                                           'display: flex; align-items: center; gap: 6px'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-rpt-preview-container" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

    </div>
  );
};

/* ── Tab: Usage ────────────────────────────────────────────── */

const AUDIT_COLS = [
  { key: 'pageName',         label: 'Page Name'     },
  { key: 'slug',             label: 'Slug'          },
  { key: 'cssFiles',         label: 'CSS Files'     },
  { key: 'inlineStyleCount', label: 'Inline Styles' },
  { key: 'status',           label: 'Status'        },
];

const auditCellValue = (page, key) => {
  if (key === 'cssFiles')         return page.cssFiles?.length ? page.cssFiles.join(', ') : '(none)';
  if (key === 'inlineStyleCount') return String(page.inlineStyleCount ?? 0);
  if (key === 'status') {
    const okFiles  = !page.cssFiles?.length || page.cssFiles.every(f => f.toLowerCase() === 'stui.css');
    const noInline = !page.inlineStyleCount;
    return (okFiles && noInline) ? 'Standardized' : 'Partial';
  }
  return String(page[key] ?? '');
};

const UsageTab = () => {
  const [auditData, setAuditData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Filter / sort state
  const [openFilter,       setOpenFilter]       = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [pendingSelections,setPendingSelections] = useState({});
  const [filterSearch,     setFilterSearch]     = useState({});
  const [sortConfig,       setSortConfig]       = useState({ key: null, direction: null });
  const [activeFilters,    setActiveFilters]    = useState({});
  const filterRef  = useRef(null);
  const headerRefs = useRef({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setOpenFilter(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const refresh = async () => {
    setLoading(true);
    setMenuOpen(false);
    setOpenFilter(null);
    setActiveFilters({});
    setSortConfig({ key: null, direction: null });
    try {
      const [auditRes, healthRes] = await Promise.all([
        fetch(API_BASE_URL + '/api/devfiles/stui-audit'),
        fetch(API_BASE_URL + '/api/devfiles/css-health'),
      ]);
      setAuditData(await auditRes.json());
      setHealthData(await healthRes.json());
    } catch (e) {
      console.error('Usage tab fetch failed:', e);
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const getColOptions = (key) => {
    if (!auditData) return [];
    return [...new Set(auditData.map(p => auditCellValue(p, key)))].sort((a, b) => {
      if (key === 'inlineStyleCount') return Number(a) - Number(b);
      return a.localeCompare(b);
    });
  };

  const getFilteredOptions = (key) => {
    const opts = getColOptions(key);
    const s = (filterSearch[key] || '').toLowerCase();
    return s ? opts.filter(o => o.toLowerCase().includes(s)) : opts;
  };

  const openFilterDropdown = (key) => {
    const header = headerRefs.current[key];
    if (header) {
      const rect = header.getBoundingClientRect();
      const dH = 320, dW = 240;
      let top  = rect.bottom + 4;
      let left = rect.left;
      if (top  + dH > window.innerHeight) top  = rect.top - dH - 4;
      if (left + dW > window.innerWidth)  left = window.innerWidth - dW - 8;
      setDropdownPosition({ top, left });
    }
    setPendingSelections(prev => ({ ...prev, [key]: activeFilters[key] || [] }));
    setOpenFilter(key);
  };

  const handleCheckboxChange = (key, value) => {
    setPendingSelections(prev => {
      const cur = prev[key] || [];
      return { ...prev, [key]: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value] };
    });
  };

  const isAllSelected = (key) => {
    const opts = getFilteredOptions(key);
    const sel  = pendingSelections[key] || [];
    return opts.length > 0 && opts.every(o => sel.includes(o));
  };

  const handleSelectAll = (key) => {
    const opts = getFilteredOptions(key);
    const sel  = pendingSelections[key] || [];
    const all  = opts.every(o => sel.includes(o));
    setPendingSelections(prev => ({
      ...prev,
      [key]: all ? sel.filter(s => !opts.includes(s)) : [...new Set([...sel, ...opts])]
    }));
  };

  const applyFilter = (key) => {
    const sel = pendingSelections[key] || [];
    const all = getColOptions(key);
    const isAll = all.length > 0 && all.every(o => sel.includes(o));
    setActiveFilters(prev => ({ ...prev, [key]: isAll ? [] : sel }));
    setOpenFilter(null);
  };

  const handleSort = (key, dir) => {
    setSortConfig({ key, direction: dir });
    setOpenFilter(null);
  };

  const hasActiveFilter = (key) => activeFilters[key]?.length > 0;
  const isSorted        = (key) => sortConfig.key === key;

  const processedRows = auditData ? (() => {
    let rows = auditData.filter(page =>
      Object.entries(activeFilters).every(([k, vals]) =>
        !vals?.length || vals.includes(auditCellValue(page, k))
      )
    );
    if (sortConfig.key) {
      const k = sortConfig.key;
      rows = [...rows].sort((a, b) => {
        const av = auditCellValue(a, k), bv = auditCellValue(b, k);
        const cmp = k === 'inlineStyleCount' ? Number(av) - Number(bv) : av.localeCompare(bv);
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }
    return rows;
  })() : [];

  return (
    <div className="stui-preview-zone stui-tab-body">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <div className="stui-detail-section-header" style={{ flex: 1, margin: 0 }}>CSS Usage Audit</div>
        <div className="stui-action-menu">
          <button className="stui-action-menu-btn" onClick={() => setMenuOpen(o => !o)} title="Actions"><GripVertical size={16} /></button>
          {menuOpen && (
            <div className="stui-action-menu-dropdown">
              <button className="stui-action-menu-item" onClick={refresh}><RefreshCw size={16} /> Refresh</button>
            </div>
          )}
        </div>
      </div>
      <p style={{ margin: '0 0 24px', fontSize: 13, color: 'var(--color-gray-500)' }}>Page-level CSS file usage and inline style counts across DAWN</p>

      {loading && <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--color-gray-400)', fontSize: 13 }}>Scanning pages…</div>}

      {!loading && auditData && (
        <>
          {/* Grid 1: Page Audit */}
          <div style={{ marginBottom: 32 }}>
            <div className="stui-detail-section-header">
              Page Audit — {processedRows.length}{processedRows.length !== auditData.length ? ` of ${auditData.length}` : ''} pages
            </div>
            <div className="stui-data-grid-container">
              <div className="stui-data-grid-scroll-area">
                <table className="stui-data-grid stui-no-column-resize">
                  <thead>
                    <tr>
                      {AUDIT_COLS.map(col => {
                        const active = hasActiveFilter(col.key) || isSorted(col.key);
                        return (
                          <th
                            key={col.key}
                            ref={el => { headerRefs.current[col.key] = el; }}
                            style={{ cursor: 'pointer' }}
                            onClick={() => openFilterDropdown(col.key)}
                          >
                            <span>{col.label}</span>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                              style={{ marginLeft: 4, opacity: active ? 1 : 0.3, color: active ? 'var(--color-teal-600)' : 'inherit' }}>
                              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                            </svg>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {processedRows.map((page, i) => (
                      <tr key={i}>
                        <td>{page.pageName}</td>
                        <td>/{page.slug}</td>
                        <td>{page.cssFiles?.length ? page.cssFiles.join(', ') : 'none'}</td>
                        <td>{page.inlineStyleCount}</td>
                        <td>{((!page.cssFiles?.length || page.cssFiles.every(f => f.toLowerCase() === 'stui.css')) && !page.inlineStyleCount) ? 'Standardized' : 'Partial'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Filter dropdown — portal to body so theme CSS vars don't bleed in */}
          {openFilter && (
            <div
              ref={filterRef}
              className="stui-filter-dropdown"
              style={{ position: 'fixed', top: dropdownPosition.top, left: dropdownPosition.left, zIndex: 9999 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="stui-filter-sort-section">
                <button className={`stui-filter-sort-btn${sortConfig.key === openFilter && sortConfig.direction === 'asc' ? ' active' : ''}`} onClick={() => handleSort(openFilter, 'asc')}>
                  <span>{openFilter === 'inlineStyleCount' ? '↑ Low to High' : '↑ Sort A to Z'}</span>
                </button>
                <button className={`stui-filter-sort-btn${sortConfig.key === openFilter && sortConfig.direction === 'desc' ? ' active' : ''}`} onClick={() => handleSort(openFilter, 'desc')}>
                  <span>{openFilter === 'inlineStyleCount' ? '↓ High to Low' : '↓ Sort Z to A'}</span>
                </button>
              </div>
              <div className="stui-filter-divider" />
              <div className="stui-filter-search">
                <input
                  type="text"
                  placeholder="Search"
                  value={filterSearch[openFilter] || ''}
                  onChange={e => setFilterSearch(prev => ({ ...prev, [openFilter]: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="stui-filter-options">
                <label className="stui-filter-option stui-filter-option-select-all">
                  <input type="checkbox" checked={isAllSelected(openFilter)} onChange={() => handleSelectAll(openFilter)} />
                  <span>(Select All)</span>
                </label>
                {getFilteredOptions(openFilter).map(opt => (
                  <label key={opt} className="stui-filter-option">
                    <input type="checkbox" checked={(pendingSelections[openFilter] || []).includes(opt)} onChange={() => handleCheckboxChange(openFilter, opt)} />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
              <div className="stui-filter-footer">
                <button onClick={() => applyFilter(openFilter)} className="stui-filter-ok-btn">OK</button>
                <button onClick={() => setOpenFilter(null)} className="stui-filter-cancel-btn">Cancel</button>
              </div>
            </div>
          )}


          {/* Grid 2: CSS Health Summary */}
          {healthData && (
            <div style={{ marginBottom: 32 }}>
              <div className="stui-detail-section-header">CSS Health Summary</div>
              <div className="stui-data-grid-container">
                <div className="stui-data-grid-scroll-area">
                  <table className="stui-data-grid stui-no-column-resize stui-w-full">
                    <thead>
                      <tr>
                        <th>STUI.css Classes</th>
                        <th>STUI Used</th>
                        <th>App.css Classes</th>
                        <th>App.css Used</th>
                        <th>Total Inline Styles</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{healthData.stuiTotal}</td>
                        <td>{healthData.stuiUsed} <span className="stui-label-xs-muted">({Math.round(healthData.stuiUsed / healthData.stuiTotal * 100)}%)</span></td>
                        <td>{healthData.appTotal}</td>
                        <td>{healthData.appUsed} <span className="stui-label-xs-muted">({Math.round(healthData.appUsed / healthData.appTotal * 100)}%)</span></td>
                        <td style={{ color: 'var(--color-error)' }}>{healthData.totalInlineStyles.toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Grid 3: Unused STUI Classes */}
          {healthData?.unusedStuiClasses?.length > 0 && (
            <div>
              <div className="stui-detail-section-header">Unused STUI.css Classes ({healthData.unusedStuiClasses.length})</div>
              <div className="stui-data-grid-container">
                <div className="stui-data-grid-scroll-area">
                  <table className="stui-data-grid stui-no-column-resize stui-w-full">
                    <thead>
                      <tr><th>Class Name</th></tr>
                    </thead>
                    <tbody>
                      {healthData.unusedStuiClasses.map((cls, i) => (
                        <tr key={i}>
                          <td style={{ fontFamily: 'monospace', fontSize: 12 }}>.{cls}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ── Tab: Standard View ────────────────────────────────────── */

const STANDARD_VIEW_SECTIONS = [
  { id: 'detail-section-header', label: 'Detail Section Header' },
  { id: 'list-layout',           label: 'List View Layout'      },
  { id: 'form-fields',           label: 'Form Fields'           },
  { id: 'searchselect',          label: 'SearchSelect'          },
  { id: 'related-tables',        label: 'Related Tables'        },
  { id: 'datasheet-extras',      label: 'Datasheet Extras'      },
  { id: 'pagination',            label: 'Pagination'            },
  { id: 'fk-select',             label: 'FK Select Modal'       },
  { id: 'composer-select',       label: 'Composer Select'       },
  { id: 'module-header-crud',    label: 'Module Header CRUD'    },
];

const DetailSectionHeaderConfigurator = () => {
  const [title, setTitle] = useState('General Information');
  const output = `stui-detail-section-header · label "${title || 'Section Title'}"`;
  return (
    <div className="stui-col-12">
      <div className="stui-row-10">
        <span className="stui-cfg-key">Label</span>
        <input value={title} onChange={e => setTitle(e.target.value)} className="stui-cfg-input" style={{ width: 220 }} />
      </div>
      <div className="stui-example-cell" style={{ justifyContent: 'flex-start', padding: '14px 18px' }}>
        <div className="stui-detail-section-header" style={{ margin: 0, width: '100%' }}>{title || 'Section Title'}</div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

// eslint-disable-next-line no-unused-vars
const StandardViewTab = ({ usageMap, pageSlugMap, navigate, onChipClick }) => {
  const [activeSection, setActiveSection] = useState('detail-section-header');
  const [listSelected, setListSelected] = useState(1);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [ssOpen, setSsOpen] = useState(false);
  const [ssValue, setSsValue] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [relatedTab, setRelatedTab] = useState(0);
  const [relatedCollapsed, setRelatedCollapsed] = useState(false);

  const listItems = [
    { id: 1, primary: 'AI-100', secondary: 'Analog Input' },
    { id: 2, primary: 'DI-201', secondary: 'Digital Input' },
    { id: 3, primary: 'AO-302', secondary: 'Analog Output' },
    { id: 4, primary: 'DO-410', secondary: 'Digital Output' },
  ];
  const ssOptions = ['Panel A', 'Panel B', 'Panel C', 'MCC-001', 'MCC-002'];
  const composerModules = ['Building', 'Drawing', 'Tag', 'Panel', 'Source'];

  return (
    <div className="stui-preview-zone stui-tab-body">
      <div className="stui-mb-20">
        <select className="stui-select" value={activeSection} onChange={e => setActiveSection(e.target.value)} style={{ minWidth: 240 }}>
          {STANDARD_VIEW_SECTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {/* ── Detail Section Header ─────────────────────────────── */}
      {activeSection === 'detail-section-header' && (
        <Section title="Detail Section Header" description="Teal bar that labels groups of fields in the detail panel. Spans full row width, sits above each form field group." cssFile={CSS_LOCATIONS.detailSectionHeader.file} cssLine={CSS_LOCATIONS.detailSectionHeader.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ maxWidth: 520 }}>
            <div className="stui-detail-section-header">General Information</div>
            <div className="stui-detail-section-header">Tag Information</div>
            <div className="stui-detail-section-header">Assignment Details</div>
            <CSSRef file="STUI.css" selector=".stui-detail-section-header" />
          </div>
          <Subsection label="Reference" />
          <RefTable
            headers={['Property', 'Value', 'Notes']}
            rows={[
              ['background-color', 'var(--color-primary)', 'WM Teal — #1a5568'],
              ['color', 'white', ''],
              ['font-family', 'var(--font-secondary)', 'Rajdhani / Roboto Condensed'],
              ['font-size', 'var(--text-base)', '1rem'],
              ['font-weight', '500', ''],
              ['padding', 'var(--spacing-sm) var(--spacing-lg)', '0.5rem 1rem'],
              ['border-radius', 'var(--radius-sm)', ''],
              ['margin', 'var(--spacing-md) 0', 'top + bottom — :first-child resets top to 0'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-detail-section-header" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <DetailSectionHeaderConfigurator />
        </Section>
      )}

      {/* ── List View Layout ──────────────────────────────────── */}
      {activeSection === 'list-layout' && (
        <Section title="List View Layout" description="Two-panel split: resizable list panel on the left, detail panel on the right. Collapses to icon-only strip. Detail grid uses 24-column span system." cssFile={CSS_LOCATIONS.listLayout.file} cssLine={CSS_LOCATIONS.listLayout.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ border: '1px solid var(--color-border)', borderRadius: 4, height: 260, display: 'flex', overflow: 'hidden' }}>
            <div className={`stui-list-panel${listCollapsed ? ' collapsed' : ''}`} style={{ width: listCollapsed ? 32 : 180, minWidth: listCollapsed ? 32 : 140, transition: 'width 0.2s' }}>
              <div className="stui-list-panel-header">
                <button className="stui-list-panel-collapse-btn" onClick={() => setListCollapsed(c => !c)} title={listCollapsed ? 'Expand' : 'Collapse'}>
                  {listCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                </button>
              </div>
              {!listCollapsed && (
                <div className="stui-list-scroll">
                  {listItems.map(item => (
                    <button key={item.id} className={`stui-list-item ${listSelected === item.id ? 'selected' : ''}`} onClick={() => setListSelected(item.id)}>
                      <span className="stui-list-item-primary">{item.primary}</span>
                      <span className="stui-list-item-secondary">{item.secondary}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="stui-detail-panel" style={{ flex: 1 }}>
              <div className="stui-detail-content" style={{ height: '100%' }}>
                <div className="stui-detail-grid" style={{ padding: 8 }}>
                  <div className="col-span-24">
                    <div className="stui-detail-section-header stui-m-0">General Information</div>
                  </div>
                  <div className="col-span-8 stui-field-group">
                    <label>Tag Number</label>
                    <input className="stui-field-input" value={listItems.find(i => i.id === listSelected)?.primary || ''} readOnly />
                  </div>
                  <div className="col-span-8 stui-field-group">
                    <label>Type</label>
                    <input className="stui-field-input" value={listItems.find(i => i.id === listSelected)?.secondary || ''} readOnly />
                  </div>
                  <div className="col-span-8 stui-field-group">
                    <label>Status</label>
                    <input className="stui-field-input" value="Active" readOnly />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Subsection label="Class Reference" />
          <RefTable
            headers={['Class', 'Element', 'Notes']}
            rows={[
              ['.stui-list-view', 'flex container', 'wraps both panels; add .collapsed when list panel is collapsed'],
              ['.stui-list-panel', 'left panel', 'resizable; add .collapsed for icon-only strip mode'],
              ['.stui-list-panel-header', 'panel header', 'contains collapse toggle button'],
              ['.stui-list-panel-collapse-btn', 'toggle button', 'ChevronLeft/Right icon; toggles collapsed state'],
              ['.stui-list-scroll', 'scrollable list area', 'flex:1, overflow-y:auto'],
              ['.stui-list-item', '<button>', 'full-width list row; add .selected for active row'],
              ['.stui-list-item-primary', 'primary text', 'bold, truncated on overflow'],
              ['.stui-list-item-secondary', 'secondary text', 'small muted label below primary'],
              ['.stui-detail-panel', 'right panel', 'flex:1; add .resizing during column drag'],
              ['.stui-detail-content', 'scrollable detail area', 'padding 16px'],
              ['.stui-detail-grid', 'CSS grid', '24-column grid; children use .col-span-N'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-list-panel" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ListLayoutConfigurator />
        </Section>
      )}

      {/* ── Form Fields ───────────────────────────────────────── */}
      {activeSection === 'form-fields' && (
        <Section title="Form Fields" description="Field group container (.stui-field-group) wraps a label + input pair. Label positions: top (default), left, right, bottom. Input states: normal, dirty, fk, derived, readonly, display." cssFile={CSS_LOCATIONS.formFields.file} cssLine={CSS_LOCATIONS.formFields.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Input States" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px 20px', maxWidth: 640 }}>
            <div className="stui-field-group">
              <label>Normal</label>
              <input className="stui-field-input" defaultValue="Sample value" />
            </div>
            <div className="stui-field-group">
              <label>Dirty (unsaved)</label>
              <input className="stui-field-input dirty" defaultValue="Modified value" />
            </div>
            <div className="stui-field-group">
              <label>FK Field</label>
              <div className="stui-field-input fk" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                <span>Panel A</span>
                <Link2 size={12} className="stui-c-muted" />
              </div>
            </div>
            <div className="stui-field-group">
              <label>Derived (read-only computed)</label>
              <input className="stui-field-input derived" value="AUTO-2024" readOnly />
            </div>
            <div className="stui-field-group">
              <label>Display Only</label>
              <span className="stui-field-display">No edit, no border</span>
            </div>
            <div className="stui-field-group">
              <label>Readonly (non-editable column)</label>
              <div className="stui-field-readonly">System managed</div>
            </div>
          </div>
          <Subsection label="Label Positions" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 420 }}>
            <div className="stui-field-group">
              <label>Label Top (default)</label>
              <input className="stui-field-input" defaultValue="Value" />
            </div>
            <div className="stui-field-group stui-label-left">
              <label>Label Left</label>
              <input className="stui-field-input" defaultValue="Value" />
            </div>
            <div className="stui-field-group stui-label-right">
              <label>Label Right</label>
              <input className="stui-field-input" defaultValue="Value" />
            </div>
            <div className="stui-field-group stui-label-bottom">
              <label>Label Bottom</label>
              <input className="stui-field-input" defaultValue="Value" />
            </div>
          </div>
          <Subsection label="Class Reference" />
          <RefTable
            headers={['Class', 'Element', 'Notes']}
            rows={[
              ['.stui-field-group', 'wrapper div', 'flex column; label + input pair'],
              ['.stui-field-group.stui-label-left', 'wrapper', 'row layout; label 120px fixed width, right-aligned'],
              ['.stui-field-group.stui-label-right', 'wrapper', 'row-reverse layout'],
              ['.stui-field-group.stui-label-bottom', 'wrapper', 'column-reverse — label below input'],
              ['.stui-field-input', '<input>/<select>/<textarea>', 'standard text input styling'],
              ['.stui-field-input.dirty', '', 'amber border + background — unsaved change indicator'],
              ['.stui-field-input.fk', '', 'dashed border — indicates FK lookup field'],
              ['.stui-field-input.derived', '', 'italic, gray bg — computed/auto-filled field'],
              ['.stui-field-display', '<span>', 'no border, no bg — inline display-only value'],
              ['.stui-field-readonly', 'div', 'gray bg, solid border — system-managed, non-editable'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-field-group" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <FormFieldsConfigurator />
        </Section>
      )}

      {/* ── SearchSelect ──────────────────────────────────────── */}
      {activeSection === 'searchselect' && (
        <Section title="SearchSelect" description="Custom FK lookup dropdown with search filtering. Renders as a trigger div + portaled dropdown. Supports open, disabled, and dirty states." cssFile={CSS_LOCATIONS.searchSelect.file} cssLine={CSS_LOCATIONS.searchSelect.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Trigger States" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 220px)', gap: '12px 24px' }}>
            <div>
              <div className="stui-searchselect-trigger" onClick={() => setSsOpen(o => !o)}>
                <span className={`stui-searchselect-value ${ssValue ? '' : 'placeholder'}`}>{ssValue || '-- Select --'}</span>
                <ChevronDown size={14} className="stui-searchselect-chevron" />
              </div>
              {ssOpen && (
                <div className="stui-searchselect-dropdown" style={{ position: 'relative', zIndex: 1, marginTop: 2 }}>
                  <div className="stui-searchselect-search">
                    <Search size={13} className="stui-searchselect-search-icon" />
                    <input className="stui-searchselect-search-input" placeholder="Search…" />
                  </div>
                  <div className="stui-searchselect-options">
                    {ssOptions.map(opt => (
                      <div key={opt} className={`stui-searchselect-option ${ssValue === opt ? 'selected' : ''}`} onClick={() => { setSsValue(opt); setSsOpen(false); }}>
                        <span className="stui-searchselect-option-check">{ssValue === opt && <Check size={12} />}</span>
                        <span className="stui-searchselect-option-text">{opt}</span>
                      </div>
                    ))}
                    <div className="stui-searchselect-empty" style={{ display: 'none' }}>No matches found</div>
                  </div>
                </div>
              )}
              <CSSRef file="STUI.css" selector=".stui-searchselect-trigger" />
            </div>
            <div className="stui-col-8">
              <div className="stui-searchselect-trigger open">
                <span className="stui-searchselect-value">Panel A</span>
                <ChevronDown size={14} className="stui-searchselect-chevron" />
              </div>
              <CSSRef file="STUI.css" selector=".stui-searchselect-trigger.open" />
              <div className="stui-searchselect-trigger disabled">
                <span className="stui-searchselect-value placeholder">-- Select --</span>
                <ChevronDown size={14} className="stui-searchselect-chevron" />
              </div>
              <CSSRef file="STUI.css" selector=".stui-searchselect-trigger.disabled" />
              <div className="stui-searchselect-trigger dirty">
                <span className="stui-searchselect-value">Panel B</span>
                <ChevronDown size={14} className="stui-searchselect-chevron" />
              </div>
              <CSSRef file="STUI.css" selector=".stui-searchselect-trigger.dirty" />
            </div>
          </div>
          <Subsection label="Class Reference" />
          <RefTable
            headers={['Class', 'Notes']}
            rows={[
              ['.stui-searchselect-trigger', 'clickable trigger div — flex row, styled like input'],
              ['.stui-searchselect-trigger.open', 'primary border + shadow when dropdown open'],
              ['.stui-searchselect-trigger.disabled', 'gray bg, not-allowed cursor'],
              ['.stui-searchselect-trigger.dirty', 'amber border + bg — unsaved change'],
              ['.stui-searchselect-value.placeholder', 'gray text when nothing selected'],
              ['.stui-searchselect-chevron', 'ChevronDown icon; rotates 180° when .open'],
              ['.stui-searchselect-dropdown', 'absolute-positioned panel (portaled to body)'],
              ['.stui-searchselect-search', 'search row at top of dropdown'],
              ['.stui-searchselect-options', 'scrollable list; max-height 220px'],
              ['.stui-searchselect-option', 'row item; .selected adds primary bg'],
              ['.stui-searchselect-option-check', '14px check icon placeholder — shows Check when .selected'],
              ['.stui-searchselect-clear', 'clear-selection row (special option style)'],
              ['.stui-searchselect-empty', 'no-results message'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-searchselect-trigger" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <SearchSelectConfigurator />
        </Section>
      )}

      {/* ── Related Tables ────────────────────────────────────── */}
      {activeSection === 'related-tables' && (
        <Section title="Related Tables" description="Collapsible panel below the detail form showing FK-related records. Has a resizable header, tab bar with counts, and a content area for the data grid." cssFile={CSS_LOCATIONS.relatedTables.file} cssLine={CSS_LOCATIONS.relatedTables.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ border: '1px solid var(--color-border)', borderRadius: 4, overflow: 'hidden', maxWidth: 640 }}>
            <div className={`stui-related-section ${relatedCollapsed ? 'collapsed' : ''}`} style={{ height: relatedCollapsed ? 'auto' : 180 }}>
              <div className={`stui-related-header ${!relatedCollapsed ? 'resizable' : ''}`}>
                {!relatedCollapsed && (
                  <div className="stui-resize-grip" style={{ position: 'static', width: 'auto', height: 'auto', display: 'flex', alignItems: 'center' }}>
                    <GripHorizontal size={13} className="stui-c-muted" />
                  </div>
                )}
                <button className="stui-related-collapse-btn" onClick={() => setRelatedCollapsed(c => !c)} title={relatedCollapsed ? 'Expand' : 'Collapse'}>
                  {relatedCollapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                <div className="stui-related-tabs">
                  {['Drawings', 'Tags', 'Equipment'].map((tab, idx) => (
                    <button key={tab} className={`stui-related-tab ${relatedTab === idx ? 'active' : ''}`} onClick={() => setRelatedTab(idx)}>
                      {tab}
                      <span className="stui-related-tab-count">{[12, 3, 7][idx]}</span>
                    </button>
                  ))}
                </div>
              </div>
              {!relatedCollapsed && (
                <div className="stui-related-content stui-related-body">
                  {relatedTab === 0 && 'Showing 12 drawings associated with this record…'}
                  {relatedTab === 1 && 'Showing 3 tags associated with this record…'}
                  {relatedTab === 2 && 'Showing 7 equipment items associated with this record…'}
                </div>
              )}
            </div>
          </div>
          <Subsection label="Class Reference" />
          <RefTable
            headers={['Class', 'Notes']}
            rows={[
              ['.stui-related-section', 'outer container; add .collapsed when folded'],
              ['.stui-related-header', 'header bar — contains resize grip, collapse btn, tabs; add .resizable for row-resize cursor'],
              ['.stui-resize-grip', 'drag handle at top of header for vertical resize'],
              ['.stui-related-collapse-btn', 'icon button to toggle collapsed state'],
              ['.stui-related-tabs', 'flex tab bar'],
              ['.stui-related-tab', 'tab button; add .active for selected, .loading while data loads'],
              ['.stui-related-tab-count', 'small pill showing record count — teal when tab is active'],
              ['.stui-related-content', 'flex content area below header; contains the data grid'],
              ['.stui-related-loading', 'centered spinner row shown while data loads'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-related-section" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <RelatedTablesConfigurator />
        </Section>
      )}

      {/* ── Datasheet Extras ──────────────────────────────────── */}
      {activeSection === 'datasheet-extras' && (
        <Section title="Datasheet Extras" description="Nested selectors inside .stui-data-grid for edit mode, delete mode, editable cells, boolean cells, and the column resize handle." cssFile={CSS_LOCATIONS.datasheetExtras.file} cssLine={CSS_LOCATIONS.datasheetExtras.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Delete Mode — selected rows" />
          <table className="stui-data-grid delete-mode stui-full-mb">
            <thead><tr><th style={{ width: 40 }}></th><th>Tag</th><th>Description</th><th>Panel</th></tr></thead>
            <tbody>
              <tr><td className="delete-checkbox-col"><input type="checkbox" /></td><td>AI-100</td><td>Flow Transmitter</td><td>Panel A</td></tr>
              <tr className="selected-for-delete"><td className="delete-checkbox-col"><input type="checkbox" checked readOnly /></td><td>DI-201</td><td>Level Switch</td><td>Panel B</td></tr>
              <tr className="selected-for-delete"><td className="delete-checkbox-col"><input type="checkbox" checked readOnly /></td><td>AO-302</td><td>Control Valve</td><td>Panel A</td></tr>
              <tr><td className="delete-checkbox-col"><input type="checkbox" /></td><td>DO-410</td><td>Motor Starter</td><td>Panel C</td></tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-data-grid tbody tr.selected-for-delete" />
          <Subsection label="Edit Mode — editable cells" />
          <table className="stui-data-grid edit-mode stui-full-mb">
            <thead><tr><th>Tag</th><th>Description</th><th>Panel</th><th>Boolean</th></tr></thead>
            <tbody>
              <tr>
                <td className="editable-cell"><input defaultValue="AI-100" className="stui-editable-input" /></td>
                <td className="editable-cell modified"><input defaultValue="Updated description" className="stui-editable-input" /></td>
                <td className="readonly-cell">Panel A</td>
                <td className="cell-boolean"><input type="checkbox" checked readOnly /></td>
              </tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-data-grid td.editable-cell.modified" />
          <Subsection label="Column Resize Handle" />
          <table className="stui-data-grid" style={{ width: 360 }}>
            <thead>
              <tr>
                <th style={{ position: 'relative' }}>Tag Number <div className="stui-column-resize-handle" /></th>
                <th style={{ position: 'relative' }}>Description <div className="stui-column-resize-handle" /></th>
              </tr>
            </thead>
            <tbody><tr><td>AI-100</td><td>Flow Transmitter</td></tr></tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-data-grid th .stui-column-resize-handle" />
          <Subsection label="Class Reference" />
          <RefTable
            headers={['Selector', 'Notes']}
            rows={[
              ['.stui-data-grid.delete-mode tbody tr:hover', 'pointer cursor on all rows in delete mode'],
              ['.stui-data-grid tbody tr.selected-for-delete', 'red bg tint + red text — row marked for deletion'],
              ['.stui-data-grid td.editable-cell', 'zero-padding cell; contains raw input'],
              ['.stui-data-grid td.editable-cell.modified', 'amber bg — cell value differs from saved'],
              ['.stui-data-grid td.cell-boolean', 'centered checkbox display'],
              ['.stui-data-grid td.readonly-cell', 'gray bg — non-editable in edit mode'],
              ['.stui-data-grid th .stui-column-resize-handle', '4px drag strip at right edge of header cell'],
            ]}
          />
          <Subsection label="Configurator" />
          <DatasheetExtrasConfigurator />
        </Section>
      )}

      {/* ── Pagination ────────────────────────────────────────── */}
      {activeSection === 'pagination' && (
        <Section title="Pagination" description="Page navigation bar docked at the bottom of the datasheet view. Flex row with prev/next controls on the left and record-range info on the right." cssFile={CSS_LOCATIONS.pagination.file} cssLine={CSS_LOCATIONS.pagination.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ border: '1px solid var(--color-border)', borderRadius: 4, overflow: 'hidden', maxWidth: 520 }}>
            <div className="stui-pagination">
              <div className="stui-pagination-controls">
                <button className="stui-pagination-btn" disabled><ChevronLeft size={14} /></button>
                <button className="stui-pagination-btn active">1</button>
                <button className="stui-pagination-btn">2</button>
                <button className="stui-pagination-btn">3</button>
                <button className="stui-pagination-btn"><ChevronRight size={14} /></button>
              </div>
              <span className="stui-pagination-info">1–50 of 143</span>
            </div>
          </div>
          <Subsection label="Class Reference" />
          <RefTable
            headers={['Class', 'Notes']}
            rows={[
              ['.stui-pagination', 'flex row container; border-top; space-between'],
              ['.stui-pagination-controls', 'flex row of buttons'],
              ['.stui-pagination-btn', 'page/nav button; add .active for current page (teal fill)'],
              ['.stui-pagination-btn:disabled', '40% opacity, not-allowed cursor'],
              ['.stui-pagination-info', 'small gray text: "1–50 of 200"'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-pagination" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <PaginationConfigurator />
        </Section>
      )}

      {/* ── FK Select Modal ───────────────────────────────────── */}
      {activeSection === 'fk-select' && (
        <Section title="FK Select Modal" description="Full-screen overlay modal for browsing and selecting a foreign key record. Supports stacking up to 5 levels deep." cssFile={CSS_LOCATIONS.fkSelect.file} cssLine={CSS_LOCATIONS.fkSelect.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview (inline — not overlaid)" />
          <div style={{ border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden', maxWidth: 560, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
            <div className="stui-fk-select-header">
              <div className="stui-row-6">
                <span className="stui-label-xs-muted">Building</span>
                <span className="stui-label-xs-muted"> → </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>Drawing</span>
              </div>
              <div className="stui-flex-4">
                <button className="stui-crud-btn" title="Open full page"><Link2 size={14} /></button>
                <button className="stui-crud-btn" title="Close"><X size={16} /></button>
              </div>
            </div>
            <div className="stui-fk-select-body" style={{ padding: 16, minHeight: 120 }}>
              <div className="stui-detail-grid">
                <div className="col-span-8 stui-field-group"><label>Drawing No.</label><input className="stui-field-input" value="DWG-001" readOnly /></div>
                <div className="col-span-8 stui-field-group"><label>Revision</label><input className="stui-field-input" value="B" readOnly /></div>
                <div className="col-span-8 stui-field-group"><label>Discipline</label><input className="stui-field-input" value="Electrical" readOnly /></div>
              </div>
            </div>
          </div>
          <Subsection label="Class Reference" />
          <RefTable
            headers={['Class', 'Notes']}
            rows={[
              ['.stui-fk-select-overlay', 'fixed full-screen dimmed backdrop; flex centering'],
              ['.stui-fk-select', 'modal card — 680px wide, max 80vh height; stacked via translate()'],
              ['.stui-fk-select-header', 'flex row: breadcrumb on left, action buttons on right'],
              ['.stui-fk-select-body', 'flex column content area — holds stui-detail-grid + stui-related-section'],
            ]}
          />
          <Subsection label="Stacking" />
          <div style={{ fontSize: 12, color: 'var(--color-gray-500)', lineHeight: 1.6 }}>
            Each FK click pushes a new item onto the stack. The modal offsets by 15px per level via <code>transform: translate(Npx, Npx)</code>. Back button pops the stack; clicking the overlay calls back/close.
          </div>
          <Subsection label="Configurator" />
          <FkSelectConfigurator />
        </Section>
      )}

      {/* ── Composer Select ───────────────────────────────────── */}
      {activeSection === 'composer-select' && (
        <Section title="Composer Select" description="Config-page dropdown used in Standard View Composer and Data Entry Composer to pick which table or module to configure. Based on the lvc-custom-dropdown pattern." cssFile={CSS_LOCATIONS.composerSelect.file} cssLine={CSS_LOCATIONS.composerSelect.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ padding: '16px 0', position: 'relative' }}>
            <div className="stui-composer-select">
              <label className="stui-composer-select-label">Module</label>
              <div className={`stui-composer-select-trigger ${composerOpen ? 'open' : ''}`} onClick={() => setComposerOpen(o => !o)}>
                <span>Building</span>
                <ChevronDown size={14} />
              </div>
              {composerOpen && (
                <div className="stui-composer-select-panel" style={{ position: 'relative', marginTop: 4, top: 'auto', left: 'auto' }}>
                  <input className="stui-composer-select-search" placeholder="Search modules…" autoFocus />
                  <div className="stui-composer-select-options">
                    {composerModules.map((m, i) => (
                      <div key={m} className={`stui-composer-select-option ${i === 0 ? 'selected' : ''}`} onClick={() => setComposerOpen(false)}>
                        <span className="stui-composer-select-option-name">{m}</span>
                        {i < 3 && <span className="stui-composer-select-option-icons" title="Has config">●</span>}
                      </div>
                    ))}
                  </div>
                  <div className="stui-composer-select-legend">
                    <span>● Has config</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <Subsection label="Class Reference" />
          <RefTable
            headers={['Class', 'Notes']}
            rows={[
              ['.stui-composer-select', 'flex row wrapper — label + trigger; position:relative for panel anchor'],
              ['.stui-composer-select-label', 'uppercase gray label to the left of the trigger'],
              ['.stui-composer-select-trigger', 'button-style trigger; add .open for primary border'],
              ['.stui-composer-select-panel', 'absolute dropdown panel; appears below trigger'],
              ['.stui-composer-select-search', 'full-width search input at top of panel'],
              ['.stui-composer-select-options', 'scrollable option list; max-height 300px'],
              ['.stui-composer-select-option', 'option row; add .selected for teal text + bg'],
              ['.stui-composer-select-option-name', 'flex:1 truncated option label'],
              ['.stui-composer-select-option-icons', 'small icon cluster at right edge of option'],
              ['.stui-composer-select-legend', 'gray footnote bar at bottom of panel'],
            ]}
          />
          <Subsection label="Used By" />
          <div className="stui-text-sm-muted">
            ListViewConfigPage.js (Standard View Composer), DataEntryComposerPage.js (Data Entry Composer)
          </div>
          <Subsection label="Configurator" />
          <ComposerSelectConfigurator />
        </Section>
      )}

      {/* ── Module Header CRUD ────────────────────────────────── */}
      {activeSection === 'module-header-crud' && (
        <Section title="Module Header CRUD" description="Flex containers for the CRUD and datasheet edit/save buttons inside .stui-module-header-actions. Two variants: list view CRUD and datasheet controls." cssFile={CSS_LOCATIONS.moduleHeaderCrud.file} cssLine={CSS_LOCATIONS.moduleHeaderCrud.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="List View CRUD (stui-module-header-crud)" />
          <div className="stui-cfg-demo-bar">
            <div className="stui-module-header-crud">
              <button className="stui-crud-btn" title="Add Record"><Plus size={18} /></button>
              <button className="stui-crud-btn" title="Edit Record"><Pencil size={18} /></button>
              <button className="stui-crud-btn disabled" title="Save Changes" disabled><Save size={18} /></button>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-module-header-crud" />
          <Subsection label="Editing State (delete + cancel + save)" />
          <div className="stui-cfg-demo-bar">
            <div className="stui-module-header-crud">
              <button className="stui-crud-btn stui-delete-btn" title="Delete Record"><Trash2 size={18} /></button>
              <button className="stui-crud-btn" title="Cancel"><X size={18} /></button>
              <button className="stui-crud-btn" title="Save Changes"><Save size={18} /></button>
            </div>
          </div>
          <Subsection label="Datasheet Controls (stui-module-header-datasheet-controls)" />
          <div className="stui-cfg-demo-bar">
            <div className="stui-module-header-datasheet-controls">
              <div className="stui-module-header-crud">
                <button className="stui-crud-btn" title="Enable Editing"><Pencil size={18} /></button>
                <button className="stui-crud-btn disabled" title="Save Changes" disabled><Save size={18} /></button>
              </div>
            </div>
          </div>
          <Subsection label="Class Reference" />
          <RefTable
            headers={['Class', 'Notes']}
            rows={[
              ['.stui-module-header-crud', 'flex row of CRUD icon buttons; gap 2px — used inside both list and datasheet views'],
              ['.stui-module-header-datasheet-controls', 'wrapper for datasheet edit+save group; gap 4px'],
              ['.stui-crud-btn', 'see Module Header tab — 30×30 icon button'],
              ['.stui-crud-btn.stui-delete-btn', 'red tint on hover — destructive action'],
              ['.stui-crud-btn.disabled', 'same as disabled attr — 0.4 opacity, not-allowed cursor'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-module-header-crud" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ModuleHeaderCrudConfigurator />
        </Section>
      )}

    </div>
  );
};

/* ── Tab: Base & Reset ─────────────────────────────────────── */

const BASE_RESET_SECTIONS = [
  { id: 'body',           label: 'Body'           },
  { id: 'box-sizing',     label: 'Box Sizing'     },
  { id: 'form-controls',  label: 'Form Controls'  },
];

const BaseResetTab = ({ onChipClick }) => {
  const [activeSection, setActiveSection] = useState('body');
  return (
    <div className="stui-preview-zone stui-tab-body">

      <div className="stui-mb-20">
        <select className="stui-select" value={activeSection} onChange={e => setActiveSection(e.target.value)} style={{ minWidth: 220 }}>
          {BASE_RESET_SECTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {activeSection === 'box-sizing' && (
        <Section title="Box Sizing" description="Universal rule that makes width and height include padding and border. Without this, padding adds to the declared width — breaking every layout." cssFile={CSS_LOCATIONS.baseReset.file} cssLine={CSS_LOCATIONS.baseReset.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div className="stui-col-6">
              <div style={{ fontSize: 11, color: 'var(--color-primary)', fontWeight: 600 }}>border-box (STUI) ✓</div>
              <div style={{ width: 200, padding: 16, border: '2px solid var(--color-primary)', boxSizing: 'border-box', background: 'var(--color-teal-50)', fontSize: 11, color: 'var(--color-gray-700)', borderRadius: 3 }}>
                width: 200px<br />padding included in width
              </div>
              <div style={{ fontSize: 10, color: 'var(--color-gray-400)' }}>rendered width: 200px</div>
            </div>
            <div className="stui-col-6">
              <div style={{ fontSize: 11, color: 'var(--color-gray-500)', fontWeight: 600 }}>content-box (browser default) ✗</div>
              <div style={{ width: 200, padding: 16, border: '2px solid var(--color-gray-300)', boxSizing: 'content-box', background: 'var(--color-gray-50)', fontSize: 11, color: 'var(--color-gray-700)', borderRadius: 3 }}>
                width: 200px<br />padding added on top
              </div>
              <div style={{ fontSize: 10, color: 'var(--color-gray-400)' }}>rendered width: 236px (200 + 32 + 4)</div>
            </div>
          </div>
          <Subsection label="Reference" />
          <RefTable
            headers={['Selector', 'Property', 'Value', 'Notes']}
            rows={[
              ['*, *::before, *::after', 'box-sizing', 'border-box', 'Applies to every element and pseudo-element'],
            ]}
          />
          <Subsection label="Configurator" />
          <BoxSizingConfigurator />
        </Section>
      )}

      {activeSection === 'body' && (
        <Section title="Body" description="Sets the application's base font, weight, color, and anti-aliasing. All text inherits from this unless overridden." cssFile={CSS_LOCATIONS.baseBody.file} cssLine={CSS_LOCATIONS.baseBody.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ padding: '16px 20px', border: '1px solid var(--color-gray-200)', borderRadius: 4, background: 'var(--color-bg-white)', maxWidth: 480 }}>
            <p style={{ margin: 0, fontFamily: 'var(--font-secondary)', fontWeight: 500, color: 'var(--color-gray-800)', fontSize: 'var(--text-base)', WebkitFontSmoothing: 'antialiased' }}>
              The quick brown fox jumps over the lazy dog — Rajdhani 500
            </p>
            <p style={{ margin: '8px 0 0', fontFamily: 'var(--font-secondary)', fontWeight: 500, color: 'var(--color-gray-500)', fontSize: 'var(--text-sm)', WebkitFontSmoothing: 'antialiased' }}>
              Secondary text at var(--text-sm) — color-gray-500
            </p>
          </div>
          <Subsection label="Reference" />
          <RefTable
            headers={['Selector', 'Property', 'Value', 'Notes']}
            rows={[
              ['body', 'margin',                   '0',                          'Remove browser default margin'],
              ['body', 'font-family',              'var(--font-secondary)',       'Rajdhani / Roboto Condensed'],
              ['body', 'font-weight',              '500',                        ''],
              ['body', '-webkit-font-smoothing',   'antialiased',                'Thinner, crisper text on macOS/retina'],
              ['body', '-moz-osx-font-smoothing',  'grayscale',                  'Firefox equivalent'],
              ['body', 'color',                    'var(--color-gray-800)',       'Base text color — #1f2937'],
            ]}
          />
          <Subsection label="Configurator" />
          <BodyConfigurator />
        </Section>
      )}

      {activeSection === 'form-controls' && (
        <Section title="Form Controls" description="Browsers apply their own system font to inputs, selects, textareas, and buttons — overriding any body font. This rule forces them to inherit from the parent." cssFile={CSS_LOCATIONS.baseFormControls.file} cssLine={CSS_LOCATIONS.baseFormControls.line} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', padding: '16px 20px', border: '1px solid var(--color-gray-200)', borderRadius: 4, background: 'var(--color-bg-white)' }}>
            <input placeholder="Input" style={{ padding: '6px 10px', border: '1px solid var(--color-gray-200)', borderRadius: 4, fontSize: 13 }} />
            <select style={{ padding: '6px 10px', border: '1px solid var(--color-gray-200)', borderRadius: 4, fontSize: 13 }}>
              <option>Select</option>
            </select>
            <button style={{ padding: '6px 14px', border: '1px solid var(--color-gray-200)', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}>Button</button>
            <textarea placeholder="Textarea" rows={2} style={{ padding: '6px 10px', border: '1px solid var(--color-gray-200)', borderRadius: 4, fontSize: 13, resize: 'none' }} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--color-gray-400)', marginTop: 6, fontStyle: 'italic' }}>
            All four elements render in Rajdhani / Roboto Condensed — same as body text
          </div>
          <Subsection label="Reference" />
          <RefTable
            headers={['Selector', 'Property', 'Value', 'Notes']}
            rows={[
              ['input, select, textarea, button', 'font-family', 'inherit', 'Picks up body font-family'],
              ['input, select, textarea, button', 'font-weight', 'inherit', 'Picks up body font-weight 500'],
            ]}
          />
          <Subsection label="Configurator" />
          <FormControlsConfigurator />
        </Section>
      )}

    </div>
  );
};

/* ── Tab: CSS Output Test ──────────────────────────────────── */

// Registry: one entry per STUI class that has a configurator.
// Each renderer receives the parsed token array and returns JSX.
const CSS_OUTPUT_RENDERERS = {
  'stui-page-header': (tokens) => {
    const iconName  = (tokens.find(t => / icon$/.test(t)) || '').replace(/ icon$/, '') || 'FileSpreadsheet';
    const titleM    = (tokens.find(t => /^title "/.test(t))  || '').match(/"([^"]*)"/);
    const descM     = (tokens.find(t => /^desc "/.test(t))   || '').match(/"([^"]*)"/);
    const hasExport = tokens.some(t => t === 'with export action menu');
    const IconMap   = { FileSpreadsheet, FileText, FileDown };
    const IconComp  = IconMap[iconName] || FileSpreadsheet;
    return (
      <div className="stui-page-header">
        <div className="stui-row-md">
          <h1 className="stui-page-title">
            <IconComp size={20} />{titleM ? titleM[1] : 'Report Title'}
          </h1>
          {descM && <span className="stui-text-sm-muted">{descM[1]}</span>}
        </div>
        {hasExport && <DemoExportMenu />}
      </div>
    );
  },
  'stui-detail-section-header': (tokens) => {
    const labelM = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    return <div className="stui-detail-section-header stui-m-0">{labelM ? labelM[1] : 'Section Title'}</div>;
  },
  'stui-btn-sort-state': (tokens) => {
    const colM = (tokens.find(t => /^col "/.test(t)) || '').match(/"([^"]*)"/);
    const dir  = tokens.some(t => t === 'dir desc') ? '↓' : '↑';
    return <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 16 }}><span className="stui-btn-sort-state">{dir} {colM ? colM[1] : 'Column'}</span><button className="stui-btn-sort-reset">✕ Clear Sort</button></div>;
  },
  'stui-col': (tokens) => {
    const spanM = tokens.find(t => /^span /.test(t));
    const n     = spanM ? parseInt(spanM.replace('span ', '')) : 12;
    const rem   = 24 - n;
    return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)', gap: 2, padding: 16 }}><div style={{ gridColumn: `span ${n}`, background: 'var(--color-teal-600)', borderRadius: 3, padding: '6px 0', textAlign: 'center', color: 'white', fontSize: 12, fontWeight: 600 }}>{n}</div>{rem > 0 && <div style={{ gridColumn: `span ${rem}`, background: 'var(--color-gray-100)', borderRadius: 3, padding: '6px 0', textAlign: 'center', fontSize: 12, color: 'var(--color-gray-400)' }}>{rem}</div>}</div>;
  },
  'stui-module-header': (tokens) => {
    const iconM  = (tokens.find(t => / icon$/.test(t)) || '').replace(/ icon$/, '');
    const titleM = (tokens.find(t => /^title "/.test(t)) || '').match(/"([^"]*)"/);
    const countM = tokens.find(t => /^count /.test(t));
    const IconMap = { Baby, FileSpreadsheet, FileText, Database };
    const IconComp = IconMap[iconM] || Baby;
    return <div className="stui-module-header-styleguideonly"><div className="stui-module-header-left"><IconComp size={20} className="stui-page-icon" /><h1>{titleM ? titleM[1] : 'Module'}</h1><span className="stui-module-header-meta">{countM ? countM.replace('count ', '') : '0'} records</span></div><div className="stui-module-header-actions"><button className="stui-crud-btn" disabled><Plus size={16} /></button><button className="stui-action-menu-btn disabled" disabled><GripVertical size={16} /></button></div></div>;
  },
  'stui-boolean-display': (tokens) => {
    const isTrue = tokens.some(t => t === 'value true');
    return <div className="stui-p-16"><div className="stui-boolean-display">{isTrue && <span className="stui-boolean-check">✓</span>}</div></div>;
  },
  'stui-breadcrumb': (tokens) => {
    const segs = tokens.join(' ').match(/"([^"]+)"/g)?.map(s => s.replace(/"/g, '')) || ['Projects', 'BU28', 'Current'];
    return <div className="stui-p-16"><div className="stui-breadcrumb" style={{ display: 'flex', alignItems: 'center' }}>{segs.map((s, i) => <React.Fragment key={i}>{i > 0 && <span className="stui-breadcrumb-arrow"> › </span>}<span className={i === segs.length - 1 ? 'stui-breadcrumb-current' : 'stui-breadcrumb-parent'}>{s}</span></React.Fragment>)}</div></div>;
  },
  'stui-badge': (tokens) => {
    const variantM = tokens.find(t => /^variant /.test(t));
    const variant  = variantM ? variantM.replace('variant ', '') : 'accepted';
    const labelM   = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    return <div className="stui-p-16"><span className={`stui-badge-${variant}`}>{labelM ? labelM[1] : variant}</span></div>;
  },
  'stui-tile': (tokens) => {
    const titleM    = (tokens.find(t => /^title "/.test(t)) || '').match(/"([^"]*)"/);
    const clickable = tokens.some(t => t === 'clickable');
    const legend    = tokens.some(t => t === 'with legend');
    return <div className="stui-p-16"><div className={`stui-tile${clickable ? ' clickable' : ''}`} style={{ width: 200 }}><div className="stui-tile-header"><FileText size={16} className="stui-tile-icon" /><span className="stui-tile-title">{titleM ? titleM[1] : 'Tile'}</span></div><div className="stui-tile-content stui-text-placeholder">…chart…</div>{legend && <div className="stui-tile-legend"><span className="stui-legend-item"><span className="stui-legend-dot" style={{ background: 'var(--color-primary)' }} />Series A</span></div>}</div></div>;
  },
  'stui-stat-row': (tokens) => {
    const stats = tokens.map(t => { const m = t.match(/"([^:]+):([^"]+)"/); return m ? { label: m[1], value: m[2] } : null; }).filter(Boolean);
    const items = stats.length ? stats : [{ label: 'Rows', value: '1,284' }, { label: 'Pages', value: '43' }];
    return <div className="stui-p-16"><div className="stui-stat-row"><div className="stui-stat-group">{items.map((s, i) => <div key={i} className="stui-stat-item"><span className="stui-stat-label">{s.label}</span><span className="stui-stat-value">{s.value}</span></div>)}</div></div></div>;
  },
  'stui-label': (tokens) => {
    const textM = (tokens.find(t => /^text "/.test(t)) || '').match(/"([^"]*)"/);
    return <div style={{ padding: 16, display: 'flex', gap: 8, alignItems: 'center' }}><span className="stui-label">{textM ? textM[1] : 'Label'}</span><select className="stui-select"><option>Option</option></select></div>;
  },
  'stui-checkbox': (tokens) => {
    const labelM  = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    const checked = tokens.some(t => t === 'checked');
    return <div className="stui-p-16"><label className="stui-checkbox"><input type="checkbox" defaultChecked={checked} readOnly /> {labelM ? labelM[1] : 'Checkbox'}</label></div>;
  },
  'stui-sub-label': (tokens) => {
    const labelM    = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    const sublabelM = (tokens.find(t => /^sublabel "/.test(t)) || '').match(/"([^"]*)"/);
    return <div style={{ padding: 16, display: 'flex', flexDirection: 'column' }}><span className="stui-label">{labelM ? labelM[1] : 'Label'}</span><div className="stui-sub-label">{sublabelM ? sublabelM[1] : 'Helper text'}</div></div>;
  },
  'stui-pill': (tokens) => {
    const optsM    = (tokens.find(t => /^options "/.test(t)) || '').match(/"([^"]*)"/);
    const selM     = tokens.find(t => /^selected /.test(t));
    const opts     = optsM ? optsM[1].split(',') : ['Option A', 'Option B'];
    const selected = selM ? parseInt(selM.replace('selected ', '')) : 0;
    return <div className="stui-p-16"><div className="stui-pill-group">{opts.map((o, i) => <button key={i} className={`stui-pill${i === selected ? ' selected' : ''}`}>{o.trim()}</button>)}</div></div>;
  },
  'stui-toggle': (tokens) => {
    const labelM = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    const on     = tokens.some(t => t === 'on');
    return <div style={{ padding: 16, display: 'flex', gap: 10, alignItems: 'center' }}><label className="stui-toggle"><input type="checkbox" defaultChecked={on} readOnly /><span className="stui-toggle-slider" /></label><span className="stui-label">{labelM ? labelM[1] : 'Toggle'}</span></div>;
  },
  'stui-row-setting': (tokens) => {
    const type      = tokens.find(t => /^type /.test(t))?.replace('type ', '') || 'toggle';
    const labelM    = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    const sublabelM = (tokens.find(t => /^sublabel "/.test(t)) || '').match(/"([^"]*)"/);
    return <div style={{ padding: '0 16px' }}><div className="stui-row-setting"><div className="stui-row-setting-label"><div className="stui-label">{labelM ? labelM[1] : 'Setting'}</div>{sublabelM && <div className="stui-sub-label">{sublabelM[1]}</div>}</div>{type === 'toggle' && <label className="stui-toggle"><input type="checkbox" defaultChecked readOnly /><span className="stui-toggle-slider" /></label>}{type === 'select' && <select className="stui-select"><option>Option A</option></select>}{type === 'buttons' && <div className="stui-row-setting-controls"><button className="stui-btn stui-btn-primary">Apply</button></div>}</div></div>;
  },
  'stui-list-view': () => <div style={{ padding: 16, fontSize: 12, color: 'var(--color-gray-500)' }}>Structural layout — two-panel split (stui-list-panel + stui-detail-panel inside stui-list-view).</div>,
  'stui-field-group': (tokens) => {
    const labelM = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    const state  = tokens.find(t => /^state /.test(t))?.replace('state ', '') || 'normal';
    const pos    = tokens.find(t => /^position /.test(t))?.replace('position ', '') || 'top';
    const posClass = pos === 'top' ? '' : ` label-${pos}`;
    return <div className="stui-p-16"><div className={`stui-field-group${posClass}`} style={{ width: 240 }}><label>{labelM ? labelM[1] : 'Field'}</label>{state === 'readonly' ? <div className="stui-field-readonly">System managed</div> : state === 'derived' ? <input className="stui-field-input derived" value="AUTO-2024" readOnly /> : <input className={`stui-field-input${state === 'normal' ? '' : ` ${state}`}`} defaultValue="Sample value" />}</div></div>;
  },
  'stui-searchselect-trigger': (tokens) => {
    const valueM = (tokens.find(t => /^value "/.test(t)) || '').match(/"([^"]*)"/);
    const state  = tokens.find(t => /^state /.test(t))?.replace('state ', '') || 'normal';
    const cls    = `stui-searchselect-trigger${state !== 'normal' ? ` ${state}` : ''}`;
    return <div className="stui-p-16"><div className={cls} style={{ width: 200 }}><span className={`stui-searchselect-value${!valueM ? ' placeholder' : ''}`}>{valueM ? valueM[1] : '-- Select --'}</span><ChevronDown size={14} className="stui-searchselect-chevron" /></div></div>;
  },
  'stui-related-section': (tokens) => {
    const tabsRaw   = tokens.find(t => /^tabs "/.test(t))?.match(/"([^"]+)"/)?.[1] || 'Tab A,Tab B';
    const collapsed = tokens.some(t => t === 'collapsed');
    const tabs      = tabsRaw.split(',').map(t => t.trim());
    return <div className="stui-p-16"><div style={{ border: '1px solid var(--color-border)', borderRadius: 4, overflow: 'hidden' }}><div className={`stui-related-section${collapsed ? ' collapsed' : ''}`}><div className="stui-related-header"><div className="stui-related-tabs">{tabs.map((t, i) => <button key={i} className={`stui-related-tab${i === 0 ? ' active' : ''}`}>{t}<span className="stui-related-tab-count">{(i+1)*4}</span></button>)}</div></div>{!collapsed && <div className="stui-related-content stui-related-body">Showing {tabs[0]} records…</div>}</div></div></div>;
  },
  'stui-pagination': (tokens) => {
    const pageM  = tokens.find(t => /^page /.test(t));
    const totalM = tokens.find(t => /^total /.test(t));
    const sizeM  = tokens.find(t => /^pageSize /.test(t));
    const page   = pageM  ? parseInt(pageM.replace('page ', ''))     : 1;
    const total  = totalM ? parseInt(totalM.replace('total ', ''))   : 100;
    const size   = sizeM  ? parseInt(sizeM.replace('pageSize ', '')) : 50;
    const totalPages = Math.ceil(total / size);
    const start  = (page - 1) * size + 1;
    const end    = Math.min(page * size, total);
    const pages  = Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1);
    return <div className="stui-p-16"><div style={{ border: '1px solid var(--color-border)', borderRadius: 4, overflow: 'hidden', maxWidth: 360 }}><div className="stui-pagination"><div className="stui-pagination-controls"><button className="stui-pagination-btn" disabled={page <= 1}><ChevronLeft size={14} /></button>{pages.map(p => <button key={p} className={`stui-pagination-btn${p === page ? ' active' : ''}`}>{p}</button>)}<button className="stui-pagination-btn" disabled={page >= totalPages}><ChevronRight size={14} /></button></div><span className="stui-pagination-info">{start}–{end} of {total}</span></div></div></div>;
  },
  'stui-fk-select': (tokens) => {
    const pathM  = (tokens.find(t => /^path "/.test(t)) || '').match(/"([^"]*)"/);
    const segs   = (pathM ? pathM[1] : 'Parent → Child').split('→').map(s => s.trim());
    return <div className="stui-p-16"><div style={{ border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden', maxWidth: 380, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}><div className="stui-fk-select-header"><div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>{segs.map((s, i) => <React.Fragment key={i}>{i > 0 && <span style={{ color: 'var(--color-gray-400)', margin: '0 2px' }}>→</span>}<span style={{ color: i === segs.length-1 ? 'var(--color-text)' : 'var(--color-gray-400)', fontWeight: i === segs.length-1 ? 600 : 400 }}>{s}</span></React.Fragment>)}</div><button className="stui-crud-btn" title="Close"><X size={16} /></button></div><div className="stui-fk-select-body" style={{ padding: '12px 16px', minHeight: 60, fontSize: 12, color: 'var(--color-gray-400)', fontStyle: 'italic' }}>…record list…</div></div></div>;
  },
  'stui-composer-select': (tokens) => {
    const moduleM = (tokens.find(t => /^module "/.test(t)) || '').match(/"([^"]*)"/);
    return <div className="stui-p-16"><div className="stui-composer-select"><label className="stui-composer-select-label">Module</label><div className="stui-composer-select-trigger"><span>{moduleM ? moduleM[1] : 'Building'}</span><ChevronDown size={14} /></div></div></div>;
  },
  'stui-module-header-crud': (tokens) => {
    const mode  = tokens.find(t => /^mode /.test(t))?.replace('mode ', '') || 'list';
    const state = tokens.find(t => /^state /.test(t))?.replace('state ', '') || 'view';
    return <div className="stui-p-16"><div style={{ padding: '12px 16px', background: 'var(--color-surface-alt)', border: '1px solid var(--color-border)', borderRadius: 4, display: 'inline-flex' }}>{mode === 'list' ? <div className="stui-module-header-crud">{state === 'view' ? <><button className="stui-crud-btn" title="Add"><Plus size={18} /></button><button className="stui-crud-btn" title="Edit"><Pencil size={18} /></button><button className="stui-crud-btn disabled" disabled><Save size={18} /></button></> : <><button className="stui-crud-btn stui-delete-btn"><Trash2 size={18} /></button><button className="stui-crud-btn"><X size={18} /></button><button className="stui-crud-btn"><Save size={18} /></button></>}</div> : <div className="stui-module-header-datasheet-controls"><div className="stui-module-header-crud"><button className="stui-crud-btn"><Pencil size={18} /></button><button className={`stui-crud-btn${state === 'view' ? ' disabled' : ''}`} disabled={state === 'view'}><Save size={18} /></button></div></div>}</div></div>;
  },
  // Prefix-matched renderers — stui-btn-primary, stui-btn-secondary, stui-btn-danger all resolve here
  'stui-btn': (tokens, className) => {
    const iconM    = (tokens.find(t => / icon$/.test(t)) || '').replace(/ icon$/, '');
    const labelM   = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    const disabled = tokens.some(t => t === 'disabled');
    const IconMap  = { FileSpreadsheet, FileText, FileDown, Download };
    const IconComp = IconMap[iconM] || null;
    const label    = labelM ? labelM[1] : 'Button';
    const variantM = (className || '').match(/stui-btn-(primary|secondary|danger)/);
    const variant  = variantM ? variantM[1] : 'primary';
    return <div className="stui-p-16"><button className={`stui-btn stui-btn-${variant}`} disabled={disabled}>{IconComp && <IconComp size={14} style={{ marginRight: 4 }} />}{label}</button></div>;
  },
  'stui-select': (tokens) => {
    const phM      = (tokens.find(t => /^placeholder "/.test(t)) || '').match(/"([^"]*)"/);
    const disabled = tokens.some(t => t === 'disabled');
    return <div className="stui-p-16"><select className="stui-select" disabled={disabled} defaultValue=""><option value="" disabled>{phM ? phM[1] : '-- Select --'}</option><option>Option A</option><option>Option B</option><option>Option C</option></select></div>;
  },
  'stui-data-grid': (tokens) => {
    const colsM = tokens.find(t => /col/.test(t));
    const rowsM = tokens.find(t => /row/.test(t));
    const cols  = colsM ? parseInt(colsM) : 3;
    const rows  = rowsM ? parseInt(rowsM) : 3;
    const headers = ['Column A', 'Column B', 'Column C', 'Column D', 'Column E'].slice(0, Math.min(cols, 5));
    const rowData = Array.from({ length: Math.min(rows, 4) }, (_, i) => headers.map((_, j) => `R${i+1}C${j+1}`));
    return <div className="stui-p-16"><div className="stui-data-grid-container"><div className="stui-data-grid-scroll-area"><table className="stui-data-grid"><thead><tr>{headers.map(h => <th key={h}>{h}</th>)}</tr></thead><tbody>{rowData.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody></table></div></div></div>;
  },
  'stui-section-card': (tokens) => {
    const hasDesc    = tokens.some(t => t === 'with description');
    const hasChip    = tokens.some(t => / chip$/.test(t));
    const subsCount  = (tokens.find(t => /subsection/.test(t)) || '1').match(/(\d+)/)?.[1] || '1';
    return <div className="stui-p-16"><div className="stui-section-card"><div className="stui-section-card-header">{hasChip && <span className="stui-css-chip">STUI.css : L100</span>}<span className="stui-section-card-title">Section Title</span>{hasDesc && <p className="stui-section-card-desc">Short description of this section.</p>}</div><div className="stui-section-card-body">{Array.from({ length: parseInt(subsCount) }, (_, i) => <div key={i} className="stui-detail-section-header" style={{ margin: '0 0 8px' }}>Subsection {i+1}</div>)}</div></div></div>;
  },
  'stui-step-panel': (tokens) => {
    const numM      = tokens.find(t => /^number /.test(t));
    const titleM    = (tokens.find(t => /^title "/.test(t)) || '').match(/"([^"]*)"/);
    const descM     = (tokens.find(t => /^desc "/.test(t)) || '').match(/"([^"]*)"/);
    const collapsed = tokens.some(t => t === 'collapsed');
    const hasBtn    = tokens.some(t => t === 'Run Report button');
    const num       = numM ? numM.replace('number ', '') : '1';
    return <div className="stui-p-16"><div className={`stui-step-panel${collapsed ? ' collapsed' : ''}`}><div className="stui-step-panel-header"><span className="stui-step-number">{num}</span><div className="stui-step-info"><span className="stui-step-title">{titleM ? titleM[1] : 'Step Title'}</span>{descM && <span className="stui-step-desc">{descM[1]}</span>}</div></div>{!collapsed && hasBtn && <div style={{ padding: '8px 16px' }}><button className="stui-btn stui-btn-primary">Run Report</button></div>}</div></div>;
  },
  'stui-filter-dropdown': (tokens) => {
    const hasSort   = !tokens.some(t => t === 'no sort');
    const hasSearch = !tokens.some(t => t === 'no search');
    const hasSelAll = !tokens.some(t => t === 'no select-all');
    return <div className="stui-p-16"><div className="stui-filter-dropdown" style={{ width: 200, border: '1px solid var(--color-border)', borderRadius: 4 }}>{hasSort && <div style={{ padding: '6px 10px', borderBottom: '1px solid var(--color-gray-100)', display: 'flex', gap: 4 }}><button className="stui-btn-sort-state">↑ Asc</button><button className="stui-btn-sort-state">↓ Desc</button></div>}{hasSearch && <div style={{ padding: '6px 10px' }}><input className="stui-field-input" placeholder="Search…" style={{ width: '100%', boxSizing: 'border-box' }} readOnly /></div>}{hasSelAll && <div style={{ padding: '4px 10px', borderBottom: '1px solid var(--color-gray-100)', fontSize: 12 }}><label style={{ display: 'flex', gap: 6, cursor: 'pointer' }}><input type="checkbox" defaultChecked readOnly /> Select All</label></div>}<div style={{ padding: '4px 0' }}>{['Alpha', 'Bravo', 'Charlie'].map(o => <label key={o} style={{ display: 'flex', gap: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}><input type="checkbox" readOnly /> {o}</label>)}</div></div></div>;
  },
  'stui-action-menu': (tokens) => {
    const isReport  = tokens.some(t => t === 'report');
    const items     = isReport
      ? ['Download PDF', 'Download Excel', 'Download CSV']
      : ['Refresh', 'Download', 'Modify View'];
    return <div className="stui-p-16"><div className="stui-action-menu"><button className="stui-action-menu-btn"><GripVertical size={16} /></button><div className="stui-action-menu-dropdown" style={{ display: 'block', position: 'relative', boxShadow: 'none', border: '1px solid var(--color-border)' }}>{items.map(item => <div key={item} className="stui-action-menu-item">{item}</div>)}</div></div></div>;
  },
  // App Shell renderers
  'stui-nav-item': (tokens) => {
    const levelM = tokens.find(t => /^level /.test(t))?.replace('level ', '') || '1';
    const labelM = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    const active = tokens.some(t => t === 'active');
    const future = tokens.some(t => t === 'future');
    const label  = labelM ? labelM[1] : 'Nav Item';
    return (
      <div style={{ padding: 16, background: 'var(--color-bg-white)', width: 220, border: '1px solid var(--color-gray-200)', borderRadius: 4 }}>
        <div className="stui-nav-item-container">
          <button className={`stui-nav-item stui-nav-item-level-${levelM}${active ? ' active' : ''}`}>
            <div className="stui-nav-item-content">
              <span className="stui-nav-item-spacer" />
              <span className="stui-nav-item-label">{label}</span>
              {future && <span className="stui-nav-future-badge">FUTURE</span>}
            </div>
          </button>
        </div>
      </div>
    );
  },
  'stui-footer': (tokens) => {
    const dbM    = (tokens.find(t => /^db "/.test(t)) || '').match(/"([^"]*)"/);
    const verM   = (tokens.find(t => /^version "/.test(t)) || '').match(/"([^"]*)"/);
    const preprod = tokens.some(t => t === 'preprod');
    return (
      <div className="stui-p-16">
        <div className="stui-demo-header-bar">
          <div className="stui-footer-left">
            <span className={`stui-footer-db-badge${preprod ? ' stui-footer-db-badge-preprod' : ''}`}>
              <Database size={12} /><span>{dbM ? dbM[1] : 'GWA'}</span>
            </span>
            {verM && <span className={`stui-footer-version${preprod ? ' stui-footer-version-preprod' : ''}`}>{verM[1]}</span>}
          </div>
          <div className="stui-footer-right">
            <button className="stui-debug-btn"><Bug size={11} /></button>
          </div>
        </div>
      </div>
    );
  },
  'stui-debug-panel': (tokens) => {
    const togOn = tokens.some(t => t === 'toggle on');
    return (
      <div className="stui-p-16">
        <div className="stui-debug-panel" style={{ position: 'relative', bottom: 'auto', right: 'auto' }}>
          <div className="stui-debug-head">
            <span className="stui-debug-title">Debug Tools</span>
            <span className="stui-debug-hint">Shift+F12</span>
            <button className="stui-debug-close"><X size={12} /></button>
          </div>
          <div className="stui-debug-section">Hover Inspectors</div>
          <div className="stui-debug-row">
            <span className="stui-debug-label">CSS Variables</span>
            <span className={`stui-debug-tog${togOn ? ' on' : ''}`} />
          </div>
          <div className="stui-debug-row">
            <span className="stui-debug-label">Tab Order</span>
            <span className="stui-debug-tog" />
          </div>
          <div className="stui-debug-section">Theme</div>
          <div className="stui-debug-mode-row">
            <button className="stui-debug-mode-btn on">Standard</button>
            <button className="stui-debug-mode-btn">Dark</button>
            <button className="stui-debug-mode-btn">Dim</button>
            <button className="stui-debug-mode-btn">Sepia</button>
          </div>
        </div>
      </div>
    );
  },
  'stui-activity-panel': (tokens) => {
    const empty  = tokens.some(t => t === 'empty');
    const format = tokens.find(t => /^format /.test(t))?.replace('format ', '') || 'xlsx';
    return (
      <div className="stui-p-16">
        <div className="stui-activity-panel" style={{ position: 'relative', top: 'auto', right: 'auto', width: 280 }}>
          <div className="stui-activity-panel-header">
            <div className="stui-row-6">
              <ArrowDownUp size={14} style={{ color: 'var(--color-primary)' }} />
              <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--color-gray-700)' }}>Data Transfer</span>
            </div>
          </div>
          <div className="stui-activity-entries-scroll">
            {empty ? (
              <div className="stui-activity-empty">No active transfers</div>
            ) : (
              <div className="stui-activity-entry">
                <div className="stui-entry-icon-col"><Download size={13} style={{ color: '#16a34a' }} /></div>
                <div className="stui-entry-main-col">
                  <div className="stui-entry-name-row">
                    <span className="stui-entry-name">I/O Assignments</span>
                    <span className="stui-entry-format-badge" style={{ background: '#dcfce7', color: '#16a34a' }}>{format.toUpperCase()}</span>
                  </div>
                  <div className="stui-entry-phase">Generating…</div>
                  <div className="stui-entry-progress-track">
                    <div className="stui-entry-progress-fill" style={{ width: '60%', background: 'var(--color-primary)' }} />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="stui-activity-panel-footer">
            <div className="stui-row-4">
              <span style={{ fontSize: 9, color: 'var(--color-gray-400)', fontWeight: 600, textTransform: 'uppercase' }}>Clear:</span>
              {['1m','1 day'].map(o => <button key={o} className="stui-activity-clear-pill">{o}</button>)}
            </div>
            <button className="stui-activity-history-link"><Clock size={11} /> History</button>
          </div>
        </div>
      </div>
    );
  },
  'stui-modal': (tokens) => {
    const titleM  = (tokens.find(t => /^title "/.test(t)) || '').match(/"([^"]*)"/);
    const hasForm = tokens.some(t => t === 'with form');
    const hasList = tokens.some(t => t === 'with list');
    return (
      <div className="stui-p-16">
        <div className="stui-modal" style={{ position: 'relative', width: 360, maxHeight: 'none' }}>
          <div className="stui-modal-header">
            <h3>{titleM ? titleM[1] : 'Modal Title'}</h3>
            <button className="stui-modal-close">&times;</button>
          </div>
          {hasList && (
            <div className="stui-modal-list">
              {['Alice Johnson', 'Bob Smith', 'Carol White'].map(name => (
                <button key={name} className="stui-modal-user">
                  <div className="stui-modal-user-info">
                    <span className="stui-modal-user-name">{name}</span>
                    <span className="stui-modal-user-group">Developer</span>
                  </div>
                </button>
              ))}
            </div>
          )}
          {hasForm && (
            <div className="stui-modal-body">
              <div className="stui-form-row"><label>Description</label><input className="stui-field-input" placeholder="Enter value…" readOnly /></div>
            </div>
          )}
          <div className="stui-modal-footer">
            <button className="stui-btn stui-btn-secondary">Cancel</button>
            <button className="stui-btn stui-btn-primary">Confirm</button>
          </div>
        </div>
      </div>
    );
  },
  'stui-db-switcher-modal': (tokens) => {
    const dbs = [
      { name: 'GWA', desc: 'Greater Wenatchee Area', active: true },
      { name: 'LEAVENWORTH', desc: 'Leavenworth Project', active: false },
    ];
    return (
      <div className="stui-p-16">
        <div className="stui-db-switcher-modal" style={{ width: 340, height: 'auto', position: 'relative' }}>
          <div className="stui-db-switcher-header">
            <div style={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>DAWN</div>
            <button className="stui-modal-close">&times;</button>
          </div>
          <div className="stui-db-switcher-list">
            {dbs.map(db => (
              <button key={db.name} className={`stui-db-switcher-row${db.active ? ' current' : ''}`}>
                <div className="stui-db-switcher-logo"><div className="stui-db-switcher-logo-placeholder"><Database size={14} /></div></div>
                <div className="stui-db-switcher-info">
                  <span className="stui-db-switcher-name">{db.name}</span>
                  <span className="stui-db-switcher-desc">{db.desc}</span>
                </div>
                {db.active && <span className="stui-db-switcher-badge">Current</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  },
};

const CSSOutputTestTab = () => {
  const [input, setInput] = useState('');

  const parsed = useMemo(() => {
    const raw = input.trim();
    if (!raw) return null;
    const parts = raw.split(' · ').map(p => p.trim()).filter(Boolean);
    if (!parts.length) return null;
    return { className: parts[0], tokens: parts.slice(1) };
  }, [input]);

  const renderer = parsed ? (() => {
    if (CSS_OUTPUT_RENDERERS[parsed.className]) return CSS_OUTPUT_RENDERERS[parsed.className];
    // Prefix fallback: stui-col-12 → stui-col, stui-badge-accepted → stui-badge, stui-btn-primary → stui-btn
    const key = Object.keys(CSS_OUTPUT_RENDERERS).find(k => parsed.className.startsWith(k + '-'));
    return key ? CSS_OUTPUT_RENDERERS[key] : null;
  })() : null;

  return (
    <div className="stui-preview-zone" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Header */}
      <div className="stui-css-output-header">
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--color-gray-700)', fontFamily: 'var(--font-secondary)' }}>CSS Output Test</h2>
        <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--color-gray-400)' }}>Paste a configurator output string — renders the actual component</p>
      </div>

      <div className="stui-css-output-wrap">

        {/* Left: paste + parsed tokens */}
        <div className="stui-css-output-left">
          <div className="stui-detail-section-header stui-m-0">Paste Output</div>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={'stui-page-header · FileSpreadsheet icon · title "I/O Assignments" · desc "Tag IO…" · with export action menu'}
            style={{ minHeight: 120, resize: 'vertical', fontSize: 12, fontFamily: 'monospace', padding: '8px 10px', border: '1px solid var(--color-gray-200)', borderRadius: 4, color: 'var(--color-gray-700)', background: 'var(--color-bg-white)', lineHeight: 1.6 }}
          />
          {input && (
            <button onClick={() => setInput('')} style={{ alignSelf: 'flex-start', fontSize: 11, color: 'var(--color-gray-400)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Clear</button>
          )}

          {parsed && (
            <div className="stui-col-6">
              <div className="stui-detail-section-header stui-m-0">Parsed</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 10, color: 'var(--color-gray-400)', width: 52, flexShrink: 0 }}>class</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--color-primary)', fontWeight: 700 }}>.{parsed.className}</span>
                </div>
                {parsed.tokens.map((t, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 10, color: 'var(--color-gray-400)', width: 52, flexShrink: 0 }}>arg {i + 1}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--color-gray-600)' }}>{t}</span>
                  </div>
                ))}
                {!renderer && (
                  <div style={{ fontSize: 11, color: 'var(--color-gray-400)', fontStyle: 'italic', marginTop: 4 }}>
                    No renderer for this class — generic preview shown
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right: live preview */}
        <div className="stui-css-output-right">
          <div className="stui-detail-section-header stui-m-0">Live Preview</div>

          {!parsed && (
            <div style={{ color: 'var(--color-gray-300)', fontSize: 13, fontStyle: 'italic', marginTop: 8 }}>
              Paste a configurator output on the left
            </div>
          )}

          {/* Registered renderer — renders the actual component */}
          {parsed && renderer && (
            <div style={{ border: '1px solid var(--color-gray-200)', borderRadius: 6, overflow: 'hidden' }}>
              {renderer(parsed.tokens, parsed.className)}
            </div>
          )}

          {/* Generic fallback for unregistered classes */}
          {parsed && !renderer && (
            <div className="stui-col-12">
              {(['div', 'button', 'span']).map(tag => (
                <div key={tag} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: 10, color: 'var(--color-gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>{`<${tag}>`}</span>
                  <div style={{ padding: '12px 16px', background: 'var(--color-gray-50)', border: '1px solid var(--color-gray-200)', borderRadius: 4, overflow: 'hidden' }}>
                    {tag === 'div'    && <div className={parsed.className}>Sample content</div>}
                    {tag === 'button' && <button className={parsed.className}>Button</button>}
                    {tag === 'span'   && <span className={parsed.className}>Span</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

/* ── Tab: CSS Migrate ──────────────────────────────────────── */

const parseStuiClasses = (cssContent) => {
  if (!cssContent) return [];
  const seen = new Set();
  const classes = [];
  const re = /\.([a-zA-Z][a-zA-Z0-9\-_]*)(?=[\s,:{])/g;
  let m;
  while ((m = re.exec(cssContent)) !== null) {
    if (!seen.has(m[1])) { seen.add(m[1]); classes.push(m[1]); }
  }
  return classes;
};

const parseStuiClassProps = (cssContent, className) => {
  if (!cssContent || !className) return [];
  const esc = className.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&');
  const blockRe = new RegExp(`\\.${esc}\\s*\\{([^}]+)\\}`, 's');
  const match = cssContent.match(blockRe);
  if (!match) return [];
  return match[1].split(';')
    .map(p => p.trim())
    .filter(p => p && p.includes(':'))
    .map(p => { const i = p.indexOf(':'); return { prop: p.substring(0, i).trim(), value: p.substring(i + 1).trim() }; });
};

const MigrationTab = () => {
  const [appIndex, setAppIndex]           = useState(null);
  const [appFilter, setAppFilter]         = useState('');
  const [selectedApp, setSelectedApp]     = useState(null);
  const [appStyles, setAppStyles]         = useState(null);
  const [appUsages, setAppUsages]         = useState(null);
  const [usagesExp, setUsagesExp]         = useState(false);
  const [loadingDet, setLoadingDet]       = useState(false);
  const [stuiContent, setStuiContent]     = useState(null);
  const [stuiFilter, setStuiFilter]       = useState('');
  const [selectedStui, setSelectedStui]   = useState(null);
  const [stuiUsages, setStuiUsages]       = useState(null);
  const [stuiUsagesExp, setStuiUsagesExp] = useState(false);
  const [loadingStuiDet, setLoadingStuiDet] = useState(false);
  const [menuOpen, setMenuOpen]           = useState(false);
  const [loading, setLoading]             = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setMenuOpen(false);
    try {
      const [idxRes, stuiRes] = await Promise.all([
        fetch(API_BASE_URL + '/api/devfiles/app-css-index'),
        fetch(API_BASE_URL + '/api/devfiles/stui-css'),
      ]);
      setAppIndex(await idxRes.json());
      const d = await stuiRes.json();
      setStuiContent(d.content);
    } catch (e) { console.error('Migration tab load failed:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Fetch App.css class details when selection changes
  useEffect(() => {
    if (!selectedApp) { setAppStyles(null); setAppUsages(null); return; }
    setUsagesExp(false);
    const load = async () => {
      setLoadingDet(true);
      try {
        const [sRes, uRes] = await Promise.all([
          fetch(API_BASE_URL + `/api/devfiles/ui-audit/styles?class=.${selectedApp}`),
          fetch(API_BASE_URL + `/api/devfiles/ui-audit/usages?class=${selectedApp}`),
        ]);
        setAppStyles(await sRes.json());
        setAppUsages(await uRes.json());
      } catch (e) { console.error('App class detail fetch failed:', e); }
      setLoadingDet(false);
    };
    load();
  }, [selectedApp]);

  // Fetch STUI class usages when selection changes
  useEffect(() => {
    if (!selectedStui) { setStuiUsages(null); return; }
    setStuiUsagesExp(false);
    const load = async () => {
      setLoadingStuiDet(true);
      try {
        const uRes = await fetch(API_BASE_URL + `/api/devfiles/ui-audit/usages?class=${selectedStui}`);
        setStuiUsages(await uRes.json());
      } catch (e) { console.error('STUI class usages fetch failed:', e); }
      setLoadingStuiDet(false);
    };
    load();
  }, [selectedStui]);

  // Auto-match: when App class selected, highlight stui-{class} if it exists
  useEffect(() => {
    setSelectedStui(null);
    if (!selectedApp || !stuiContent) return;
    const candidate = `stui-${selectedApp}`;
    if (parseStuiClasses(stuiContent).includes(candidate)) setSelectedStui(candidate);
  }, [selectedApp, stuiContent]);

  const filteredApp = (appIndex || []).filter(
    item => !appFilter || item.className.toLowerCase().includes(appFilter.toLowerCase())
  );
  const groupedApp = filteredApp.reduce((acc, item) => {
    (acc[item.section] = acc[item.section] || []).push(item);
    return acc;
  }, {});

  const stuiClasses  = parseStuiClasses(stuiContent);
  const filteredStui = stuiFilter ? stuiClasses.filter(c => c.toLowerCase().includes(stuiFilter.toLowerCase())) : stuiClasses;
  const stuiProps    = parseStuiClassProps(stuiContent, selectedStui);
  const autoMatch    = selectedApp ? `stui-${selectedApp}` : null;
  const hasMatch     = !!(autoMatch && stuiClasses.includes(autoMatch));

  // Migration status computation
  const stillInApp     = !!(selectedApp && appIndex?.some(item => item.className === selectedApp));
  const exactStuiMatch = !!(selectedApp && stuiClasses.includes(selectedApp));
  const stuiEquivName  = exactStuiMatch ? selectedApp : (hasMatch ? autoMatch : null);
  const hasStuiEquiv   = !!stuiEquivName;
  const stuiEquivProps = stuiEquivName ? parseStuiClassProps(stuiContent, stuiEquivName) : [];
  const varsUsed       = [...new Set(stuiEquivProps.flatMap(p => (p.value.match(/var\(--[^)]+\)/g) || [])))];
  const migStatus      = selectedApp
    ? (stillInApp && !hasStuiEquiv)                              ? 'not-started'
    : (stillInApp && hasStuiEquiv)                               ? 'duplicate'
    : (!stillInApp && hasStuiEquiv && varsUsed.length > 0)       ? 'vars-pending'
    : (!stillInApp && hasStuiEquiv && varsUsed.length === 0)     ? 'clean'
    : 'no-equiv'
    : null;

  const panelHeader = 'stui-panel-header';
  const detailBox   = 'stui-panel-detail';
  const propTable   = { width: '100%', fontSize: 11, borderCollapse: 'collapse' };
  const propTd1     = { padding: '2px 8px 2px 0', color: 'var(--color-gray-500)', fontFamily: 'monospace', width: '42%', verticalAlign: 'top' };
  const propTd2     = { padding: '2px 0', fontFamily: 'monospace', color: 'var(--color-gray-700)', wordBreak: 'break-all' };

  return (
    <div className="stui-preview-zone" style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(200vh - 84px)' }}>

      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 32px', borderBottom: '1px solid var(--color-gray-200)', background: 'var(--color-gray-50)', flexShrink: 0 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--color-gray-700)', fontFamily: 'var(--font-secondary)' }}>CSS Migration Tool</h2>
          {appIndex && <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--color-gray-400)' }}>{appIndex.length} App.css classes · {stuiClasses.length} STUI.css classes</p>}
        </div>
        <div className="stui-action-menu">
          <button className="stui-action-menu-btn" onClick={() => setMenuOpen(o => !o)} title="Actions"><GripVertical size={16} /></button>
          {menuOpen && (
            <div className="stui-action-menu-dropdown">
              <button className="stui-action-menu-item" onClick={refresh}><RefreshCw size={16} /> Refresh</button>
            </div>
          )}
        </div>
      </div>

      {loading && <div style={{ padding: '48px 32px', color: 'var(--color-gray-400)', fontSize: 13, textAlign: 'center' }}>Loading CSS index…</div>}

      {/* Migration status banner */}
      {!loading && selectedApp && migStatus && (() => {
        const colors = {
          'clean':       { bg: '#f0fdf4', border: '#86efac', text: '#16a34a', label: '✓ Clean'          },
          'not-started': { bg: '#fef2f2', border: '#fca5a5', text: '#dc2626', label: '✗ Not Migrated'   },
          'duplicate':   { bg: '#fffbeb', border: '#fcd34d', text: '#d97706', label: '⚠ In Both Files'  },
          'vars-pending':{ bg: '#fffbeb', border: '#fcd34d', text: '#d97706', label: '~ Migrated'       },
          'no-equiv':    { bg: 'var(--color-gray-50)', border: 'var(--color-gray-300)', text: 'var(--color-gray-500)', label: '◌ No Equivalent' },
        };
        const c = colors[migStatus];
        return (
          <div style={{ padding: '10px 20px', flexShrink: 0, borderBottom: `2px solid ${c.border}`, background: c.bg, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: c.text }}>{c.label}</span>
              <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--color-gray-600)' }}>.{selectedApp}</span>
            </div>
            <div className="stui-label-xs">
              {migStatus === 'clean'       && `Removed from App.css · STUI equivalent: .${stuiEquivName} · No var() dependencies`}
              {migStatus === 'not-started' && 'Still defined in App.css · No STUI equivalent found'}
              {migStatus === 'duplicate'   && `Defined in both files · Remove from App.css to complete — STUI equivalent: .${stuiEquivName}`}
              {migStatus === 'vars-pending'&& `Removed from App.css · STUI equivalent: .${stuiEquivName} · Uses ${varsUsed.length} CSS var${varsUsed.length !== 1 ? 's' : ''} from App.css :root`}
              {migStatus === 'no-equiv'    && 'Not in App.css · No STUI equivalent defined'}
            </div>
            {migStatus === 'vars-pending' && varsUsed.length > 0 && (
              <div style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--color-gray-400)', lineHeight: 1.8 }}>{varsUsed.join('  ·  ')}</div>
            )}
          </div>
        );
      })()}

      {!loading && (
        <div className="stui-migrate-wrap">

          {/* ── Left: App.css ── */}
          <div className="stui-migrate-panel-left">

            {/* Panel header + filter */}
            <div className={panelHeader}>
              <div className="stui-detail-section-header" style={{ margin: '0 0 10px' }}>App.css Browser</div>
              <input placeholder="Filter classes…" value={appFilter} onChange={e => setAppFilter(e.target.value)}
                className="stui-panel-filter" />
            </div>

            {/* ── Detail section — ABOVE the browser ── */}
            {selectedApp && (
              <div className={detailBox}>
                <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: 'var(--color-primary)', marginBottom: 10 }}>.{selectedApp}</div>

                {/* Live preview */}
                <div style={{ background: 'var(--color-gray-50)', border: '1px solid var(--color-gray-200)', borderRadius: 4, padding: '10px 12px', marginBottom: 12, overflow: 'hidden', position: 'relative' }}>
                  <div style={{ fontSize: 10, color: 'var(--color-gray-400)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Live Preview</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div className={selectedApp}>Div</div>
                    <button className={selectedApp}>Button</button>
                    <span className={selectedApp}>Span</span>
                  </div>
                </div>

                {/* CSS properties */}
                {loadingDet && <div style={{ fontSize: 12, color: 'var(--color-gray-400)', marginBottom: 8 }}>Loading…</div>}
                {!loadingDet && appStyles?.rules?.map((rule, ri) => (
                  <div key={ri} style={{ marginBottom: 10 }}>
                    {rule.state !== 'default' && <div style={{ fontSize: 10, color: 'var(--color-gray-400)', fontFamily: 'monospace', marginBottom: 3 }}>{rule.selector}</div>}
                    <table style={propTable}><tbody>
                      {Object.entries(rule.properties).map(([prop, val], pi) => (
                        <tr key={pi} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                          <td style={propTd1}>{prop}</td>
                          <td style={propTd2}>{val}</td>
                        </tr>
                      ))}
                    </tbody></table>
                  </div>
                ))}

                {/* Usages */}
                {!loadingDet && appUsages && (
                  <div style={{ marginTop: 8, borderTop: '1px solid var(--color-gray-100)', paddingTop: 8 }}>
                    <button onClick={() => setUsagesExp(ex => !ex)} style={{ fontSize: 11, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {usagesExp ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      Used by {appUsages.count} file{appUsages.count !== 1 ? 's' : ''}
                    </button>
                    {usagesExp && appUsages.usages?.map((u, ui) => (
                      <div key={ui} style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--color-gray-500)', padding: '2px 12px' }}>{u.fileName}:{u.line}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Class browser — below detail ── */}
            <div style={{ flex: 1, overflowY: 'auto', fontSize: 12 }}>
              {Object.keys(groupedApp).map(section => (
                <div key={section}>
                  <div style={{ padding: '4px 16px', background: 'var(--color-primary)', color: 'white', fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-secondary)', position: 'sticky', top: 0, zIndex: 1 }}>{section}</div>
                  {groupedApp[section].map(item => (
                    <div key={`${item.className}-${item.line}`} onClick={() => setSelectedApp(item.className)} style={{
                      padding: '4px 20px', cursor: 'pointer', fontFamily: 'monospace',
                      background: selectedApp === item.className ? 'var(--color-primary-light)' : 'transparent',
                      color: selectedApp === item.className ? 'var(--color-primary)' : 'var(--color-gray-700)',
                      borderLeft: selectedApp === item.className ? '3px solid var(--color-primary)' : '3px solid transparent',
                      fontWeight: selectedApp === item.className ? 600 : 400,
                    }}>.{item.className}</div>
                  ))}
                </div>
              ))}
              {appIndex && filteredApp.length === 0 && (
                <div style={{ padding: '24px 16px', color: 'var(--color-gray-400)', fontSize: 12, textAlign: 'center' }}>No classes match &quot;{appFilter}&quot;</div>
              )}
            </div>

          </div>

          {/* ── Right: STUI.css ── */}
          <div className="stui-migrate-panel-right">

            {/* Panel header + filter */}
            <div className={panelHeader}>
              <div className="stui-detail-section-header" style={{ margin: '0 0 10px' }}>STUI.css</div>
              <input placeholder="Filter classes…" value={stuiFilter} onChange={e => setStuiFilter(e.target.value)}
                className="stui-panel-filter" />
            </div>

            {/* Match banner */}
            {selectedApp && hasMatch && (
              <div style={{ padding: '7px 16px', fontSize: 12, flexShrink: 0, borderBottom: '1px solid var(--color-gray-200)', background: 'var(--color-teal-50)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontWeight: 700 }}>✓ Match</span> — <span className="stui-mono">.{autoMatch}</span> covers this
              </div>
            )}

            {/* ── Detail section — ABOVE the browser ── */}
            {selectedStui && (
              <div className={detailBox}>
                <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: 'var(--color-primary)', marginBottom: 10 }}>.{selectedStui}</div>

                {/* Live preview */}
                <div style={{ background: 'var(--color-gray-50)', border: '1px solid var(--color-gray-200)', borderRadius: 4, padding: '10px 12px', marginBottom: 12, overflow: 'hidden', position: 'relative' }}>
                  <div style={{ fontSize: 10, color: 'var(--color-gray-400)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Live Preview</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div className={selectedStui}>Div</div>
                    <button className={selectedStui}>Button</button>
                    <span className={selectedStui}>Span</span>
                  </div>
                </div>

                {/* CSS properties */}
                {stuiProps.length > 0
                  ? <div style={{ marginBottom: 10 }}>
                      <table style={propTable}><tbody>
                        {stuiProps.map((p, pi) => (
                          <tr key={pi} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                            <td style={propTd1}>{p.prop}</td>
                            <td style={propTd2}>{p.value}</td>
                          </tr>
                        ))}
                      </tbody></table>
                    </div>
                  : <div style={{ fontSize: 11, color: 'var(--color-gray-400)', fontStyle: 'italic', marginBottom: 10 }}>No properties found</div>
                }

                {/* Usages */}
                {loadingStuiDet && <div style={{ fontSize: 12, color: 'var(--color-gray-400)' }}>Loading usages…</div>}
                {!loadingStuiDet && stuiUsages && (
                  <div style={{ marginTop: 8, borderTop: '1px solid var(--color-gray-100)', paddingTop: 8 }}>
                    <button onClick={() => setStuiUsagesExp(ex => !ex)} style={{ fontSize: 11, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {stuiUsagesExp ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      Used by {stuiUsages.count} file{stuiUsages.count !== 1 ? 's' : ''}
                    </button>
                    {stuiUsagesExp && stuiUsages.usages?.map((u, ui) => (
                      <div key={ui} style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--color-gray-500)', padding: '2px 12px' }}>{u.fileName}:{u.line}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Class browser — below detail ── */}
            <div style={{ flex: 1, overflowY: 'auto', fontSize: 12 }}>
              {filteredStui.map(cls => {
                const isMatch = cls === autoMatch;
                const isSel   = cls === selectedStui;
                return (
                  <div key={cls} onClick={() => setSelectedStui(cls)} style={{
                    padding: '5px 16px', cursor: 'pointer', fontFamily: 'monospace',
                    background: isSel ? 'var(--color-primary-light)' : isMatch ? 'var(--color-teal-50)' : 'transparent',
                    color: isSel ? 'var(--color-primary)' : isMatch ? 'var(--color-primary)' : 'var(--color-gray-700)',
                    borderLeft: isSel ? '3px solid var(--color-primary)' : isMatch ? '3px solid var(--color-primary-hover)' : '3px solid transparent',
                    fontWeight: isSel || isMatch ? 600 : 400,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <span>.{cls}</span>
                    {isMatch && <span style={{ fontSize: 10, background: 'var(--color-primary)', color: 'white', padding: '1px 5px', borderRadius: 3 }}>MATCH</span>}
                  </div>
                );
              })}
            </div>

          </div>

        </div>
      )}
    </div>
  );
};

/* ── Migration Log Tab ─────────────────────────────────────── */

const MigrationLogTab = () => {
  const [rows, setRows]         = useState([]);
  const [appInput, setAppInput] = useState('');
  const [stuiInput, setStuiInput] = useState('');
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_BASE_URL + '/api/devfiles/css-migration');
      setRows(await res.json());
    } catch (e) { setError('Failed to load migration log.'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!appInput.trim() || !stuiInput.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(API_BASE_URL + '/api/devfiles/css-migration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appCssClass: appInput.trim(), stuiCssClass: stuiInput.trim() }),
      });
      if (res.ok) {
        setAppInput('');
        setStuiInput('');
        await load();
      }
    } catch (e) { setError('Failed to add entry.'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await fetch(API_BASE_URL + `/api/devfiles/css-migration/${id}`, { method: 'DELETE' });
      setRows(r => r.filter(x => x.id !== id));
    } catch (e) { setError('Failed to delete entry.'); }
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 800 }}>
      <div className="stui-mb-20">
        <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: 'var(--color-gray-700)', fontFamily: 'var(--font-secondary)' }}>Migration Log</h2>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--color-gray-400)' }}>Classes moved from App.css to STUI.css</p>
      </div>

      {error && <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--color-error)' }}>{error}</div>}

      {/* Add form */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center' }}>
        <input
          placeholder="App.css class"
          value={appInput}
          onChange={e => setAppInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
          className="stui-cfg-input stui-mono"
        />
        <span style={{ color: 'var(--color-gray-400)', fontSize: 14 }}>→</span>
        <input
          placeholder="STUI.css class"
          value={stuiInput}
          onChange={e => setStuiInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
          className="stui-cfg-input stui-mono"
        />
        <button className="stui-btn stui-btn-primary" onClick={handleAdd} disabled={saving || !appInput.trim() || !stuiInput.trim()}>
          {saving ? 'Adding…' : 'Add'}
        </button>
      </div>

      {/* Grid */}
      {loading
        ? <div style={{ fontSize: 12, color: 'var(--color-gray-400)' }}>Loading…</div>
        : (
          <table className="stui-data-grid stui-w-full">
            <thead>
              <tr>
                <th>App.css Class</th>
                <th>STUI.css Class</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0
                ? <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--color-gray-400)', fontStyle: 'italic', padding: '20px 0' }}>No entries yet</td></tr>
                : rows.map(row => (
                  <tr key={row.id}>
                    <td className="stui-mono">.{row.appCssClass}</td>
                    <td className="stui-mono">.{row.stuiCssClass}</td>
                    <td>
                      <button onClick={() => handleDelete(row.id)} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-400)', padding: '2px 4px', lineHeight: 1 }}>✕</button>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        )
      }
    </div>
  );
};

/* ── App Shell Tab ──────────────────────────────────────────── */

const NavConfigurator = () => {
  const [level, setLevel] = useState('1');
  const [label, setLabel] = useState('Nav Item');
  const [active, setActive] = useState(false);
  const [future, setFuture] = useState(false);
  const output = [`stui-nav-item`, `level ${level}`, `label "${label || 'Nav Item'}"`, active ? 'active' : 'inactive', future ? 'future' : ''].filter(Boolean).join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-row-10">
        <span className="stui-cfg-key">Level</span>
        {['1', '2', '3'].map(v => (
          <label key={v} className="stui-cfg-label">
            <input type="radio" name="nav-level" checked={level === v} onChange={() => setLevel(v)} /> {v}
          </label>
        ))}
      </div>
      <div className="stui-row-10">
        <span className="stui-cfg-key">Label</span>
        <input value={label} onChange={e => setLabel(e.target.value)} className="stui-cfg-input" style={{ width: 160 }} />
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <span className="stui-cfg-key">State</span>
        <label className="stui-cfg-label">
          <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} /> Active
        </label>
        <label className="stui-cfg-label">
          <input type="checkbox" checked={future} onChange={e => setFuture(e.target.checked)} /> Future badge
        </label>
      </div>
      <div className="stui-example-cell" style={{ justifyContent: 'flex-start' }}>
        <div style={{ background: '#1a5568', padding: '4px 0', borderRadius: 4, width: 220 }}>
          <div className="stui-nav-item-container">
            <button className={`stui-nav-item stui-nav-item-level-${level}${active ? ' active' : ''}`}>
              <div className="stui-nav-item-content">
                <span className="stui-nav-item-spacer" />
                <span className="stui-nav-item-label">{label || 'Nav Item'}</span>
                {future && <span className="stui-nav-future-badge">FUTURE</span>}
              </div>
            </button>
          </div>
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const FooterConfigurator = () => {
  const [db, setDb] = useState('GWA');
  const [version, setVersion] = useState('v18.001');
  const [preprod, setPreprod] = useState(false);
  const output = [`stui-footer`, `db "${db || 'GWA'}"`, `version "${version || 'v1.000'}"`, preprod ? 'preprod' : ''].filter(Boolean).join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-row-10">
        <span className="stui-cfg-key">DB</span>
        <input value={db} onChange={e => setDb(e.target.value)} className="stui-cfg-input" style={{ width: 120 }} />
      </div>
      <div className="stui-row-10">
        <span className="stui-cfg-key">Version</span>
        <input value={version} onChange={e => setVersion(e.target.value)} className="stui-cfg-input" style={{ width: 120 }} />
      </div>
      <div className="stui-row-10">
        <span className="stui-cfg-key">Env</span>
        <label className="stui-cfg-label">
          <input type="checkbox" checked={preprod} onChange={e => setPreprod(e.target.checked)} /> Pre-production
        </label>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const DebugConfigurator = () => {
  const [togOn, setTogOn] = useState(false);
  const output = [`stui-debug-panel`, togOn ? 'toggle on' : 'toggle off'].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-row-10">
        <span className="stui-cfg-key-lg">CSS Vars tog</span>
        <label className="stui-cfg-label">
          <input type="checkbox" checked={togOn} onChange={e => setTogOn(e.target.checked)} /> On
        </label>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const ActivityConfigurator = () => {
  const [empty, setEmpty] = useState(false);
  const [format, setFormat] = useState('xlsx');
  const output = [`stui-activity-panel`, empty ? 'empty' : `format ${format}`].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-row-10">
        <span className="stui-cfg-key">State</span>
        <label className="stui-cfg-label">
          <input type="checkbox" checked={empty} onChange={e => setEmpty(e.target.checked)} /> Empty
        </label>
      </div>
      {!empty && (
        <div className="stui-row-10-wrap">
          <span className="stui-cfg-key">Format</span>
          {['xlsx', 'pdf', 'csv'].map(v => (
            <label key={v} className="stui-cfg-label">
              <input type="radio" name="act-fmt" checked={format === v} onChange={() => setFormat(v)} /> {v}
            </label>
          ))}
        </div>
      )}
      <ConfigOutput text={output} />
    </div>
  );
};

const ModalConfigurator = () => {
  const [title, setTitle] = useState('Modal Title');
  const [body, setBody] = useState('list');
  const output = [`stui-modal`, `title "${title || 'Modal Title'}"`, body === 'list' ? 'with list' : body === 'form' ? 'with form' : ''].filter(Boolean).join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-row-10">
        <span className="stui-cfg-key">Title</span>
        <input value={title} onChange={e => setTitle(e.target.value)} className="stui-cfg-input" style={{ width: 200 }} />
      </div>
      <div className="stui-row-10-wrap">
        <span className="stui-cfg-key">Body</span>
        {[['list', 'User list'], ['form', 'Form'], ['none', 'None']].map(([v, lbl]) => (
          <label key={v} className="stui-cfg-label">
            <input type="radio" name="modal-body" checked={body === v} onChange={() => setBody(v)} /> {lbl}
          </label>
        ))}
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const AppShellTab = ({ usageMap, pageSlugMap, navigate }) => {
  const [activeSection, setActiveSection] = useState('nav');
  const sections = [
    { id: 'nav',      label: 'Navigation'    },
    { id: 'header',   label: 'Header'        },
    { id: 'activity', label: 'Activity Panel' },
    { id: 'modal',    label: 'Modal'         },
    { id: 'dbsw',     label: 'DB Switcher'   },
    { id: 'footer',   label: 'Footer'        },
    { id: 'debug',    label: 'Debug Panel'   },
  ];
  return (
    <div className="stui-preview-zone stui-tab-body">
      <div className="stui-mb-20">
        <select className="stui-select" value={activeSection} onChange={e => setActiveSection(e.target.value)} style={{ minWidth: 220 }}>
          {sections.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {/* Navigation */}
      {activeSection === 'nav' && (
        <Section title="Navigation" description="Sidebar nav items at three indent levels. The sidebar uses stui-nav-sidebar; individual items use stui-nav-item with stui-nav-item-level-1/2/3." cssFile="STUI.css" cssLine={null} onChipClick={() => {}} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ background: '#1a5568', padding: '8px 0', borderRadius: 6, width: 240, marginBottom: 8 }}>
            {[
              { level: 1, label: 'Reports',         active: true  },
              { level: 2, label: 'I/O Assignments',  active: false },
              { level: 2, label: 'Drawing Log',      active: false },
              { level: 1, label: 'Settings',         active: false },
            ].map((item, i) => (
              <div key={i} className="stui-nav-item-container">
                <button className={`stui-nav-item stui-nav-item-level-${item.level}${item.active ? ' active' : ''}`}>
                  <div className="stui-nav-item-content">
                    <span className="stui-nav-item-spacer" />
                    <span className="stui-nav-item-label">{item.label}</span>
                  </div>
                </button>
              </div>
            ))}
          </div>
          <CSSRef file="STUI.css" selector=".stui-nav-sidebar / .stui-nav-item / .stui-nav-item-level-1/2/3 / .stui-nav-item.active" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Role']}
            rows={[
              ['.stui-nav-sidebar',            'Sidebar container — full-height, teal bg, collapsible'],
              ['.stui-nav-sidebar.collapsed',  'Sidebar in collapsed/icon-only state'],
              ['.stui-nav-scroll',             'Scrollable inner area for nav items'],
              ['.stui-nav-item',               'Individual nav button — full width, left-aligned'],
              ['.stui-nav-item-level-1',       'Top-level item — no indent'],
              ['.stui-nav-item-level-2',       'Sub-item — 16px indent'],
              ['.stui-nav-item-level-3',       'Deep sub-item — 32px indent'],
              ['.stui-nav-item.active',        'Currently selected item — white highlight'],
              ['.stui-nav-item-content',       'Flex row inside the item button'],
              ['.stui-nav-item-label',         'Text label within nav item'],
              ['.stui-nav-item-spacer',        'Icon placeholder / indent spacer'],
              ['.stui-nav-future-badge',       '"FUTURE" label badge on not-yet-implemented items'],
              ['.stui-nav-footer',             'Bottom area of sidebar (version, collapse toggle)'],
              ['.stui-nav-footer-btn',         'Collapse / expand toggle button in sidebar footer'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-nav-item" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <NavConfigurator />
        </Section>
      )}

      {/* Header */}
      {activeSection === 'header' && (
        <Section title="Header" description="Top app bar with logo, DB badge, user menu trigger, and activity panel toggle." cssFile="STUI.css" cssLine={null} onChipClick={() => {}} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-header" style={{ position: 'relative', marginBottom: 8 }}>
            <div className="stui-header-left">
              <span style={{ fontWeight: 700, fontSize: 15, color: 'white', letterSpacing: 1 }}>DAWN</span>
            </div>
            <div className="stui-header-right">
              <button className="stui-header-db-btn"><Database size={13} /><span>GWA</span></button>
              <button className="stui-header-user-btn"><User size={14} /><span>T. McMurry</span><ChevronDown size={12} /></button>
              <button className="stui-header-activity-btn"><ArrowDownUp size={15} /></button>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-header / .stui-header-left / .stui-header-right / .stui-header-db-btn / .stui-header-user-btn / .stui-header-activity-btn" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Role']}
            rows={[
              ['.stui-header',               'Top app bar — teal, flex, fixed height'],
              ['.stui-header-left',          'Left slot — logo / app name'],
              ['.stui-header-right',         'Right slot — db, user, activity buttons'],
              ['.stui-header-db-btn',        'Database name trigger button'],
              ['.stui-header-user-btn',      'User identity / menu trigger'],
              ['.stui-header-activity-btn',  'Data transfer activity panel toggle'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-header" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {/* Activity Panel */}
      {activeSection === 'activity' && (
        <Section title="Activity Panel" description="Slide-in panel showing active data transfers. Floats from the header activity button." cssFile="STUI.css" cssLine={null} onChipClick={() => {}} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ padding: 8, background: 'var(--color-gray-50)', borderRadius: 6, marginBottom: 8, display: 'inline-block' }}>
            <div className="stui-activity-panel" style={{ position: 'relative', top: 'auto', right: 'auto', width: 280 }}>
              <div className="stui-activity-panel-header">
                <div className="stui-row-6">
                  <ArrowDownUp size={14} style={{ color: 'var(--color-primary)' }} />
                  <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--color-gray-700)' }}>Data Transfer</span>
                </div>
              </div>
              <div className="stui-activity-entries-scroll">
                <div className="stui-activity-entry">
                  <div className="stui-entry-icon-col"><Download size={13} style={{ color: '#16a34a' }} /></div>
                  <div className="stui-entry-main-col">
                    <div className="stui-entry-name-row">
                      <span className="stui-entry-name">I/O Assignments</span>
                      <span className="stui-entry-format-badge" style={{ background: '#dcfce7', color: '#16a34a' }}>XLSX</span>
                    </div>
                    <div className="stui-entry-phase">Generating…</div>
                    <div className="stui-entry-progress-track">
                      <div className="stui-entry-progress-fill" style={{ width: '60%', background: 'var(--color-primary)' }} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="stui-activity-panel-footer">
                <div className="stui-row-4">
                  <span style={{ fontSize: 9, color: 'var(--color-gray-400)', fontWeight: 600, textTransform: 'uppercase' }}>Clear:</span>
                  {['1m', '1 day'].map(o => <button key={o} className="stui-activity-clear-pill">{o}</button>)}
                </div>
                <button className="stui-activity-history-link"><Clock size={11} /> History</button>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-activity-panel / .stui-activity-entry / .stui-activity-icon / .stui-activity-icon.active" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Role']}
            rows={[
              ['.stui-activity-icon',           'Header activity toggle — pulsing when transfers are active'],
              ['.stui-activity-icon.active',    'Pulse animation state'],
              ['.stui-activity-panel',          'Float panel containing transfer list'],
              ['.stui-activity-panel-header',   'Panel title row'],
              ['.stui-activity-entries-scroll', 'Scrollable list of transfer entries'],
              ['.stui-activity-entry',          'Single transfer row — icon + name + progress'],
              ['.stui-activity-entry.done',     'Completed transfer (muted)'],
              ['.stui-activity-entry.failed',   'Failed transfer (red tint)'],
              ['.stui-entry-icon-col',          'Left icon column'],
              ['.stui-entry-main-col',          'Right content column — name, phase, progress'],
              ['.stui-entry-name-row',          'Name + format badge row'],
              ['.stui-entry-name',              'Report/file name text'],
              ['.stui-entry-format-badge',      'XLSX/PDF/CSV badge chip'],
              ['.stui-entry-phase',             'Current phase label (Generating, Streaming…)'],
              ['.stui-entry-progress-track',    'Gray progress bar track'],
              ['.stui-entry-progress-fill',     'Filled portion of progress bar'],
              ['.stui-activity-empty',          'Empty state message when no transfers'],
              ['.stui-activity-panel-footer',   'Footer row — clear pills + history link'],
              ['.stui-activity-clear-pill',     'Time-window clear button (1m, 1 day)'],
              ['.stui-activity-history-link',   'Link to Activity History page'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-activity-panel" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ActivityConfigurator />
        </Section>
      )}

      {/* Modal */}
      {activeSection === 'modal' && (
        <Section title="Modal" description="Standard overlay modal — shared by impersonation, add-task, and other dialogs. stui-modal-overlay covers the full screen; stui-modal is the card." cssFile="STUI.css" cssLine={null} onChipClick={() => {}} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ position: 'relative', padding: 16, background: 'var(--color-gray-100)', borderRadius: 6, marginBottom: 8 }}>
            <div className="stui-modal" style={{ position: 'relative', width: 360, maxHeight: 'none' }}>
              <div className="stui-modal-header">
                <h3>Select User</h3>
                <button className="stui-modal-close">&times;</button>
              </div>
              <div className="stui-modal-list">
                {['Alice Johnson', 'Bob Smith', 'Carol White'].map(name => (
                  <button key={name} className="stui-modal-user">
                    <div className="stui-modal-user-info">
                      <span className="stui-modal-user-name">{name}</span>
                      <span className="stui-modal-user-group">Developer</span>
                    </div>
                  </button>
                ))}
              </div>
              <div className="stui-modal-footer">
                <button className="stui-btn stui-btn-secondary">Cancel</button>
                <button className="stui-btn stui-btn-primary">Confirm</button>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-modal-overlay / .stui-modal / .stui-modal-header / .stui-modal-body / .stui-modal-footer / .stui-modal-close" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Role']}
            rows={[
              ['.stui-modal-overlay',        'Full-screen dim overlay behind modal'],
              ['.stui-modal',                'Modal card — centered, white, rounded, shadow'],
              ['.stui-modal-header',         'Header row — title + close button'],
              ['.stui-modal-close',          'X close button in header'],
              ['.stui-modal-body',           'Content area — forms, inputs'],
              ['.stui-modal-list',           'Scrollable user/item selection list'],
              ['.stui-modal-user',           'User row button in selection list'],
              ['.stui-modal-user-info',      'Name + group wrapper'],
              ['.stui-modal-user-name',      'Primary user name text'],
              ['.stui-modal-user-group',     'Secondary role/group text'],
              ['.stui-modal-footer',         'Action button row — Cancel / Confirm'],
              ['.stui-form-row',             'Label + input row in modal body'],
              ['.stui-help-sections',        'Help modal content wrapper'],
              ['.stui-help-section',         'Individual help topic section'],
              ['.stui-help-section-heading', 'Help section title'],
              ['.stui-help-section-list',    'Help bullet list'],
              ['.stui-help-steps',           'Numbered step list'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-modal" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ModalConfigurator />
        </Section>
      )}

      {/* DB Switcher */}
      {activeSection === 'dbsw' && (
        <Section title="DB Switcher" description="Database selection modal opened from the DB badge in the header. Lists available databases with the current one marked." cssFile="STUI.css" cssLine={null} onChipClick={() => {}} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ padding: 16, background: 'var(--color-gray-100)', borderRadius: 6, marginBottom: 8 }}>
            <div className="stui-db-switcher-modal" style={{ width: 340, height: 'auto', position: 'relative' }}>
              <div className="stui-db-switcher-header">
                <div style={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>DAWN</div>
                <button className="stui-modal-close">&times;</button>
              </div>
              <div className="stui-db-switcher-list">
                {[
                  { name: 'GWA',         desc: 'Greater Wenatchee Area', active: true  },
                  { name: 'LEAVENWORTH', desc: 'Leavenworth Project',    active: false },
                ].map(db => (
                  <button key={db.name} className={`stui-db-switcher-row${db.active ? ' current' : ''}`}>
                    <div className="stui-db-switcher-logo"><div className="stui-db-switcher-logo-placeholder"><Database size={14} /></div></div>
                    <div className="stui-db-switcher-info">
                      <span className="stui-db-switcher-name">{db.name}</span>
                      <span className="stui-db-switcher-desc">{db.desc}</span>
                    </div>
                    {db.active && <span className="stui-db-switcher-badge">Current</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-db-switcher-modal / .stui-db-switcher-header / .stui-db-switcher-list / .stui-db-switcher-row / .stui-db-switcher-badge" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Role']}
            rows={[
              ['.stui-db-switcher-modal',            'DB selection modal card'],
              ['.stui-db-switcher-header',           'Header row — app logo + close button'],
              ['.stui-db-switcher-list',             'List of available databases'],
              ['.stui-db-switcher-row',              'Single database row button'],
              ['.stui-db-switcher-row.current',      'Currently active database — highlighted'],
              ['.stui-db-switcher-logo',             'Logo thumbnail container'],
              ['.stui-db-switcher-logo-placeholder', 'Fallback icon when no logo image'],
              ['.stui-db-switcher-info',             'DB name + description wrapper'],
              ['.stui-db-switcher-name',             'Database short name (GWA, etc.)'],
              ['.stui-db-switcher-desc',             'Database long description'],
              ['.stui-db-switcher-badge',            '"Current" active badge'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-db-switcher-modal" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <div className="stui-col-8">
            <div className="stui-config-output">stui-db-switcher-modal</div>
            <p style={{ fontSize: 11, color: 'var(--color-gray-400)', margin: 0 }}>No token variants — paste <code>stui-db-switcher-modal</code> into CSS Output Test to render.</p>
          </div>
        </Section>
      )}

      {/* Footer */}
      {activeSection === 'footer' && (
        <Section title="Footer" description="Bottom app bar — always visible. Shows active database, app version, and debug panel trigger." cssFile="STUI.css" cssLine={null} onChipClick={() => {}} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ padding: 8, background: 'var(--color-gray-100)', borderRadius: 6, marginBottom: 4 }}>
            <div className="stui-demo-header-bar">
              <div className="stui-footer-left">
                <span className="stui-footer-db-badge"><Database size={12} /><span>GWA</span></span>
                <span className="stui-footer-version">v18.001</span>
              </div>
              <div className="stui-footer-right">
                <button className="stui-debug-btn"><Bug size={11} /></button>
              </div>
            </div>
          </div>
          <div style={{ padding: 8, background: 'var(--color-gray-100)', borderRadius: 6, marginBottom: 8 }}>
            <div className="stui-demo-header-bar">
              <div className="stui-footer-left">
                <span className="stui-footer-db-badge stui-footer-db-badge-preprod"><Database size={12} /><span>PREPROD</span></span>
                <span className="stui-footer-version stui-footer-version-preprod">v18.001</span>
              </div>
              <div className="stui-footer-right">
                <button className="stui-debug-btn"><Bug size={11} /></button>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-footer / .stui-footer-left / .stui-footer-right / .stui-footer-db-badge / .stui-footer-version / .stui-debug-btn" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Role']}
            rows={[
              ['.stui-footer',                  'Footer bar — teal, full width, fixed bottom'],
              ['.stui-footer-left',             'Left slot — db badge + version string'],
              ['.stui-footer-right',            'Right slot — debug trigger'],
              ['.stui-footer-db-badge',         'Active database chip — icon + name'],
              ['.stui-footer-db-badge-preprod', 'Pre-production variant — amber tone'],
              ['.stui-footer-version',          'App version string next to db badge'],
              ['.stui-footer-version-preprod',  'Pre-production version variant'],
              ['.stui-debug-btn',               'Bug icon button — opens debug panel'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-footer" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <FooterConfigurator />
        </Section>
      )}

      {/* Debug Panel */}
      {activeSection === 'debug' && (
        <Section title="Debug Panel" description="Developer overlay panel opened by the bug icon in the footer. Contains hover inspectors, theme switcher, and diagnostic tools." cssFile="STUI.css" cssLine={null} onChipClick={() => {}} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ padding: 8, background: 'var(--color-gray-100)', borderRadius: 6, marginBottom: 8, display: 'inline-block' }}>
            <div className="stui-debug-panel" style={{ position: 'relative', bottom: 'auto', right: 'auto' }}>
              <div className="stui-debug-head">
                <span className="stui-debug-title">Debug Tools</span>
                <span className="stui-debug-hint">Shift+F12</span>
                <button className="stui-debug-close"><X size={12} /></button>
              </div>
              <div className="stui-debug-section">Hover Inspectors</div>
              <div className="stui-debug-row">
                <span className="stui-debug-label">CSS Variables</span>
                <span className="stui-debug-tog" />
              </div>
              <div className="stui-debug-row">
                <span className="stui-debug-label">Tab Order</span>
                <span className="stui-debug-tog on" />
              </div>
              <div className="stui-debug-section">Theme</div>
              <div className="stui-debug-mode-row">
                <button className="stui-debug-mode-btn on">Standard</button>
                <button className="stui-debug-mode-btn">Dark</button>
                <button className="stui-debug-mode-btn">Dim</button>
                <button className="stui-debug-mode-btn">Sepia</button>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-debug-panel / .stui-debug-head / .stui-debug-row / .stui-debug-tog / .stui-debug-mode-btn" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Role']}
            rows={[
              ['.stui-debug-panel',       'Floating debug card — positioned above footer'],
              ['.stui-debug-head',        'Panel header row — title, shortcut hint, close button'],
              ['.stui-debug-title',       'Panel title text'],
              ['.stui-debug-hint',        'Keyboard shortcut hint (Shift+F12)'],
              ['.stui-debug-close',       'Close button'],
              ['.stui-debug-section',     'Section divider label within the panel'],
              ['.stui-debug-row',         'Toggle row — label + toggle switch'],
              ['.stui-debug-label',       'Toggle label text'],
              ['.stui-debug-tog',         'Toggle switch (off state)'],
              ['.stui-debug-tog.on',      'Toggle switch (on state)'],
              ['.stui-debug-mode-row',    'Theme button row'],
              ['.stui-debug-mode-btn',    'Theme selector button'],
              ['.stui-debug-mode-btn.on', 'Currently active theme button'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-debug-panel" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <DebugConfigurator />
        </Section>
      )}
    </div>
  );
};

/* ── Authorization Tab ─────────────────────────────────────── */

const LOGO_URL = 'https://pmdpreprodstor.blob.core.windows.net/org-assets/brand/wm-logo-white.png';

const INVITE_EMAIL_HTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1.0">
  <link href="https://fonts.googleapis.com/css2?family=Orbitron:wght@700&family=Rajdhani:wght@400;500;600&display=swap" rel="stylesheet">
</head>
<body style="margin:0;padding:0;background:#f0f4f5;font-family:'Rajdhani',Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f0f4f5;padding:40px 20px;">
  <tr><td align="center">
    <table width="520" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:10px;overflow:hidden;box-shadow:0 2px 16px rgba(0,0,0,0.10);">
      <tr>
        <td style="background:#1a5568;padding:20px 28px;">
          <table cellpadding="0" cellspacing="0"><tr>
            <td style="vertical-align:middle;padding-right:14px;">
              <img src="${LOGO_URL}" alt="Wunderlich-Malec" height="36" style="display:block;">
            </td>
            <td style="border-left:1px solid rgba(255,255,255,0.3);padding-left:14px;vertical-align:middle;">
              <div style="color:#ffffff;font-family:'Orbitron',sans-serif;font-weight:700;font-size:18px;line-height:1.1;letter-spacing:2px;">DAWN</div>
              <div style="color:rgba(255,255,255,0.65);font-family:'Rajdhani',Arial,sans-serif;font-size:10px;letter-spacing:0.12em;line-height:1.4;margin-top:4px;font-weight:500;">DOCUMENTATION &middot; ASSETS &middot; WIRING &middot; NETWORKS</div>
            </td>
          </tr></table>
        </td>
      </tr>
      <tr>
        <td style="padding:36px 36px 28px;">
          <p style="font-size:17px;color:#1f2937;margin:0 0 16px;font-weight:600;font-family:'Rajdhani',Arial,sans-serif;">Hi Josh Neville,</p>
          <p style="font-size:15px;color:#4b5563;line-height:1.7;margin:0 0 14px;font-family:'Rajdhani',Arial,sans-serif;">
            You&rsquo;ve been invited to <strong>DAWN</strong>, the Wunderlich-Malec engineering platform
            for Documentation, Assets, Wiring &amp; Networking.
          </p>
          <p style="font-size:15px;color:#4b5563;line-height:1.7;margin:0 0 14px;font-family:'Rajdhani',Arial,sans-serif;">Your account has been set up under <strong>Wunderlich-Malec</strong>.</p>
          <p style="font-size:15px;color:#4b5563;line-height:1.7;margin:0 0 28px;font-family:'Rajdhani',Arial,sans-serif;">
            Click the button below to accept your invitation &mdash; you&rsquo;ll sign in with your
            Microsoft account (<strong>josh.neville@wmeng.com</strong>).
          </p>
          <table cellpadding="0" cellspacing="0" style="margin:0 auto 28px;"><tr>
            <td style="background:#1a5568;border-radius:6px;">
              <a href="#" style="display:block;padding:13px 44px;font-size:15px;font-weight:600;color:#ffffff;text-decoration:none;letter-spacing:1px;font-family:'Rajdhani',Arial,sans-serif;">ACCEPT INVITATION</a>
            </td>
          </tr></table>
          <p style="font-size:12px;color:#9ca3af;text-align:center;margin:0 0 24px;font-family:'Rajdhani',Arial,sans-serif;">
            This invitation expires on <strong>Wednesday, May 20, 2026 at 12:00 PM UTC</strong>.
          </p>
          <hr style="border:none;border-top:1px solid #e5e7eb;margin:0 0 18px;">
          <p style="font-size:11px;color:#9ca3af;margin:0;line-height:1.8;font-family:'Rajdhani',Arial,sans-serif;">
            If the button above doesn&rsquo;t work, copy and paste this link into your browser:<br>
            <a href="#" style="color:#1a5568;word-break:break-all;font-size:11px;">https://ppdawn.wmeng.com/accept-invitation?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.sample</a>
          </p>
        </td>
      </tr>
      <tr>
        <td style="background:#f0f4f5;padding:16px 36px;border-top:1px solid #e5e7eb;">
          <p style="font-size:11px;color:#9ca3af;margin:0;text-align:center;font-family:'Rajdhani',Arial,sans-serif;">
            Wunderlich-Malec Engineering &nbsp;&middot;&nbsp; This is an automated message &mdash; do not reply
          </p>
        </td>
      </tr>
    </table>
  </td></tr>
</table>
</body>
</html>`;

const TEST_EMAIL_HTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1.0">
  <link href="https://fonts.googleapis.com/css2?family=Orbitron:wght@700&family=Rajdhani:wght@400;500;600&display=swap" rel="stylesheet">
</head>
<body style="margin:0;padding:0;background:#f0f4f5;font-family:'Rajdhani',Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f0f4f5;padding:40px 20px;">
  <tr><td align="center">
    <table width="520" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:10px;overflow:hidden;box-shadow:0 2px 16px rgba(0,0,0,0.10);">
      <tr>
        <td style="background:#1a5568;padding:20px 28px;">
          <table cellpadding="0" cellspacing="0"><tr>
            <td style="vertical-align:middle;padding-right:14px;">
              <img src="${LOGO_URL}" alt="Wunderlich-Malec" height="36" style="display:block;">
            </td>
            <td style="border-left:1px solid rgba(255,255,255,0.3);padding-left:14px;vertical-align:middle;">
              <div style="color:#ffffff;font-family:'Orbitron',sans-serif;font-weight:700;font-size:18px;line-height:1.1;letter-spacing:2px;">DAWN</div>
              <div style="color:rgba(255,255,255,0.65);font-family:'Rajdhani',Arial,sans-serif;font-size:10px;letter-spacing:0.12em;line-height:1.4;margin-top:4px;font-weight:500;">DOCUMENTATION &middot; ASSETS &middot; WIRING &middot; NETWORKS</div>
            </td>
          </tr></table>
        </td>
      </tr>
      <tr>
        <td style="padding:36px 36px 28px;">
          <p style="font-size:17px;color:#1f2937;margin:0 0 16px;font-weight:600;font-family:'Rajdhani',Arial,sans-serif;">Hi Josh Neville,</p>
          <p style="font-size:15px;color:#4b5563;line-height:1.7;margin:0 0 14px;font-family:'Rajdhani',Arial,sans-serif;">
            This is a test email from <strong>DAWN</strong>, the Wunderlich-Malec engineering platform
            for Documentation, Assets, Wiring &amp; Networking.
          </p>
          <p style="font-size:15px;color:#4b5563;line-height:1.7;margin:0 0 28px;font-family:'Rajdhani',Arial,sans-serif;">
            If you&rsquo;re reading this, email delivery to <strong>josh.neville@wmeng.com</strong> is working correctly.
            No action is required &mdash; you can disregard this message.
          </p>
          <table cellpadding="0" cellspacing="0" style="margin:0 auto 28px;width:100%;"><tr>
            <td style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:6px;padding:14px 24px;text-align:center;">
              <p style="font-size:14px;font-weight:600;color:#166534;margin:0;font-family:'Rajdhani',Arial,sans-serif;letter-spacing:0.5px;">EMAIL DELIVERY CONFIRMED</p>
            </td>
          </tr></table>
          <p style="font-size:12px;color:#9ca3af;text-align:center;margin:0 0 24px;font-family:'Rajdhani',Arial,sans-serif;">
            Sent on <strong>Tuesday, May 13, 2026 at 3:00 PM UTC</strong>
          </p>
          <hr style="border:none;border-top:1px solid #e5e7eb;margin:0 0 18px;">
          <p style="font-size:11px;color:#9ca3af;margin:0;line-height:1.8;font-family:'Rajdhani',Arial,sans-serif;">
            This message was sent as a delivery test by a DAWN administrator.
          </p>
        </td>
      </tr>
      <tr>
        <td style="background:#f0f4f5;padding:16px 36px;border-top:1px solid #e5e7eb;">
          <p style="font-size:11px;color:#9ca3af;margin:0;text-align:center;font-family:'Rajdhani',Arial,sans-serif;">
            Wunderlich-Malec Engineering &nbsp;&middot;&nbsp; This is an automated message &mdash; do not reply
          </p>
        </td>
      </tr>
    </table>
  </td></tr>
</table>
</body>
</html>`;

const AUTH_GATE_STATES = [
  { id: 'video',          label: 'Video'          },
  { id: 'connecting',     label: 'Connecting'     },
  { id: 'authenticating', label: 'Authenticating' },
  { id: 'verifying',      label: 'Verifying'      },
  { id: 'selecting',      label: 'Step: Select'   },
  { id: 'loading',        label: 'Loading'        },
  { id: 'db-picker',      label: 'DB Picker'      },
  { id: 'debug',          label: 'Debug'          },
];

const GATE_STEPS = [
  { key: 'connecting',     label: 'Connecting to Microsoft' },
  { key: 'authenticating', label: 'Signing you in'          },
  { key: 'verifying',      label: 'Checking your account'   },
  { key: 'selecting',      label: 'Select a database'       },
  { key: 'loading',        label: 'Loading your workspace'  },
];

const GATE_STEP_MAP = { connecting: 0, authenticating: 1, verifying: 2, selecting: 3, loading: 4 };

const MOCK_DEBUG_LOG = [
  { time: '[00:000]', msg: 'AUTH: connecting',                            type: 'step'  },
  { time: '[00:412]', msg: 'AUTH: authenticating',                        type: 'step'  },
  { time: '[01:203]', msg: 'AUTH: verifying',                             type: 'step'  },
  { time: '[02:118]', msg: 'AUTH: ready',                                 type: 'step'  },
  { time: '[02:119]', msg: 'Auth resolved → selecting database',          type: 'step'  },
  { time: '[02:120]', msg: 'API: databases/available → started',          type: 'info'  },
  { time: '[02:387]', msg: 'API: databases/available → OK (267ms), 3 DB(s)', type: 'info' },
];

const AuthorizationTab = () => {
  const [gateIdx, setGateIdx]     = useState(1);
  const [emailView, setEmailView] = useState('invite');
  const [databases, setDatabases] = useState([]);

  useEffect(() => {
    databasesApi.getAvailable()
      .then(res => setDatabases(res.data || []))
      .catch(() => {});
  }, []);

  const gateState      = AUTH_GATE_STATES[gateIdx];
  const currentStepIdx = GATE_STEP_MAP[gateState.id] ?? -1;

  const renderGateBody = () => {
    if (gateState.id === 'video') {
      return (
        <div style={{ flex: 1, background: '#0a2030', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.35)', fontFamily: 'Rajdhani, Arial, sans-serif', fontSize: 13 }}>
          [ DAWN video splash ]
        </div>
      );
    }
    if (gateState.id === 'db-picker') {
      return (
        <>
          <div className="auth-gate-db-header">Select a database</div>
          <div className="auth-gate-db-list">
            {databases.map(db => {
              const name = db.displayName || db.name;
              return (
                <button key={db.id} className="auth-gate-db-item">
                  {db.iconUrl
                    ? <img src={db.iconUrl} alt="" className="auth-gate-db-icon" />
                    : <div className="auth-gate-db-icon-placeholder">{name.charAt(0)}</div>
                  }
                  <div className="auth-gate-db-info">
                    <div className="auth-gate-db-name">{name}</div>
                    {db.description && <div className="auth-gate-db-desc">{db.description}</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      );
    }
    if (gateState.id === 'debug') {
      return (
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px', fontFamily: 'monospace', fontSize: 12 }}>
          {MOCK_DEBUG_LOG.map((entry, i) => (
            <div key={i} style={{ padding: '2px 0', color: entry.type === 'step' ? '#1a5568' : entry.type === 'error' ? '#dc2626' : '#6b7280' }}>
              <span style={{ color: '#9ca3af', marginRight: 8 }}>{entry.time}</span>
              {entry.msg}
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="auth-gate-steps">
        {GATE_STEPS.map((step, idx) => {
          const isDone   = idx < currentStepIdx;
          const isActive = !isDone && idx === currentStepIdx;
          const state    = isDone ? 'done' : isActive ? 'active' : 'pending';
          return (
            <div key={step.key} className={`auth-gate-step ${state}`}>
              <div className="auth-gate-step-icon">
                {isDone   ? <CheckCircle size={20} color="#1a5568" /> :
                 isActive ? <Loader size={20} className="auth-gate-spinner" color="#1a5568" /> :
                            <div className="auth-gate-step-circle" />}
              </div>
              <span className="auth-gate-step-label">{step.label}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ padding: '24px 32px', overflowY: 'auto', flex: 1 }}>

      {/* ── Email Format ──────────────────────────── */}
      <div className="stui-detail-section-header">Email Format</div>
      <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <button className={`stui-btn ${emailView === 'invite' ? 'stui-btn-primary' : 'stui-btn-secondary'}`} onClick={() => setEmailView('invite')}>Invitation</button>
            <button className={`stui-btn ${emailView === 'test'   ? 'stui-btn-primary' : 'stui-btn-secondary'}`} onClick={() => setEmailView('test')}>Test Email</button>
          </div>
          <div style={{ marginBottom: 10, fontSize: 13, color: 'var(--color-gray-600)' }}>
            <strong>Subject:</strong>&nbsp;{emailView === 'invite' ? "You've been invited to DAWN" : 'DAWN Email Test'}
          </div>
          <div style={{ border: '1px solid var(--color-gray-200)', borderRadius: 8, overflow: 'hidden', height: 500 }}>
            <iframe
              key={emailView}
              srcDoc={emailView === 'invite' ? INVITE_EMAIL_HTML : TEST_EMAIL_HTML}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title={emailView === 'invite' ? 'Invitation Email Preview' : 'Test Email Preview'}
            />
          </div>
      </div>

      {/* ── Authorization Gate ────────────────────── */}
      <div className="stui-detail-section-header">Authorization Gate</div>
      <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            <button className="stui-btn stui-btn-secondary" onClick={() => setGateIdx(i => Math.max(0, i - 1))} disabled={gateIdx === 0}><ChevronLeft size={14} /></button>
            {AUTH_GATE_STATES.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setGateIdx(i)}
                style={{
                  padding: '4px 11px', border: 'none', borderRadius: 4, fontSize: 12,
                  cursor: 'pointer', transition: 'all 0.15s',
                  background: i === gateIdx ? '#1a5568' : 'var(--color-gray-100)',
                  color: i === gateIdx ? '#fff' : 'var(--color-gray-600)',
                  fontWeight: i === gateIdx ? 600 : 400,
                }}
              >{s.label}</button>
            ))}
            <button className="stui-btn stui-btn-secondary" onClick={() => setGateIdx(i => Math.min(AUTH_GATE_STATES.length - 1, i + 1))} disabled={gateIdx === AUTH_GATE_STATES.length - 1}><ChevronRight size={14} /></button>
          </div>
          <div style={{ background: '#1a5568', borderRadius: 12, padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="auth-gate-card" style={{ position: 'relative' }}>
              {gateState.id === 'video' ? renderGateBody() : (
                <>
                  <div className="auth-gate-header">
                    <img src={wmLogo} alt="Wunderlich-Malec" className="auth-gate-logo" />
                    <div className="auth-gate-header-divider" />
                    <div>
                      <div className="auth-gate-title">DAWN</div>
                      <div className="auth-gate-subtitle">DOCUMENTATION · ASSETS · WIRING · NETWORKING</div>
                    </div>
                  </div>
                  {renderGateBody()}
                  <button style={{ position: 'absolute', bottom: 10, right: 10, background: 'none', border: 'none', cursor: 'default', color: '#d1d5db', padding: 4 }} tabIndex={-1}>
                    <Bug size={14} />
                  </button>
                </>
              )}
            </div>
          </div>
      </div>

      {/* ── Database Picker ───────────────────────── */}
      <div className="stui-detail-section-header">Database Picker</div>
      <div style={{ marginBottom: 32 }}>
          <div className="db-switcher-modal">
            <div className="db-switcher-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', flex: 1, minWidth: 0 }}>
                <img src={wmLogo} alt="Wunderlich-Malec" style={{ height: '32px', flexShrink: 0 }} />
                <div style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', paddingLeft: '0.875rem' }}>
                  <div style={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.1 }}>DAWN</div>
                  <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.7rem', letterSpacing: '0.04em', lineHeight: 1.2, marginTop: '2px' }}>
                    Documentation, Assets, Wiring &amp; Networking
                  </div>
                </div>
              </div>
              <button className="impersonate-close" style={{ background: 'none', border: 'none', cursor: 'default', padding: 4 }} tabIndex={-1}>&times;</button>
            </div>
            <div className="db-switcher-list">
              {databases.map((db, i) => {
                const name = db.displayName || db.name;
                return (
                  <button key={db.id} className={`db-switcher-row${i === 0 ? ' current' : ''}`}>
                    <div className="db-switcher-logo">
                      {db.iconUrl
                        ? <img src={db.iconUrl} alt="" />
                        : <div className="db-switcher-logo-placeholder"><Database size={16} /></div>
                      }
                    </div>
                    <div className="db-switcher-info">
                      <span className="db-switcher-name">{name}</span>
                      {db.description && <span className="db-switcher-desc">{db.description}</span>}
                    </div>
                    {i === 0 && <span className="db-switcher-badge">Current</span>}
                  </button>
                );
              })}
            </div>
          </div>
      </div>

    </div>
  );
};

/* ── Page shell ────────────────────────────────────────────── */

const TABS = [
  { id: 'base-reset',    label: 'Base'              },
  { id: 'elements',      label: 'Elements'          },
  { id: 'authorization', label: 'Authorization'      },
  { id: 'standard-view', label: 'Standard View'     },
  { id: 'data-entry',    label: 'Data Entry Module' },
  { id: 'reports',       label: 'Reports'           },
  { id: 'app-shell',    label: 'App Shell'         },
  { id: 'usage',         label: 'Usage'             },
  { id: 'migration',     label: 'CSS Migrate'       },
  { id: 'css-test',      label: 'CSS Output Test'   },
  { id: 'migration-log', label: 'Migration Log'     },
];

const STUIPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('elements');
  const [usageMap, setUsageMap] = useState(null);
  const [pageSlugMap, setPageSlugMap] = useState(null);
  const [elementTexts, setElementTexts] = useState(null);

  const handleChipClick = () => {};

  useEffect(() => {
    const loadData = async () => {
      try {
        const [mapRes, auditRes, textsRes] = await Promise.all([
          fetch(API_BASE_URL + '/api/devfiles/stui-usage-map'),
          fetch(API_BASE_URL + '/api/devfiles/stui-audit'),
          fetch(API_BASE_URL + '/api/devfiles/element-texts'),
        ]);
        setUsageMap(await mapRes.json());
        const pages = await auditRes.json();
        const slugMap = {};
        // Store { slug, dbScoped } so UsedBy can build the correct navigation path
        pages.forEach(p => { slugMap[p.pageName] = { slug: p.slug, dbScoped: p.dbScoped }; });
        setPageSlugMap(slugMap);
        setElementTexts(await textsRes.json());
      } catch (e) {
        console.error('STUI data load failed:', e);
      }
    };
    loadData();
  }, []);

  const tabProps = { usageMap, pageSlugMap, elementTexts, navigate, onChipClick: handleChipClick };

  return (
    <div className="stui-module-container">
      <div className="stui-module-header-styleguideonly">
        <div className="stui-module-header-left">
          <Baby size={20} className="stui-page-icon" />
          <h1>Standard Template User Interface</h1>
          <span className="stui-module-header-meta">STUI.css — Reference: I/O Assignments Report</span>
        </div>
        <div className="stui-module-header-actions">
          <button className="stui-crud-btn" disabled title="Add"><Plus size={16} /></button>
          <button className="stui-crud-btn" disabled title="Edit"><Pencil size={16} /></button>
          <button className="stui-crud-btn" disabled title="Save"><Save size={16} /></button>
          <button className="stui-action-menu-btn disabled" disabled title="Actions"><GripVertical size={16} /></button>
        </div>
      </div>

      <div className="stui-ssg-tab-bar">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`stui-ssg-tab-btn${activeTab === tab.id ? ' active' : ''}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'elements'       && <ElementsTab {...tabProps} />}
      {activeTab === 'standard-view'  && <StandardViewTab {...tabProps} />}
      {activeTab === 'authorization'  && <AuthorizationTab />}
      {activeTab === 'data-entry'     && <PlaceholderTab label="Data Entry Module" />}
      {activeTab === 'reports'        && <ReportsTab {...tabProps} />}
      {activeTab === 'app-shell'      && <AppShellTab {...tabProps} />}
      {activeTab === 'base-reset'     && <BaseResetTab onChipClick={handleChipClick} />}
      {activeTab === 'usage'          && <UsageTab />}
      {activeTab === 'migration'      && <MigrationTab />}
      {activeTab === 'css-test'       && <CSSOutputTestTab />}
      {activeTab === 'migration-log'  && <MigrationLogTab />}
    </div>
  );
};

export default STUIPage;
